<!-- Shared "API Call" config block for the build form's Dropdown / Slider &
     Input Box / File Upload sections — reused via ngTemplateOutlet right
     after each section's "Target chart(s)/input(s)" picker. When enabled, a
     value change on THIS control fires the configured request instead of the
     built-in generate/recompute call, and the response is applied to the
     targets above exactly like a normal recompute result — see
     resolveInputComputeCall / InputApiCallConfig. -->
<ng-template #apiCallConfigBlock let-kind="kind">
  <div class="control-group control-group-full dvm-api-call-config">
    <label class="dvm-api-call-toggle">
      <input type="checkbox" [(ngModel)]="buildApiCallConfig.enabled" />
      <span>Use a custom API call for this control</span>
    </label>

    <ng-container *ngIf="buildApiCallConfig.enabled">
      <div class="dvm-api-call-row">
        <select class="dvm-api-call-method" [(ngModel)]="buildApiCallConfig.method">
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="PATCH">PATCH</option>
        </select>
        <input type="text" class="dvm-api-call-url" placeholder="https://api.example.com/endpoint" [(ngModel)]="buildApiCallConfig.url" />
      </div>

      <label>When this control changes</label>
      <select class="dvm-api-call-mode" [(ngModel)]="buildApiCallConfig.mode">
        <option value="replace">Call ONLY this API (it answers for every target)</option>
        <option value="parallel">Call this API AND the normal compute API together</option>
      </select>

      <ng-container *ngIf="buildApiCallConfig.mode === 'parallel'">
        <label>Targets this API answers for</label>
        <ng-select
          #apiTargetSel
          class="ss-select"
          [class.is-open]="!!apiTargetSel.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="apiCallTargetOptions"
          bindLabel="label"
          bindValue="value"
          [multiple]="true"
          [closeOnSelect]="false"
          [hideSelected]="false"
          [clearable]="true"
          [searchable]="true"
          placeholder="Pick the target(s) your microservice returns"
          [(ngModel)]="buildApiCallConfig.targets"
        ></ng-select>
        <p class="dvm-pt-hint">
          Both requests fire at the same time. The target(s) picked here are answered by your
          API; every other target above still comes from the normal compute API, and the two
          responses are merged. Leave this empty to fire your API as a pure side effect while
          compute keeps serving all targets.
        </p>
        <small class="control-hint" *ngIf="!apiCallTargetOptions.length">
          Add a chart, KPI or input to the dashboard first so it can be assigned here.
        </small>
      </ng-container>

      <ng-container *ngIf="buildApiCallConfig.method !== 'GET'">
        <label>Request body</label>
        <textarea class="dvm-api-call-body" rows="6" [(ngModel)]="buildApiCallConfig.bodyTemplate" spellcheck="false"></textarea>
        <p class="dvm-pt-hint">
          Tokens: <code>{{ '{value}' }}</code> (current value), <code>{{ '{componentId}' }}</code>
          <ng-container *ngIf="kind === 'dropdown'">, <code>{{ '{field}' }}</code></ng-container>
          <ng-container *ngIf="kind === 'upload'">, <code>{{ '{fileName}' }}</code>, <code>{{ '{fileType}' }}</code></ng-container>
          — a token as the WHOLE value of a JSON string (e.g. <code>"{{ '{value}' }}"</code>) keeps its native type; embedded in a longer string it's stringified.
        </p>
      </ng-container>

      <p class="dvm-pt-hint">
        Response is expected in the same shape the built-in compute call already returns:
        <code>{{ '{ "results": [{ "componentId": "...", "result": {...} }] }' }}</code>
      </p>
    </ng-container>
  </div>
</ng-template>

<section
  class="dvm-layout-builder-workspace"
  [class.steps-panel-collapsed]="stepsPanelCollapsed"
  [class.preview-panel-collapsed]="previewPanelCollapsed"
  [class.data-panel-collapsed]="dataPanelCollapsed"
  [style.--steps-pane-width.px]="stepsPaneWidth"
  [style.--data-pane-width.px]="dataPaneWidth"
  [style.grid-template-columns]="paneGridTemplate"
>
  <!-- ══════════════════ LEFT: configuration panel ══════════════════ -->
  <aside class="dvm-layout-target-panel wf-section" *ngIf="false">
      <header class="wf-section-header">
        <div><span class="wf-section-kicker">01 · Setup</span><h3>Target chart</h3></div>
        <button
          type="button"
          class="panel-collapse-button"
          mat-icon-button
          (click)="targetPanelCollapsed = !targetPanelCollapsed"
          [matTooltip]="targetPanelCollapsed ? 'Open target chart' : 'Collapse target chart'"
          [attr.aria-label]="targetPanelCollapsed ? 'Open target chart panel' : 'Collapse target chart panel'"
        ><mat-icon>{{ targetPanelCollapsed ? 'chevron_right' : 'chevron_left' }}</mat-icon></button>
      </header>
      <div class="panel-content">
      <div class="control-row">
        <div class="control-group">
          <label for="wfLayoutType">Chart layout</label>
          <ng-select
            labelForId="wfLayoutType"
            #ssWfLayout
            class="ss-select"
            [class.is-open]="!!ssWfLayout.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="layoutTypes"
            bindLabel="label"
            bindValue="value"
            [clearable]="true"
            [ngModel]="selectedLayoutType"
            (ngModelChange)="selectedLayoutType = $event ?? ''; onLayoutTypeChange()"
          ></ng-select>
        </div>
        <div class="control-group">
          <label for="wfTitle">Title</label>
          <input
            id="wfTitle"
            type="text"
            [(ngModel)]="title"
            placeholder="e.g. Gross Revenue to Net Profit Multi-Bridge"
          />
        </div>
      </div>

      <div class="control-row">
        <div class="control-group">
          <label for="wfComponentId">Component Id</label>
          <input id="wfComponentId" type="text" [(ngModel)]="componentId" />
        </div>
        <div class="control-group">
          <label for="wfDashboardId">Dashboard Id</label>
          <input id="wfDashboardId" type="number" [(ngModel)]="dashboardId" />
        </div>
      </div>

      <div class="control-group control-group-full">
        <label for="wfDatasets">Datasets</label>
        <ng-select
          labelForId="wfDatasets"
          #ssWfDatasets
          class="ss-select ss-select--multi ss-select--full"
          [class.is-open]="!!ssWfDatasets.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="entityOptions"
          [multiple]="true"
          [closeOnSelect]="false"
          [hideSelected]="false"
          [loading]="entitiesLoading"
          [clearable]="true"
          [searchable]="true"
          placeholder="Select one or more operational data tables/entities"
          [ngModel]="selectedDatasets"
          (ngModelChange)="onSelectedDatasetsChange($event)"
        ></ng-select>
        <small class="control-hint" *ngIf="!selectedDatasets.length">Pick at least one dataset before adding steps.</small>
      </div>
      </div>
  </aside>

  <div class="pane-resizer target-pane-resizer" aria-hidden="true"></div>

  <aside class="dvm-layout-steps-panel wf-section wf-steps-section" [class.is-collapsed]="stepsPanelCollapsed" [style.grid-column]="paneGridColumn('steps')" (dragover)="onPaneDragOver($event)" (drop)="onPaneDrop('steps', $event)">
      <header class="wf-section-header wf-steps-head pane-drag-handle" draggable="true" (dragstart)="onPaneDragStart('steps', $event)" (dragend)="onPaneDragEnd()" matTooltip="Drag to reorder panel">
        <div><span class="wf-section-kicker">Build</span><h3>{{ buildPanelTitle }} <span class="wf-step-count" *ngIf="buildPanelCount !== null">{{ buildPanelCount }}</span></h3></div>
        <div class="wf-header-actions">
          <button *ngIf="isWaterfallLayout" type="button" class="add-step-button" mat-stroked-button (click)="addStep()"><mat-icon>add</mat-icon> Add step</button>
          <button
            type="button"
            class="panel-collapse-button"
            mat-icon-button
            (click)="stepsPanelCollapsed = !stepsPanelCollapsed"
            [matTooltip]="stepsPanelCollapsed ? 'Open bridge steps' : 'Collapse bridge steps'"
            [attr.aria-label]="stepsPanelCollapsed ? 'Open bridge steps panel' : 'Collapse bridge steps panel'"
          ><mat-icon>{{ paneCollapseIcon('steps') }}</mat-icon></button>
        </div>
      </header>
      <div class="panel-content steps-panel-content">

      <div class="bridge-chart-settings">
        <div class="bridge-settings-heading"><mat-icon>tune</mat-icon><span>{{ layoutSectionTitle('config', 'Chart configuration') }}</span></div>

        <!-- LAYER 1 — Chart Catalogue: Category → Type. The Category picker shows
             only when a catalogue is configured; it filters the Type list below. -->
        <div class="control-group" *ngIf="hasChartCatalogue">
          <label for="bridgeChartCategory">Chart category</label>
          <ng-select
            labelForId="bridgeChartCategory"
            #ssBridgeCategory
            class="ss-select"
            [class.is-open]="!!ssBridgeCategory.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="chartCategories"
            bindLabel="label"
            bindValue="id"
            [clearable]="true"
            [ngModel]="selectedChartCategory"
            (ngModelChange)="selectedChartCategory = $event ?? ''; onChartCategoryChange()"
          ></ng-select>
        </div>

        <div class="control-group">
          <label for="bridgeLayoutType">{{ hasChartCatalogue ? 'Chart type' : 'Chart layout' }}</label>
          <ng-select
            labelForId="bridgeLayoutType"
            #ssBridgeLayout
            class="ss-select"
            [class.is-open]="!!ssBridgeLayout.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="hasChartCatalogue ? typesForSelectedCategory : layoutTypes"
            bindLabel="label"
            bindValue="value"
            [clearable]="true"
            [ngModel]="selectedLayoutType"
            (ngModelChange)="selectedLayoutType = $event ?? ''; onLayoutTypeChange()"
          ></ng-select>
        </div>
        <!-- Title moved into its own 'Title' accordion (Bar & Waterfall layouts).
             For the remaining layouts (kpi/drill-down/…) keep the inline Title
             field here; config-driven — a layout's config section listing
             `title` in its fields shows it, omitting it hides it. -->
        <!-- Bar / Line & Area / Drill-Down surface their Title via a 'Title' format
             accordion, so exclude the inline field for them. -->
        <div class="control-group" *ngIf="!isBarLayout && !isLineAreaLayout && !(isTableMatrixLayout && isTmDrilldown) && fieldEnabled('config', 'title')">
          <label for="bridgeTitle">Title</label>
          <input id="bridgeTitle" type="text" [(ngModel)]="title" placeholder="e.g. Gross Revenue to Net Profit" />
        </div>
        <!-- <div class="selected-data-summary">
          <mat-icon>database</mat-icon>
          <span>{{ selectedDatasets.length ? selectedDatasets.length + ' table(s) selected' : 'Drag a field from the Data pane to select a table' }}</span>
        </div> -->
      </div>

      <app-dvm-relation-hops
        *ngIf="activeRelationHops as relationConfig"
        [config]="relationConfig"
        [value]="relationHops"
        (valueChange)="relationHops = $event"
      ></app-dvm-relation-hops>

      <ng-container *ngIf="isWaterfallLayout">
        <div class="wf-empty" *ngIf="!steps.length">
          No steps yet - add a Start step to anchor the bridge, then Change/Total steps to build it out.
        </div>

        <div class="wf-step-card" *ngFor="let step of steps; let i = index; let first = first; let last = last">
          <div class="wf-step-head">
            <span class="wf-step-index">{{ i + 1 }}</span>

            <ng-select
              #ssWfStepType
              class="ss-select wf-step-type-select"
              [class.is-open]="!!ssWfStepType.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="stepTypes"
              bindLabel="label"
              bindValue="value"
              [clearable]="true"
              [ngModel]="step.stepType"
              (ngModelChange)="step.stepType = $event ?? ''; onStepTypeChange(step)"
            ></ng-select>

            <input
              type="text"
              class="wf-step-label"
              [ngModel]="step.label"
              (ngModelChange)="step.label = $event; onStepFormulaOrLabelChange()"
              placeholder="Display label"
            />


            <div class="wf-step-actions">
              <button type="button" mat-icon-button [disabled]="first" (click)="moveStepUp(i)" matTooltip="Move up" aria-label="Move step up">
                <mat-icon>arrow_upward</mat-icon>
              </button>
              <button type="button" mat-icon-button [disabled]="last" (click)="moveStepDown(i)" matTooltip="Move down" aria-label="Move step down">
                <mat-icon>arrow_downward</mat-icon>
              </button>
              <button type="button" mat-icon-button class="row-remove-button" (click)="removeStep(step)" matTooltip="Remove step" aria-label="Remove step">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </div>

          <div class="wf-step-mode-toggle" *ngIf="step.stepType === 'CHANGE' || step.stepType === 'START'" role="group" aria-label="Step input mode">
            <button type="button" [class.is-active]="!step.formulaMode" (click)="step.formulaMode ? toggleStepFormulaMode(step) : null">
              <mat-icon>table_chart</mat-icon>
              Dataset
            </button>
            <button type="button" [class.is-active]="step.formulaMode" (click)="!step.formulaMode ? toggleStepFormulaMode(step) : null">
              <mat-icon>functions</mat-icon>
              Formula
            </button>
          </div>

          <!-- Direction applies to a CHANGE step's own delta/bar regardless of
               whether its value comes from a dataset aggregation or a formula,
               so it's shown for both modes (moved out of the dataset-only body
               below, which used to hide it whenever Formula mode was active). -->
          <div class="wf-step-body" *ngIf="step.stepType === 'CHANGE' && fieldEnabled('steps', 'sign')">
            <div class="control-group">
              <label>{{ fieldLabel('steps', 'sign', 'Direction') }}</label>
              <ng-select
                #ssWfSignShared
                class="ss-select"
                [class.is-open]="!!ssWfSignShared.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="signOptions"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [ngModel]="step.sign"
                (ngModelChange)="step.sign = $event ?? ''; onStepFormulaOrLabelChange()"
              ></ng-select>
            </div>
          </div>

          <div
            class="wf-step-body wf-field-drop-zone"
            *ngIf="step.stepType !== 'TOTAL' && !step.formulaMode"
            (dragover)="allowFieldDrop($event)"
            (drop)="onStepFieldDrop($event, step)"
            title="Drop a field from the Data pane"
          >
            <div class="control-group" *ngIf="fieldEnabled('steps', 'dataset')">
              <label>{{ fieldLabel('steps', 'dataset', 'Dataset') }}</label>
              <ng-select
                #ssWfDataset
                class="ss-select"
                [class.is-open]="!!ssWfDataset.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="entityOptions"
                [clearable]="true"
                [ngModel]="step.dataset"
                (ngModelChange)="onStepDatasetChange(step, $event)"
              ></ng-select>
            </div>
            <div class="control-group" *ngIf="fieldEnabled('steps', 'field')">
              <label>{{ fieldLabel('steps', 'field', 'Field') }}</label>
              <ng-select
                #ssWfField
                class="ss-select"
                [class.is-open]="!!ssWfField.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="columnsForStep(step)"
                [clearable]="true"
                [searchable]="true"
                [loading]="isStepColumnsLoading(step)"
                [placeholder]="stepFieldPlaceholder(step)"
                [ngModel]="step.field || null"
                (ngModelChange)="onStepFieldChange(step, $event)"
              ></ng-select>
            </div>
            <div class="control-group" *ngIf="fieldEnabled('steps', 'aggregation')">
              <label>{{ fieldLabel('steps', 'aggregation', 'Aggregation') }}</label>
              <ng-select
                #ssWfAgg
                class="ss-select"
                [class.is-open]="!!ssWfAgg.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="measureAggregations"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [ngModel]="step.aggregation"
                (ngModelChange)="step.aggregation = $event ?? ''; onStepFormulaOrLabelChange()"
              ></ng-select>
            </div>
          </div>

          <div class="wf-step-body" *ngIf="(step.stepType === 'TOTAL' || step.formulaMode) && fieldEnabled('steps', 'formula')">
            <div
              class="control-group control-group-full wf-field-drop-zone"
              (dragover)="allowFieldDrop($event)"
              (drop)="onWaterfallFormulaDrop(step, $event)"
              title="Drag a column here to add it to the formula"
            >
              <label>{{ fieldLabel('steps', 'formula', 'Calculation logic / formula') }}</label>
              <input
                type="text"
                [ngModel]="step.formula"
                (ngModelChange)="step.formula = $event; onStepFormulaOrLabelChange()"
                placeholder="Drag columns here, e.g. [Gross Revenue] + [Product Sales] - [COGS]"
              />
            </div>
          </div>
        </div>

        <!-- Per-axis label FONT WEIGHTS — waterfall's option builder is fully
             format-driven, so these write straight into the shared format bag
             (same keys buildWaterfallOption reads); '' = default (falls back
             to the Format accordion's tile-wide "Axis label weight").
             CONFIG-DRIVEN via the waterfallAxis layoutSteps section in
             charts/waterfall.json; options from the shell `fontWeights` list. -->
        <div class="control-row" *ngIf="fieldEnabled('waterfallAxis', 'xAxisFontWeight') || fieldEnabled('waterfallAxis', 'yAxisFontWeight')">
          <div class="control-group" *ngIf="fieldEnabled('waterfallAxis', 'xAxisFontWeight')">
            <label>{{ fieldLabel('waterfallAxis', 'xAxisFontWeight', 'X axis label weight') }}</label>
            <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="fontWeightOptions" bindLabel="label" bindValue="value" [clearable]="true" [searchable]="false" [ngModel]="barFormat['xAxisFontWeight'] || ''" (ngModelChange)="barFormat['xAxisFontWeight'] = $event ?? ''; onBarFormatChange()"></ng-select>
          </div>
          <div class="control-group" *ngIf="fieldEnabled('waterfallAxis', 'yAxisFontWeight')">
            <label>{{ fieldLabel('waterfallAxis', 'yAxisFontWeight', 'Y axis label weight') }}</label>
            <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="fontWeightOptions" bindLabel="label" bindValue="value" [clearable]="true" [searchable]="false" [ngModel]="barFormat['yAxisFontWeight'] || ''" (ngModelChange)="barFormat['yAxisFontWeight'] = $event ?? ''; onBarFormatChange()"></ng-select>
          </div>
        </div>

        <!-- Waterfall chart options — the SAME config-driven format accordions as
             the Bar layout (Title / Axis / Legend / Labels / Tooltip / Formatting /
             Advanced), fed by the WATERFALL-scoped formatControls in
             charts/waterfall.json and gated by the capabilityRegistry `sections`
             list; the values fold into the tile on Generate. -->
        <ng-container [ngTemplateOutlet]="chartFormatAccordion"></ng-container>
      </ng-container>

      <!-- ══ Table & Matrix: config-driven type picker + per-type fields ══ -->
      <div class="table-matrix-config" *ngIf="isTableMatrixLayout">
        <div class="control-group">
          <label>{{ fieldLabel('tableMatrix', 'tableType', 'Table type') }}</label>
          <ng-select
            #ssTmType
            class="ss-select"
            [class.is-open]="!!ssTmType.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="tableTypes"
            bindLabel="label"
            bindValue="value"
            groupBy="group"
            [clearable]="true"
            [ngModel]="tmType"
            (ngModelChange)="tmType = $event ?? ''; onTableMatrixTypeChange()"
          ></ng-select>
          <small class="control-hint" *ngIf="tmTypeHint">{{ tmTypeHint }}</small>
          <small class="control-hint tm-migration-notice" *ngIf="tmMigrationNotice">{{ tmMigrationNotice }}</small>
        </div>

        <!-- Dataset — declared (required) in charts/table-matrix.json's config
             section but previously never rendered in this form: the only picker
             lived in the collapsible target/data panel, so skipping that panel
             left Generate permanently disabled (and Drill-Down's pickers
             disabled with it). Single-select: the table renders from ONE
             dataset (drillDataset = first of selectedDatasets). -->
        <ng-container *ngTemplateOutlet="datasetPicker"></ng-container>

        <!-- Normal / Editable / Grouped / Tree / Financial: choose columns (empty = all).
             One row per column — each with its OWN dataset picker so a table can
             mix columns from different datasets ({dataset, field, displayName}
             in the compute payload). Dataset defaults to the primary one above. -->
        <!-- ALL flat table shapes share this block (per-type pickers + shared
             sections like Navigation live inside, each with its own gate).
             Previously gated to the five columns-based types only, which
             worked by ACCIDENT for Matrix/Pivot — the tmFlagsFor fallback
             co-stamped them `normal` — and hid their pickers once that flags
             bug was fixed. -->
        <ng-container *ngIf="isTmFlatShape">
          <!-- Normal / Editable / Grouped / Tree / Financial: choose columns
               (empty = all). Matrix/Pivot GENERATE their columns, so the
               column cards stay hidden for them. -->
          <!-- The column cards are a TEMPLATE so the header view toggle's second
               view gets the exact same per-column configuration (dataset, display
               name, editable + control, alignment, hidden, action columns,
               default filters). `columns` is the list being edited and `toggle`
               marks it as the second view's list. -->
          <ng-template #tmColumnCards let-columns="columns" let-toggle="toggle" let-label="label" let-hint="hint">
          <div class="control-group wf-field-drop-zone" (dragover)="allowFieldDrop($event)" (drop)="onTmColumnFieldDrop($event, toggle)" title="Drag columns here (leave empty to show every column)">
            <label>{{ label }}</label>
            <small class="control-hint" *ngIf="!columns.length">
              {{ hint }}
            </small>

            <div class="tm-column-list" *ngIf="columns.length">
              <div class="tm-column-card" *ngFor="let column of columns; let i = index">
                <div class="tm-column-card-head">
                  <span class="wf-step-index">{{ i + 1 }}</span>
                  <span class="tm-column-card-title">{{ column.displayName || column.field || (column.isAction ? 'Action column' : 'New column') }}</span>
                  <button type="button" mat-icon-button class="row-remove-button" (click)="removeTmColumn(column)" matTooltip="Remove column" aria-label="Remove column">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
                <div class="tm-column-card-body">
                  <!-- An ACTION column binds to no dataset field, so its
                       Dataset / Column pickers are hidden: only the header text
                       and the Action settings below apply to it. -->
                  <div class="control-group" *ngIf="!column.isAction">
                    <label>Dataset</label>
                    <ng-select
                      #ssTmColDataset
                      class="ss-select"
                      [class.is-open]="!!ssTmColDataset.isOpen?.()"
                      appendTo="body"
                      ngClass="ss-dropdown-panel"
                      [items]="entityOptions"
                      [loading]="entitiesLoading"
                      [clearable]="true"
                      [searchable]="true"
                      placeholder="Same as primary"
                      [ngModel]="column.dataset || drillDataset || null"
                      (ngModelChange)="onTmColumnDatasetChange(column, $event)"
                    ></ng-select>
                  </div>
                  <div class="control-group" *ngIf="!column.isAction">
                    <label>Column</label>
                    <ng-select
                      #ssTmColField
                      class="ss-select"
                      [class.is-open]="!!ssTmColField.isOpen?.()"
                      appendTo="body"
                      ngClass="ss-dropdown-panel"
                      [items]="columnsForTmColumn(column)"
                      [clearable]="true"
                      [searchable]="true"
                      [loading]="isTmColumnLoading(column)"
                      [placeholder]="tmColumnFieldPlaceholder(column)"
                      [ngModel]="column.field || null"
                      (ngModelChange)="onTmColumnFieldChange(column, $event)"
                    ></ng-select>
                  </div>
                  <div class="control-group">
                    <label>{{ column.isAction ? 'Header label' : 'Display name' }}</label>
                    <input
                      type="text"
                      [ngModel]="column.displayName"
                      (ngModelChange)="onTmColumnDisplayNameChange(column, $event)"
                      [placeholder]="column.isAction ? 'Optional — blank for a bare button column' : 'Header label'"
                    />
                  </div>
                  <!-- Per-column editability — ONE static label per table type so
                       the checkbox never flips its wording. Normal table: opt-IN
                       checkbox "Editable" (columns are read-only by default).
                       Editable table: opt-OUT checkbox "Read-only" (columns are
                       editable by default). -->
                  <div class="control-group tm-column-editable" *ngIf="isTmEditable || isTmNormal">
                    <label class="tm-editable-toggle" *ngIf="isTmNormal && !column.isAction" title="Check to let users edit this column's cells">
                      <input
                        type="checkbox"
                        [ngModel]="isTmColumnEditable(column)"
                        (ngModelChange)="onTmColumnEditableChange(column, $event)"
                      />
                      <span>Editable</span>
                    </label>
                    <label class="tm-editable-toggle" *ngIf="isTmEditable && !column.isAction" title="Check to lock this column as read-only">
                      <input
                        type="checkbox"
                        [ngModel]="!isTmColumnEditable(column)"
                        (ngModelChange)="onTmColumnEditableChange(column, !$event)"
                      />
                      <span>Read-only</span>
                    </label>
                    <!-- Presentation-only: a hidden column disappears from the
                         table but its value is STILL sent in the save payload. -->
                    <label class="tm-editable-toggle" *ngIf="!column.isAction" title="Hide this column in the table — its value is still sent in the save payload">
                      <input
                        type="checkbox"
                        [ngModel]="!!column.hidden"
                        (ngModelChange)="onTmColumnHiddenChange(column, $event)"
                      />
                      <span>Hidden</span>
                    </label>
                    <!-- Marks the column required: its header renders a * so
                         users can see which values must be filled in. -->
                    <label class="tm-editable-toggle" *ngIf="!column.isAction" title="Mark this column mandatory — its header shows a * in the table">
                      <input
                        type="checkbox"
                        [ngModel]="!!column.mandatory"
                        (ngModelChange)="onTmColumnMandatoryChange(column, $event)"
                      />
                      <span>Mandatory</span>
                    </label>
                    <!-- ACTION column: replaces the column's value with a button
                         in every row (see the Action block below). -->
                    <label class="tm-editable-toggle" title="Turn this into an action column — every row shows a button instead of a value">
                      <input
                        type="checkbox"
                        [ngModel]="!!column.isAction"
                        (ngModelChange)="onTmColumnIsActionChange(column, $event)"
                      />
                      <span>Action column</span>
                    </label>
                  </div>

                  <!-- ── Action column settings ──────────────────────────────
                       Only shown once "Action column" is ticked. The column
                       needs no dataset field: its `field` is just the header
                       key, so nothing is read from or saved to the row. -->
                  <div class="tm-column-action-box" *ngIf="column.isAction">
                    <div class="control-row">
                      <div class="control-group">
                        <label>On click</label>
                        <ng-select
                          #ssTmActKind
                          class="ss-select"
                          [class.is-open]="!!ssTmActKind.isOpen?.()"
                          appendTo="body"
                          ngClass="ss-dropdown-panel"
                          [items]="tmColumnActionKinds"
                          bindLabel="label"
                          bindValue="value"
                          [clearable]="true"
                          [searchable]="false"
                          [ngModel]="column.actionKind || 'emit'"
                          (ngModelChange)="column.actionKind = $event ?? ''; onTmColumnActionChange()"
                        ></ng-select>
                      </div>
                      <div class="control-group">
                        <label>Icon</label>
                        <!-- Live preview beside the field: the input holds an
                             icon NAME, so without rendering it there was no way
                             to tell a valid name from a typo until you looked at
                             the table. -->
                        <div class="tm-action-icon-row">
                          <input
                            type="text"
                            [ngModel]="column.buttonIcon"
                            (ngModelChange)="column.buttonIcon = $event; onTmColumnActionChange()"
                            placeholder="visibility / history / 👁"
                            title="A Material icon name (visibility, history, edit, download) or any emoji / short glyph"
                          />
                          <span
                            class="tm-action-icon-preview"
                            [class.is-empty]="!column.buttonIcon?.trim()"
                            [title]="column.buttonIcon?.trim() ? 'Preview — this is how it renders in the table' : 'No icon set'"
                          >
                            <mat-icon *ngIf="isTmMaterialIcon(column.buttonIcon)">{{ column.buttonIcon }}</mat-icon>
                            <span *ngIf="column.buttonIcon?.trim() && !isTmMaterialIcon(column.buttonIcon)">{{ column.buttonIcon }}</span>
                            <span class="tm-action-icon-none" *ngIf="!column.buttonIcon?.trim()">—</span>
                          </span>
                        </div>
                        <small class="control-hint" *ngIf="column.buttonIcon?.trim() && isTmMaterialIcon(column.buttonIcon)">
                          If the preview shows the name as text instead of a symbol, that Material icon name doesn't exist.
                        </small>
                      </div>
                      <div class="control-group">
                        <label>Button text</label>
                        <input
                          type="text"
                          [ngModel]="column.buttonLabel"
                          (ngModelChange)="column.buttonLabel = $event; onTmColumnActionChange()"
                          placeholder="Optional — icon only if blank"
                        />
                      </div>
                    </div>

                    <!-- popupComponent: point at a POPUP tile on this dashboard.
                         Its content blocks (heading / text / key-value / table /
                         apiTable / buttons) render in the row's popup, with
                         {field} filled from that row. -->
                    <div class="control-row" *ngIf="column.actionKind === 'popupComponent'">
                      <div class="control-group control-group-full">
                        <label>Use popup</label>
                        <ng-select
                          #ssTmActPopup
                          class="ss-select"
                          [class.is-open]="!!ssTmActPopup.isOpen?.()"
                          appendTo="body"
                          ngClass="ss-dropdown-panel"
                          [items]="tmPopupComponentOptions"
                          bindLabel="label"
                          bindValue="value"
                          [clearable]="true"
                          [searchable]="true"
                          [placeholder]="tmPopupComponentOptions.length ? 'Pick a popup component' : 'No popup components on this dashboard yet'"
                          [ngModel]="column.popupComponentId || null"
                          (ngModelChange)="column.popupComponentId = $event ?? ''; onTmColumnActionChange()"
                        ></ng-select>
                        <small class="control-hint is-error" *ngIf="tmPopupComponentMissing(column)">
                          ⚠ That popup is no longer on this dashboard — pick another.
                        </small>
                        <small class="control-hint" *ngIf="!tmPopupComponentOptions.length">
                          Add a Popup component to this dashboard first, then choose it here.
                        </small>
                        <small class="control-hint" *ngIf="tmPopupComponentOptions.length">
                          Build the popup's heading / text / table blocks in that Popup component.
                          Use <code>{{ '{' }}field{{ '}' }}</code> in its block text or API URLs to pull values from the clicked row.
                          Its content is snapshotted on Generate — re-generate this table after editing the popup.
                        </small>
                      </div>
                    </div>

                    <!-- URL / method / body only matter for the API kinds. -->
                    <ng-container *ngIf="tmActionNeedsUrl(column)">
                      <div class="control-row">
                        <div class="control-group control-group-full">
                          <label>Action URL</label>
                          <input
                            type="text"
                            [ngModel]="column.actionUrl"
                            (ngModelChange)="column.actionUrl = $event; onTmColumnActionChange()"
                            placeholder="/cu_ref_config_rule/history"
                            title="Relative paths resolve against the platform API base, same as the other configured URLs"
                          />
                        </div>
                        <div class="control-group">
                          <label>Method</label>
                          <ng-select
                            #ssTmActMethod
                            class="ss-select"
                            [class.is-open]="!!ssTmActMethod.isOpen?.()"
                            appendTo="body"
                            ngClass="ss-dropdown-panel"
                            [items]="tmColumnActionMethods"
                            bindLabel="label"
                            bindValue="value"
                            [clearable]="true"
                            [searchable]="false"
                            [ngModel]="column.actionMethod || 'POST'"
                            (ngModelChange)="column.actionMethod = $event ?? ''; onTmColumnActionChange()"
                          ></ng-select>
                        </div>
                      </div>
                      <div class="control-row">
                        <div class="control-group control-group-full">
                          <label>Request body (JSON)</label>
                          <textarea
                            class="tm-action-body-editor"
                            rows="5"
                            spellcheck="false"
                            [class.is-invalid]="tmActionBodyError(column)"
                            [ngModel]="column.actionBody"
                            (ngModelChange)="column.actionBody = $event; onTmColumnActionChange()"
                            placeholder='Blank sends the whole row. Example:&#10;{&#10;  "id": "{rule_name}",&#10;  "premium": {incremental_premium}&#10;}'
                          ></textarea>
                          <!-- Live JSON check on the template (placeholders are
                               stubbed first, so a valid template never reads as
                               broken just because it has {tokens} in it). -->
                          <small class="control-hint is-error" *ngIf="tmActionBodyError(column)">
                            ⚠ {{ tmActionBodyError(column) }}
                          </small>
                          <small class="control-hint" *ngIf="!tmActionBodyError(column) && column.actionBody?.trim()">
                            ✓ Valid JSON
                          </small>
                          <small class="control-hint">
                            <strong>Dynamic values:</strong> wrap a column's field name in braces.
                            <code>"{{ '{' }}field{{ '}' }}"</code> (in quotes) sends it as text;
                            <code>{{ '{' }}field{{ '}' }}</code> (no quotes) keeps its real type — number, true/false, null.
                          </small>
                          <!-- The exact tokens this table can substitute, so the
                               author doesn't have to guess field names. -->
                          <small class="control-hint" *ngIf="tmActionTokens().length">
                            <strong>Available:</strong>
                            <button
                              *ngFor="let token of tmActionTokens()"
                              type="button"
                              class="tm-action-token"
                              (click)="appendTmActionToken(column, token)"
                              [title]="'Insert ' + token"
                            >{{ token }}</button>
                          </small>
                        </div>
                      </div>
                      <div class="control-row">
                        <div class="control-group" *ngIf="column.actionKind === 'apiPopup'">
                          <label>Response rows path</label>
                          <input
                            type="text"
                            [ngModel]="column.actionRowsPath"
                            (ngModelChange)="column.actionRowsPath = $event; onTmColumnActionChange()"
                            placeholder="history — blank = auto-detect"
                            title="Dot-path to the rows array. A single-element array wrapper is stepped through automatically, so 'history' works for [{ history: [...] }] too."
                          />
                        </div>
                        <!-- Sort + cap: this is how "show only the latest
                             version" is expressed — sort by the version/date
                             field descending and keep 1 row. -->
                        <div class="control-group" *ngIf="column.actionKind === 'apiPopup'">
                          <label>Sort rows by</label>
                          <input
                            type="text"
                            [ngModel]="column.actionRowsSortBy"
                            (ngModelChange)="column.actionRowsSortBy = $event; onTmColumnActionChange()"
                            placeholder="Version_no / Updated_date"
                            title="Field in the response to sort by. Nested fields also match by their short name."
                          />
                        </div>
                        <div class="control-group" *ngIf="column.actionKind === 'apiPopup' && column.actionRowsSortBy?.trim()">
                          <label>Direction</label>
                          <ng-select
                            #ssTmActSortDir
                            class="ss-select"
                            [class.is-open]="!!ssTmActSortDir.isOpen?.()"
                            appendTo="body"
                            ngClass="ss-dropdown-panel"
                            [items]="tmActionSortDirections"
                            bindLabel="label"
                            bindValue="value"
                            [clearable]="true"
                            [searchable]="false"
                            [ngModel]="column.actionRowsSortDir || 'desc'"
                            (ngModelChange)="column.actionRowsSortDir = $event ?? ''; onTmColumnActionChange()"
                          ></ng-select>
                        </div>
                        <!-- Pick + order the response columns. A history/audit
                             response usually carries far more fields than are
                             worth showing. -->
                        <div class="control-group control-group-full" *ngIf="column.actionKind === 'apiPopup'">
                          <label>Show columns</label>
                          <input
                            type="text"
                            [ngModel]="column.actionRowsColumns"
                            (ngModelChange)="column.actionRowsColumns = $event; onTmColumnActionChange()"
                            placeholder="Version_no, Updated_by, comment — blank = all"
                            title="Comma-separated. Each entry matches the response field by its short name or full path. Use name:Label to rename a header."
                          />
                          <small class="control-hint">
                            In order, comma-separated. Short names work
                            (<code>comment</code> matches <code>DATA.comment</code>), and
                            <code>name:Label</code> renames the header — e.g.
                            <code>Version_no:Version, DATA.incremental_premium:Premium</code>.
                            Blank shows every column.
                          </small>
                        </div>
                        <div class="control-group" *ngIf="column.actionKind === 'apiPopup'">
                          <label>Max rows</label>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            [ngModel]="column.actionMaxRows"
                            (ngModelChange)="column.actionMaxRows = $event; onTmColumnActionChange()"
                            placeholder="0 = all"
                            title="Show at most this many rows after sorting. Set 1 with a descending sort to show only the latest."
                          />
                        </div>
                        <div class="control-group tm-column-editable" *ngIf="column.actionKind === 'api' || column.actionKind === 'apiPopup'">
                          <label class="tm-editable-toggle" title="Re-fetch the table after a successful call — for actions that change the row">
                            <input
                              type="checkbox"
                              [ngModel]="!!column.actionRefresh"
                              (ngModelChange)="column.actionRefresh = $event; onTmColumnActionChange()"
                            />
                            <span>Refresh table after call</span>
                          </label>
                        </div>
                      </div>
                    </ng-container>

                    <div class="control-row">
                      <div class="control-group" *ngIf="column.actionKind === 'rowPopup' || column.actionKind === 'apiPopup'">
                        <label>Popup title</label>
                        <input
                          type="text"
                          [ngModel]="column.actionTitle"
                          (ngModelChange)="column.actionTitle = $event; onTmColumnActionChange()"
                          [placeholder]="column.displayName || 'Row details'"
                        />
                      </div>
                      <div class="control-group control-group-full">
                        <label>Confirm before running</label>
                        <input
                          type="text"
                          [ngModel]="column.actionConfirm"
                          (ngModelChange)="column.actionConfirm = $event; onTmColumnActionChange()"
                          placeholder="Blank = no confirmation. e.g. Approve this rule?"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="control-group" *ngIf="(isTmEditable || isTmNormal) && isTmColumnEditable(column) && !column.isAction">
                    <label>Input type</label>
                    <ng-select
                      #ssTmColControl
                      class="ss-select"
                      [class.is-open]="!!ssTmColControl.isOpen?.()"
                      appendTo="body"
                      ngClass="ss-dropdown-panel"
                      [items]="tmColumnControls"
                      bindLabel="label"
                      bindValue="value"
                      [clearable]="true"
                      [searchable]="true"
                      placeholder="Text box"
                      [ngModel]="column.control || 'text'"
                      (ngModelChange)="onTmColumnControlChange(column, $event)"
                    ></ng-select>
                  </div>
                  <div class="control-group" *ngIf="(isTmEditable || isTmNormal) && isTmColumnEditable(column) && !column.isAction">
                    <label class="tm-editable-toggle" title="Show Save all only when this column was edited">
                      <input
                        type="checkbox"
                        [ngModel]="!!column.saveAllTrigger"
                        (ngModelChange)="onTmColumnSaveAllTriggerChange(column, $event)"
                      />
                      <span>Show Save all on edit</span>
                    </label>
                  </div>
                  <div class="control-group">
                    <label>Alignment</label>
                    <ng-select
                      #ssTmColAlign
                      class="ss-select"
                      [class.is-open]="!!ssTmColAlign.isOpen?.()"
                      appendTo="body"
                      ngClass="ss-dropdown-panel"
                      [items]="tmColumnAlignments"
                      bindLabel="label"
                      bindValue="value"
                      [clearable]="true"
                      [searchable]="false"
                      placeholder="Default"
                      [ngModel]="column.align || null"
                      (ngModelChange)="onTmColumnAlignChange(column, $event)"
                    ></ng-select>
                  </div>
                </div>

                <!-- Default filter attached to THIS column's row — applied on the
                     table's FIRST data request, before any global filter. "Filter
                     column" defaults to this row's own column but can be pointed
                     at ANY other column (e.g. attach a rule_categories filter to
                     the Rule Name row even though rule_categories isn't itself
                     displayed). Blank value = no filter for this row. -->
                <div class="tm-column-card-body tm-column-filter-row">
                  <div class="control-group">
                    <label>Filter column</label>
                    <ng-select
                      #ssTmColFilterField
                      class="ss-select"
                      [class.is-open]="!!ssTmColFilterField.isOpen?.()"
                      appendTo="body"
                      ngClass="ss-dropdown-panel"
                      [items]="columnsForTmColumn(column)"
                      [clearable]="true"
                      [searchable]="true"
                      [loading]="isTmColumnLoading(column)"
                      [placeholder]="column.field || 'Same as this column'"
                      [ngModel]="column.filterField || null"
                      (ngModelChange)="onTmColumnFilterFieldChange(column, $event)"
                    ></ng-select>
                  </div>
                  <div class="control-group">
                    <label>Operator</label>
                    <ng-select
                      #ssTmColFilterOp
                      class="ss-select"
                      [class.is-open]="!!ssTmColFilterOp.isOpen?.()"
                      appendTo="body"
                      ngClass="ss-dropdown-panel"
                      [items]="tmInitialFilterOperators"
                      bindLabel="label"
                      bindValue="value"
                      [clearable]="true"
                      [ngModel]="column.filterOperator"
                      (ngModelChange)="onTmColumnFilterOperatorChange(column, $event)"
                    ></ng-select>
                  </div>
                  <div class="control-group">
                    <label>Value</label>
                    <input
                      type="text"
                      [ngModel]="column.filterValue"
                      (ngModelChange)="onTmColumnFilterValueChange(column, $event)"
                      placeholder="Leave blank for no default filter"
                    />
                  </div>
                </div>
              </div>
            </div>

            <button type="button" mat-stroked-button class="add-step-button tm-add-column" (click)="addTmColumn(toggle)"><mat-icon>add</mat-icon> Add column</button>
          </div>
          </ng-template>

          <!-- View 1 (the table's normal columns). -->
          <ng-container
            *ngIf="isTmNormal || isTmEditable || isTmGrouped || isTmTreegrid || isTmFinancial"
            [ngTemplateOutlet]="tmColumnCards"
            [ngTemplateOutletContext]="{
              columns: tmColumns,
              toggle: false,
              label: fieldLabel('tableMatrix', 'columns', 'Columns'),
              hint: 'No columns picked — the table shows every column of the primary dataset. Add rows to pick specific columns (from any dataset) and rename their headers.'
            }"
          ></ng-container>

          <!-- ══ Header view toggle: ONE table, two configured views ══
               Option 1 is the table exactly as configured above. Option 2 gets
               its own dataset + columns, fetched with its own compute payload
               when the user flips the toggle in the table header. -->
          <div class="control-group tm-view-toggle-config" *ngIf="isTmNormal || isTmEditable || isTmGrouped || isTmTreegrid || isTmFinancial">
            <label class="tm-editable-toggle" title="Show a two-option toggle in the table header that swaps the columns and the dataset">
              <input type="checkbox" [ngModel]="tmToggleEnabled" (ngModelChange)="tmToggleEnabled = $event; onTableMatrixTypeChange()" />
              <span>Enable header view toggle</span>
            </label>
            <small class="control-hint">
              Adds a two-option switch to the table header. The first option shows the columns configured above; the second shows its own dataset and columns, requested with its own payload when picked.
            </small>

            <ng-container *ngIf="tmToggleEnabled">
              <div class="control-row">
                <div class="control-group">
                  <label>Option 1 label</label>
                  <input type="text" placeholder="View 1" [ngModel]="tmToggleLabel1" (ngModelChange)="tmToggleLabel1 = $event; onTableMatrixTypeChange()" />
                </div>
                <div class="control-group">
                  <label>Option 2 label</label>
                  <input type="text" placeholder="View 2" [ngModel]="tmToggleLabel2" (ngModelChange)="tmToggleLabel2 = $event; onTableMatrixTypeChange()" />
                </div>
              </div>

              <div class="control-group">
                <label>Option 2 — dataset</label>
                <ng-select
                  #ssTmToggleDataset
                  class="ss-select"
                  [class.is-open]="!!ssTmToggleDataset.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="entityOptions"
                  [loading]="entitiesLoading"
                  [clearable]="true"
                  [searchable]="true"
                  placeholder="Same as primary"
                  [ngModel]="tmToggleDataset || null"
                  (ngModelChange)="onTmToggleDatasetChange($event)"
                ></ng-select>
              </div>

              <!-- Option 2's columns get the SAME per-column configuration as
                   the table's normal columns. -->
              <ng-container
                [ngTemplateOutlet]="tmColumnCards"
                [ngTemplateOutletContext]="{
                  columns: tmToggleColumns,
                  toggle: true,
                  label: 'Option 2 — columns',
                  hint: 'No columns picked — add rows to choose the columns this view shows. These are the columns sent in the request when option 2 is selected.'
                }"
              ></ng-container>
            </ng-container>
          </div>

          <div class="control-row" *ngIf="isTmNormal">
            <div class="control-group wf-field-drop-zone" (dragover)="allowFieldDrop($event)" (drop)="onTmGroupByFieldDrop($event)" title="Drag a column here to group rows">
              <label>{{ fieldLabel('tableMatrix', 'groupBy', 'Group rows by') }}</label>
              <ng-select
                #ssTmGroup
                class="ss-select"
                [class.is-open]="!!ssTmGroup.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="drillDatasetColumns"
                [clearable]="true"
                [searchable]="true"
                [loading]="isDrillDatasetColumnsLoading"
                [disabled]="!drillDataset"
                placeholder="No grouping"
                [ngModel]="tmGroupBy || null"
                (ngModelChange)="tmGroupBy = $event || ''; onTableMatrixTypeChange()"
              ></ng-select>
            </div>
            <div class="control-group">
              <label>{{ fieldLabel('tableMatrix', 'aggregation', 'Aggregation') }}</label>
              <ng-select
                #ssTmAgg
                class="ss-select"
                [class.is-open]="!!ssTmAgg.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="tmAggregations"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [ngModel]="tmAggregation"
                (ngModelChange)="tmAggregation = $event ?? ''; onTableMatrixTypeChange()"
              ></ng-select>
            </div>
          </div>

          <!-- ══ Matrix: row dimension × column dimension × measure ══ -->
          <ng-container *ngIf="isTmMatrix">
            <div class="control-row">
              <div class="control-group">
                <label>Row dimension</label>
                <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="drillDatasetColumns" [clearable]="true" [searchable]="true" [disabled]="!drillDataset" placeholder="Rows down" [ngModel]="tmRowField || null" (ngModelChange)="tmRowField = $event || ''; onTableMatrixTypeChange()"></ng-select>
              </div>
              <div class="control-group">
                <label>Column dimension</label>
                <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="drillDatasetColumns" [clearable]="true" [searchable]="true" [disabled]="!drillDataset" placeholder="Columns across" [ngModel]="tmColumnField || null" (ngModelChange)="tmColumnField = $event || ''; onTableMatrixTypeChange()"></ng-select>
              </div>
            </div>
            <div class="control-group">
              <label>Value (measure)</label>
              <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="drillDatasetColumns" [clearable]="true" [searchable]="true" [disabled]="!drillDataset" placeholder="Numeric column to aggregate" [ngModel]="tmValueField || null" (ngModelChange)="tmValueField = $event || ''; onTableMatrixTypeChange()"></ng-select>
            </div>
          </ng-container>

          <!-- ══ Pivot: row fields × column fields × measure ══ -->
          <ng-container *ngIf="isTmPivot">
            <div class="control-group">
              <label>Row fields</label>
              <ng-select class="ss-select ss-select--multi" appendTo="body" ngClass="ss-dropdown-panel" [items]="drillDatasetColumns" [multiple]="true" [closeOnSelect]="false" [clearable]="true" [searchable]="true" [disabled]="!drillDataset" placeholder="Rows (one or more)" [ngModel]="tmRowFields" (ngModelChange)="tmRowFields = $event || []; onTableMatrixTypeChange()"></ng-select>
            </div>
            <div class="control-group">
              <label>Column fields</label>
              <ng-select class="ss-select ss-select--multi" appendTo="body" ngClass="ss-dropdown-panel" [items]="drillDatasetColumns" [multiple]="true" [closeOnSelect]="false" [clearable]="true" [searchable]="true" [disabled]="!drillDataset" placeholder="Columns (one or more)" [ngModel]="tmColumnFields2" (ngModelChange)="tmColumnFields2 = $event || []; onTableMatrixTypeChange()"></ng-select>
            </div>
            <div class="control-group">
              <label>Column order (optional)</label>
              <input
                type="text"
                class="ss-input"
                placeholder="e.g. Mining, Marine — anything left out (like Total) moves to the end"
                [ngModel]="tmPivotColumnOrderText"
                (ngModelChange)="tmPivotColumnOrderText = $event; onTableMatrixTypeChange()"
              />
              <small class="control-hint">Comma-separated band names, in the order you want them to appear. Bands you don't list keep their place after the listed ones — leave "Total" out to push it to the far right.</small>
            </div>
            <div class="control-group">
              <label>Sort (fallback for values above)</label>
              <select
                class="ss-input"
                [ngModel]="tmPivotColumnOrderSort"
                (ngModelChange)="tmPivotColumnOrderSort = $event; onTableMatrixTypeChange()"
              >
                <option value="">Default (data order)</option>
                <option value="custom">Custom order (use the list above)</option>
                <option value="alpha-asc">A to Z</option>
                <option value="alpha-desc">Z to A</option>
              </select>
              <small class="control-hint">
                Values listed above still come first, in that order. "Custom order" relies entirely on that list; A to Z / Z to A sorts every value you did not list.
              </small>
            </div>
            <div class="control-group">
              <label>Values (measures — each fans out per column value)</label>
              <ng-select class="ss-select ss-select--multi" appendTo="body" ngClass="ss-dropdown-panel" [items]="drillDatasetColumns" [multiple]="true" [closeOnSelect]="false" [clearable]="true" [searchable]="true" [disabled]="!drillDataset" placeholder="Numeric column(s) to aggregate" [ngModel]="tmValueFields" (ngModelChange)="tmValueFields = $event || []; onTableMatrixTypeChange()"></ng-select>
              <small class="control-hint">Pick several to get Excel-style year blocks: e.g. Column field = fiscal_year, Values = net_quantity + net_sales → 2024 Net Quantity | 2025 … | 2024 Net Sales | 2025 …</small>
            </div>
          </ng-container>

          <!-- ══ Grouped: ordered group levels ══ -->
          <div class="control-group" *ngIf="isTmGrouped">
            <label>Group by (levels, outer → inner)</label>
            <ng-select class="ss-select ss-select--multi" appendTo="body" ngClass="ss-dropdown-panel" [items]="drillDatasetColumns" [multiple]="true" [closeOnSelect]="false" [clearable]="true" [searchable]="true" [disabled]="!drillDataset" placeholder="Pick group columns in order" [ngModel]="tmGroupByFields" (ngModelChange)="tmGroupByFields = $event || []; onTableMatrixTypeChange()"></ng-select>
          </div>

          <!-- ══ Tree Grid: id + parent-id columns ══ -->
          <div class="control-row" *ngIf="isTmTreegrid">
            <div class="control-group">
              <label>Row ID column</label>
              <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="drillDatasetColumns" [clearable]="true" [searchable]="true" [disabled]="!drillDataset" placeholder="Unique id" [ngModel]="tmTreeIdField || null" (ngModelChange)="tmTreeIdField = $event || ''; onTableMatrixTypeChange()"></ng-select>
            </div>
            <div class="control-group">
              <label>Parent ID column</label>
              <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="drillDatasetColumns" [clearable]="true" [searchable]="true" [disabled]="!drillDataset" placeholder="Points to parent id" [ngModel]="tmTreeParentField || null" (ngModelChange)="tmTreeParentField = $event || ''; onTableMatrixTypeChange()"></ng-select>
            </div>
          </div>

          <!-- ══ Financial: role + indent columns ══ -->
          <div class="control-row" *ngIf="isTmFinancial">
            <div class="control-group">
              <label>Row role column (header/item/subtotal/total)</label>
              <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="drillDatasetColumns" [clearable]="true" [searchable]="true" [disabled]="!drillDataset" placeholder="Optional role column" [ngModel]="tmRoleField || null" (ngModelChange)="tmRoleField = $event || ''; onTableMatrixTypeChange()"></ng-select>
            </div>
            <div class="control-group">
              <label>Indent column (optional)</label>
              <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="drillDatasetColumns" [clearable]="true" [searchable]="true" [disabled]="!drillDataset" placeholder="Numeric indent depth" [ngModel]="tmIndentField || null" (ngModelChange)="tmIndentField = $event || ''; onTableMatrixTypeChange()"></ng-select>
            </div>
          </div>

          <!-- ══ Navigation: click a cell value → open a target dashboard filtered ══ -->
          <div class="tm-nav-config">
            <div class="tm-nav-head" (click)="toggleTmNavAccordion()">
              <mat-icon>{{ tmNavAccordionOpen ? 'expand_more' : 'chevron_right' }}</mat-icon>
              <span>Navigation</span>
              <span class="tm-nav-badge" *ngIf="tmNavEnabled">on</span>
            </div>
            <div class="tm-nav-body" *ngIf="tmNavAccordionOpen">
              <label class="dvm-format-toggle tm-nav-enable">
                <input type="checkbox" [ngModel]="tmNavEnabled" (ngModelChange)="tmNavEnabled = $event; onTableMatrixTypeChange()" />
                Enable dashboard navigation
              </label>

              <ng-container *ngIf="tmNavEnabled">
                <div class="control-group">
                  <label>Filter column (value sent on navigate)</label>
                  <ng-select
                    #ssTmNavKey
                    class="ss-select"
                    [class.is-open]="!!ssTmNavKey.isOpen?.()"
                    appendTo="body"
                    ngClass="ss-dropdown-panel"
                    [items]="drillDatasetColumns"
                    [clearable]="true"
                    [searchable]="true"
                    [disabled]="!drillDataset"
                    placeholder="Defaults to the first column"
                    [ngModel]="tmNavFilterKey || null"
                    (ngModelChange)="tmNavFilterKey = $event || ''; onTableMatrixTypeChange()"
                  ></ng-select>
                </div>

                <div class="control-group">
                  <label>Clickable columns (empty = filter column only)</label>
                  <ng-select
                    #ssTmNavCols
                    class="ss-select ss-select--multi"
                    [class.is-open]="!!ssTmNavCols.isOpen?.()"
                    appendTo="body"
                    ngClass="ss-dropdown-panel"
                    [items]="drillDatasetColumns"
                    [multiple]="true"
                    [closeOnSelect]="false"
                    [hideSelected]="false"
                    [clearable]="true"
                    [searchable]="true"
                    [disabled]="!drillDataset"
                    placeholder="Filter column only"
                    [ngModel]="tmNavColumns"
                    (ngModelChange)="tmNavColumns = $event || []; onTableMatrixTypeChange()"
                  ></ng-select>
                </div>

                <div class="control-group">
                  <label>Target dashboard</label>
                  <ng-select
                    #ssTmNavDash
                    class="ss-select"
                    [class.is-open]="!!ssTmNavDash.isOpen?.()"
                    appendTo="body"
                    ngClass="ss-dropdown-panel"
                    [items]="tmNavTargetDashboards"
                    bindLabel="label"
                    bindValue="value"
                    [clearable]="true"
                    [searchable]="true"
                    placeholder="Pick a saved dashboard"
                    [ngModel]="tmNavTargetDashboard || null"
                    (ngModelChange)="tmNavTargetDashboard = $event || ''; onTmNavTargetDashboardChange()"
                  ></ng-select>
                  <small class="control-hint" *ngIf="!tmNavTargetDashboards.length">No saved dashboards yet — publish/save one to use as a target.</small>
                </div>

                <div class="control-group" *ngIf="tmNavTargetDashboard">
                  <label>Target components (empty = all charts)</label>
                  <ng-select
                    #ssTmNavComps
                    class="ss-select ss-select--multi"
                    [class.is-open]="!!ssTmNavComps.isOpen?.()"
                    appendTo="body"
                    ngClass="ss-dropdown-panel"
                    [items]="tmNavTargetComponentOptions"
                    bindLabel="label"
                    bindValue="value"
                    [multiple]="true"
                    [closeOnSelect]="false"
                    [hideSelected]="false"
                    [clearable]="true"
                    [searchable]="true"
                    placeholder="All charts on the target dashboard"
                    [ngModel]="tmNavTargetComponents"
                    (ngModelChange)="tmNavTargetComponents = $event || []; onTableMatrixTypeChange()"
                  ></ng-select>
                </div>

                <div class="control-group">
                  <label>Navigate button label</label>
                  <input type="text" [ngModel]="tmNavButtonLabel" (ngModelChange)="tmNavButtonLabel = $event; onTableMatrixTypeChange()" placeholder="Navigate" />
                </div>
              </ng-container>
            </div>
          </div>
        </ng-container>

        <!-- ══ Notes: free text under/over the table (every table type). For the
             DRILL-DOWN type the same block renders lower down, next to its
             format accordions. ══ -->
        <ng-container *ngIf="!isTmDrilldown" [ngTemplateOutlet]="tmNotesConfig"></ng-container>

        <!-- Table format options — the SAME config-driven format accordions as
             the Bar/Waterfall layouts (Formatting / Totals / Columns /
             Interaction / Export / Upload), fed by the TABLE_MATRIX-scoped
             formatControls in charts/table-matrix.json, gated per table type by
             `visibleWhenType` and by the capabilityRegistry `sections` list;
             the values fold into the tile on Generate and live-refresh the
             edited tile. For the DRILL-DOWN type these accordions render AFTER
             the Hierarchy dimensions / Measures / Chart-interactions block (see
             the second outlet lower down) so the required mapping comes first. -->
        <ng-container *ngIf="!isTmDrilldown" [ngTemplateOutlet]="chartFormatAccordion"></ng-container>
      </div>

      <div class="drill-down-dimensions wf-field-drop-zone" *ngIf="isDrillDownLayout || (isTableMatrixLayout && isTmDrilldown)" (dragover)="allowFieldDrop($event)" (drop)="onDrillDimensionFieldDrop($event)">
        <header class="wf-section-header wf-steps-head">
          <h3>{{ layoutSectionTitle('dimensions', 'Hierarchy dimensions') }}</h3>
          <button type="button" mat-stroked-button (click)="addDrillDimension()">+ Add Level</button>
        </header>
        <small class="control-hint" *ngIf="layoutSectionHint('dimensions')">{{ layoutSectionHint('dimensions') }}</small>
        <small class="control-hint" *ngIf="!(isTableMatrixLayout && isTmDrilldown)">One dataset drives every level.</small>
        <small class="control-hint" *ngIf="isTableMatrixLayout && isTmDrilldown">The primary dataset below drives every level and measure by default — each card can pick a different dataset (client-side computation joins them). The table opens at Level 1; users expand rows to drill down.</small>
        <ng-container *ngTemplateOutlet="datasetPicker"></ng-container>

        <div class="wf-empty" *ngIf="!drillDownDimensions.length">
          No dimensions yet - add Level 1 and map fields like Type Grouping, Global Business Unit, Business Unit.
        </div>

        <div class="wf-step-card wf-field-drop-zone" *ngFor="let dimension of drillDownDimensions; let i = index; let first = first; let last = last" (dragover)="allowFieldDrop($event)" (drop)="onDrillDimensionFieldDrop($event, dimension)">
          <div class="wf-step-head">
            <span class="wf-step-index">{{ i + 1 }}</span>

            <ng-select
              #ssWfDimField
              class="ss-select wf-step-field-select"
              [class.is-open]="!!ssWfDimField.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="columnsForDrillRow(dimension)"
              [clearable]="true"
              [searchable]="true"
              [loading]="isDrillRowLoading(dimension)"
              [disabled]="!drillRowHasDataset(dimension)"
              [placeholder]="drillColumnPlaceholder('dimension')"
              [ngModel]="dimension.field || null"
              (ngModelChange)="dimension.field = $event || ''; onDrillDimensionChange()"
            ></ng-select>

            <input
              type="text"
              class="wf-step-label"
              [ngModel]="dimension.label"
              (ngModelChange)="dimension.label = $event; onDrillDimensionChange()"
              placeholder="Display name"
              [matTooltip]="i === 0 ? 'Shown as the tree column header' : 'Sent to the backend for this level'"
            />

            <div class="wf-step-actions">
              <button type="button" mat-icon-button [disabled]="first" (click)="moveDrillDimensionUp(i)" matTooltip="Move up" aria-label="Move level up">
                <mat-icon>arrow_upward</mat-icon>
              </button>
              <button type="button" mat-icon-button [disabled]="last" (click)="moveDrillDimensionDown(i)" matTooltip="Move down" aria-label="Move level down">
                <mat-icon>arrow_downward</mat-icon>
              </button>
              <button type="button" mat-icon-button class="row-remove-button" (click)="removeDrillDimension(dimension)" matTooltip="Remove level" aria-label="Remove level">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </div>

          <!-- Optional fixed ordering for THIS level's values. Left blank, the
               key is omitted from the payload entirely and the backend keeps its
               default ordering. -->
          <!-- Merged drill-down + pivot: a level may read from its OWN dataset
               (joined by the flat compute contract in client-side mode). -->
          <div class="control-row" *ngIf="isTableMatrixLayout && isTmDrilldown">
            <div class="control-group control-group-full">
              <label>Dataset</label>
              <ng-select
                #ssWfDimDataset
                class="ss-select"
                [class.is-open]="!!ssWfDimDataset.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="entityOptions"
                [loading]="entitiesLoading"
                [clearable]="true"
                [searchable]="true"
                placeholder="Same as primary"
                [ngModel]="dimension.dataset || drillDataset || null"
                (ngModelChange)="onDrillRowDatasetChange(dimension, $event, 'dimension')"
              ></ng-select>
            </div>
          </div>

          <div class="control-row" *ngIf="fieldEnabled('dimensions', 'valueOrder')">
            <div class="control-group control-group-full">
              <label>{{ fieldLabel('dimensions', 'valueOrder', 'Value order (optional)') }}</label>
              <input
                type="text"
                [ngModel]="dimension.valueOrder"
                (ngModelChange)="dimension.valueOrder = $event; onDrillDimensionChange()"
                placeholder="e.g. Base Options, Additional Options, Routing Cost"
              />
              <small class="control-hint">
                Comma-separated, in the order you want the rows to appear. Leave blank to keep the default order.
              </small>
            </div>
          </div>
          <div class="control-row" *ngIf="fieldEnabled('dimensions', 'valueOrder')">
            <div class="control-group control-group-full">
              <label>Sort (fallback for values above)</label>
              <select
                class="ss-input"
                [ngModel]="dimension.sortBy || ''"
                (ngModelChange)="dimension.sortBy = $event || undefined; onDrillDimensionChange()"
              >
                <option value="">Default (data order)</option>
                <option value="custom">Custom order (use the list above)</option>
                <option value="alpha-asc">A → Z</option>
                <option value="alpha-desc">Z → A</option>
              </select>
              <small class="control-hint">
                Values listed above still come first, in that order. "Custom order" relies entirely on that list (nothing else is auto-sorted); A → Z / Z → A instead sorts every value you DIDN'T list — e.g. A → Z puts K-series before Q-series automatically, without typing every model name.
              </small>
            </div>
          </div>

          <!-- Wrapped in a control-group like the measure card's checkbox: the
               builder SCSS hides any .control-row WITHOUT a .control-group
               (`.control-row:not(:has(.control-group))`), which silently hid
               this checkbox while "Editable values" on measures still showed. -->
          <div class="control-row" *ngIf="fieldEnabled('dimensions', 'editable')">
            <div class="control-group">
              <label class="checkbox-label" matTooltip="Renders this level's names in the tree column as editable text inputs (a rename); Save sends the old + new name through the table's save pipeline">
                <input
                  type="checkbox"
                  [ngModel]="dimension.editable"
                  (ngModelChange)="dimension.editable = $event; onDrillDimensionChange()"
                />
                {{ fieldLabel('dimensions', 'editable', 'Editable labels') }}
              </label>
            </div>
          </div>

          <!-- Edit control for the rename: a free-text input, or a dropdown over
               CUSTOM values (they need not exist in the data) and/or the labels
               already in the table at this level. -->
          <div class="control-row" *ngIf="dimension.editable && fieldEnabled('dimensions', 'editor')">
            <div class="control-group">
              <label>{{ fieldLabel('dimensions', 'editor', 'Edit control') }}</label>
              <ng-select
                #ssWfDimEditor
                class="ss-select"
                [class.is-open]="!!ssWfDimEditor.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="drillEditorKinds"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [ngModel]="dimension.editor || 'input'"
                (ngModelChange)="dimension.editor = $event === 'dropdown' ? 'dropdown' : undefined; onDrillDimensionChange()"
              ></ng-select>
            </div>
          </div>
          <div class="control-row" *ngIf="dimension.editable && dimension.editor === 'dropdown' && fieldEnabled('dimensions', 'editor')">
            <div class="control-group control-group-full">
              <label>{{ fieldLabel('dimensions', 'dropdownOptions', 'Dropdown values') }}</label>
              <input
                type="text"
                [ngModel]="dimension.dropdownOptions"
                (ngModelChange)="dimension.dropdownOptions = $event; onDrillDimensionChange()"
                placeholder="e.g. Approved, Pending, Rejected"
              />
              <small class="control-hint">
                Comma-separated custom values — they don't have to exist in the data. Leave blank to list only the values from the table.
              </small>
              <label class="checkbox-label" matTooltip="Adds every label currently present at this level to the dropdown, after the custom values">
                <input
                  type="checkbox"
                  [ngModel]="dimension.dropdownFromData === true"
                  (ngModelChange)="dimension.dropdownFromData = $event ? true : undefined; onDrillDimensionChange()"
                />
                {{ fieldLabel('dimensions', 'dropdownFromData', 'Also list values from the table') }}
              </label>
            </div>
          </div>
        </div>
      </div>
    <div class="drill-down-measures wf-field-drop-zone" *ngIf="isDrillDownLayout || (isTableMatrixLayout && isTmDrilldown)" (dragover)="allowFieldDrop($event)" (drop)="onDrillMeasureFieldDrop($event)">
      <header class="wf-section-header wf-steps-head">
        <h3>{{ layoutSectionTitle('measures', 'Measures') }}</h3>
        <button type="button" mat-stroked-button (click)="addDrillMeasure()">+ Add Measure</button>
      </header>
      <small class="control-hint" *ngIf="layoutSectionHint('measures')">{{ layoutSectionHint('measures') }}</small>

      <div class="wf-empty" *ngIf="!drillDownMeasures.length">
        No measures yet — add at least one value column (e.g. DN Price) to show and roll up at every level.
      </div>

      <div class="wf-step-card wf-field-drop-zone" *ngFor="let measure of drillDownMeasures; let i = index; let first = first; let last = last" (dragover)="allowFieldDrop($event)" (drop)="onDrillMeasureFieldDrop($event, measure)">
        <div class="wf-step-head">
          <span class="wf-step-index">{{ i + 1 }}</span>

          <ng-select
            #ssWfMeasureField
            class="ss-select wf-step-field-select"
            [class.is-open]="!!ssWfMeasureField.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="columnsForDrillRow(measure)"
            [clearable]="true"
            [searchable]="true"
            [loading]="isDrillRowLoading(measure)"
            [disabled]="!drillRowHasDataset(measure)"
            [placeholder]="drillColumnPlaceholder('measure')"
            [ngModel]="measure.field || null"
            (ngModelChange)="measure.field = $event || ''; onDrillMeasureChange()"
          ></ng-select>

          <input
            type="text"
            class="wf-step-label"
            [ngModel]="measure.label"
            (ngModelChange)="measure.label = $event; onDrillMeasureChange()"
            placeholder="Measure label"
          />
<div class="wf-step-actions">
            <button type="button" mat-icon-button [disabled]="first" (click)="moveDrillMeasureUp(i)" matTooltip="Move up" aria-label="Move measure up">
              <mat-icon>arrow_upward</mat-icon>
            </button>
            <button type="button" mat-icon-button [disabled]="last" (click)="moveDrillMeasureDown(i)" matTooltip="Move down" aria-label="Move measure down">
              <mat-icon>arrow_downward</mat-icon>
            </button>
            <button type="button" mat-icon-button class="row-remove-button" (click)="removeDrillMeasure(measure)" matTooltip="Remove measure" aria-label="Remove measure">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </div>

        <div class="wf-step-body">
          <!-- Merged drill-down + pivot: a measure may come from another dataset. -->
          <div class="control-group" *ngIf="isTableMatrixLayout && isTmDrilldown">
            <label>Dataset</label>
            <ng-select
              #ssWfMeasureDataset
              class="ss-select"
              [class.is-open]="!!ssWfMeasureDataset.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="entityOptions"
              [loading]="entitiesLoading"
              [clearable]="true"
              [searchable]="true"
              placeholder="Same as primary"
              [ngModel]="measure.dataset || drillDataset || null"
              (ngModelChange)="onDrillRowDatasetChange(measure, $event, 'measure')"
            ></ng-select>
          </div>
          <div class="control-group" *ngIf="fieldEnabled('measures', 'aggregation')">
            <label>{{ fieldLabel('measures', 'aggregation', 'Aggregation') }}</label>
            <ng-select
              #ssWfMeasureAgg
              class="ss-select"
              [class.is-open]="!!ssWfMeasureAgg.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="drillMeasureAggregationOptions"
              bindLabel="label"
              bindValue="value"
              [clearable]="true"
              [ngModel]="measure.aggregation"
              (ngModelChange)="measure.aggregation = $event ?? ''; onDrillMeasureChange()"
            ></ng-select>
            <!-- "Average per group": the column each vote is taken per (engine_model)
                 — the node shows the mean of one average per distinct value of it,
                 not the row-weighted mean. Client-side drill-down only. -->
            <ng-container *ngIf="isTableMatrixLayout && isTmDrilldown && isAvgByMeasure(measure)">
              <label>Group by column</label>
              <ng-select
                #ssWfMeasureGroupField
                class="ss-select"
                [class.is-open]="!!ssWfMeasureGroupField.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="columnsForDrillRow(measure)"
                [clearable]="true"
                [searchable]="true"
                [loading]="isDrillRowLoading(measure)"
                [disabled]="!drillRowHasDataset(measure)"
                placeholder="Column to average per (e.g. engine_model)"
                [ngModel]="measure.groupField || null"
                (ngModelChange)="measure.groupField = $event || ''; onDrillMeasureChange()"
              ></ng-select>
              <small class="control-hint" [class.control-hint-warning]="!measure.groupField">
                {{ measure.groupField
                  ? 'Each distinct ' + measure.groupField + ' under a row is averaged first, then those averages are averaged — one vote per ' + measure.groupField + ', whatever its row count. Re-generate after changing.'
                  : 'Pick the column to average per — without it this behaves like a plain Average over every row.' }}
              </small>
            </ng-container>
          </div>
          <div class="control-group" *ngIf="fieldEnabled('measures', 'rollUpStrategy')">
            <label>{{ fieldLabel('measures', 'rollUpStrategy', 'Roll-up strategy') }}</label>
            <ng-select
              #ssWfMeasureRollup
              class="ss-select"
              [class.is-open]="!!ssWfMeasureRollup.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="rollUpStrategies"
              bindLabel="label"
              bindValue="value"
              [clearable]="true"
              [ngModel]="measure.rollUpStrategy"
              (ngModelChange)="measure.rollUpStrategy = $event ?? ''; onDrillMeasureChange()"
            ></ng-select>
          </div>
          <div class="control-group" *ngIf="fieldEnabled('measures', 'total')">
            <label class="checkbox-label" matTooltip="Adds this measure to a dataset-wide Grand Total row at the bottom of the table">
              <input
                type="checkbox"
                [ngModel]="measure.total"
                (ngModelChange)="measure.total = $event; onDrillMeasureChange()"
              />
              {{ fieldLabel('measures', 'total', 'Show in Grand Total') }}
            </label>
          </div>
          <!-- Merged drill-down + pivot: value kind (number vs free-text attribute)
               and, once a pivot column exists, whether this measure fans out per
               column value or stays ONE column (Cost increase %, AOP Target…). -->
          <div class="control-group" *ngIf="isTableMatrixLayout && isTmDrilldown">
            <label>Value type</label>
            <ng-select
              #ssWfMeasureKind
              class="ss-select"
              [class.is-open]="!!ssWfMeasureKind.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="drillMeasureKinds"
              bindLabel="label"
              bindValue="value"
              [clearable]="true"
              [ngModel]="measure.kind || 'number'"
              (ngModelChange)="measure.kind = $event === 'text' ? 'text' : undefined; onDrillMeasureChange()"
            ></ng-select>
            <small class="control-hint" *ngIf="measure.kind === 'text'">Shown as text per row — a parent (or a pivot cell spanning several raw rows) lists its distinct values comma-separated, never summed. Tick Editable values for a free-text input (e.g. Voice of Business, Pricing Action reason).</small>
          </div>
          <!-- Percent display: adds a trailing "%" to this measure's cells
               (e.g. SSA %, Margin %, Cost Increase) — number measures only. -->
          <div class="control-group" *ngIf="isTableMatrixLayout && isTmDrilldown && measure.kind !== 'text'">
            <label class="checkbox-label" matTooltip="Shows this measure's values with a trailing % sign">
              <input
                type="checkbox"
                [ngModel]="measure.format === 'percent'"
                (ngModelChange)="measure.format = $event ? 'percent' : undefined; onDrillMeasureChange()"
              />
              Show as percent (%)
            </label>
          </div>
          <!-- Fraction scaling: only relevant once "Show as percent" is on.
               SSA % / Margin % are stored as 0-1 ratios (0.05 = 5%) and need
               ×100; Cost Increase is already stored in percentage points
               (4.6 = 4.6%) and must NOT be scaled. -->
          <div class="control-group" *ngIf="isTableMatrixLayout && isTmDrilldown && measure.kind !== 'text' && measure.format === 'percent'">
            <label class="checkbox-label" matTooltip="Tick if the raw value is a 0-1 ratio (e.g. 0.05) that needs ×100 to read as a percentage. Leave unticked if the raw value is already in percentage points (e.g. 4.6).">
              <input
                type="checkbox"
                [ngModel]="measure.percentScale === 'fraction'"
                (ngModelChange)="measure.percentScale = $event ? 'fraction' : undefined; onDrillMeasureChange()"
              />
              Value is a fraction (multiply by 100)
            </label>
            <small class="control-hint">Tick this for a column like Margin % or SSA % stored as 0.05 (needs to read "5%"). Leave unticked for a column like Cost Increase already stored as 4.6 (should read "4.6%", not "460%").</small>
          </div>
          <!-- Hide negative values: blanks the cell instead of showing e.g.
               "-8.6%" — the Total/Grand Total row is unaffected. -->
          <div class="control-group" *ngIf="isTableMatrixLayout && isTmDrilldown && measure.kind !== 'text' && measure.format === 'percent'">
            <label class="checkbox-label" matTooltip="Leaves this measure's cell blank instead of showing a negative value">
              <input
                type="checkbox"
                [ngModel]="!!measure.hideNegative"
                (ngModelChange)="measure.hideNegative = $event || undefined; onDrillMeasureChange()"
              />
              Hide negative values
            </label>
          </div>
          <!-- Decimal places override: this measure's own round-off, e.g. 0 for
               a whole-number Net Quantity column while the rest of the table
               keeps the shared "Decimal places" Format setting. -->
          <div class="control-group" *ngIf="isTableMatrixLayout && isTmDrilldown && measure.kind !== 'text'">
            <label>Decimal places (this column only)</label>
            <input
              type="number"
              class="ss-input"
              min="0"
              max="6"
              step="1"
              placeholder="Same as table default"
              [ngModel]="measure.decimals ?? null"
              (ngModelChange)="onDrillMeasureDecimalsChange(measure, $event)"
            />
            <small class="control-hint">Leave blank to use the table's shared "Decimal places" setting. Set to 0 for whole numbers (e.g. Net Quantity).</small>
          </div>
          <!-- Year scope (client mode): aggregate only the newest year's rows for
               this measure — Sales = 2026 sales instead of every year summed. The
               year column rides along in the flat rows (compute payload + a hidden
               helper measure in the saved payload for published recomputes). -->
          <div class="control-group" *ngIf="isTableMatrixLayout && isTmDrilldown">
            <label>Year scope</label>
            <ng-select
              #ssWfMeasureYearScope
              class="ss-select"
              [class.is-open]="!!ssWfMeasureYearScope.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="drillMeasureYearScopes"
              bindLabel="label"
              bindValue="value"
              [clearable]="true"
              [ngModel]="measure.yearScope || 'all'"
              (ngModelChange)="onDrillMeasureYearScopeChange(measure, $event)"
            ></ng-select>
            <ng-container *ngIf="measure.yearScope === 'latest'">
              <label>Year column</label>
              <ng-select
                #ssWfMeasureYearField
                class="ss-select"
                [class.is-open]="!!ssWfMeasureYearField.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="columnsForDrillRow(measure)"
                [clearable]="true"
                [searchable]="true"
                [loading]="isDrillRowLoading(measure)"
                [disabled]="!drillRowHasDataset(measure)"
                [placeholder]="effectiveYearField(measure) ? 'Auto: ' + effectiveYearField(measure) : 'Column holding the year (e.g. fiscal_year)'"
                [ngModel]="measure.yearField || null"
                (ngModelChange)="measure.yearField = $event || ''; onDrillMeasureChange()"
              ></ng-select>
              <!-- Blank column = the dataset's detected year column is used (shown as
                   the placeholder). No detectable year column = the scope cannot
                   apply and every year feeds the measure — say so, loudly. -->
              <small class="control-hint control-hint-warning" *ngIf="yearScopeUnresolved(measure)">No year column found in this dataset — pick one, or every year feeds this measure (the header will not show a year).</small>
              <small class="control-hint" *ngIf="!yearScopeUnresolved(measure)">Only the rows of the newest year in the data (e.g. 2026) feed this measure — at every level, in every pivot column and in the Total block. The header's info icon shows the year used. Re-generate after changing.</small>
              <label class="checkbox-label" matTooltip="The year column is only used to pick the newest year's rows and is hidden by default. Tick to show it as its own column in the table.">
                <input
                  type="checkbox"
                  [ngModel]="measure.showYearColumn === true"
                  (ngModelChange)="measure.showYearColumn = $event ? true : undefined; onDrillMeasureChange()"
                />
                Show year column in table
              </label>
            </ng-container>
          </div>
          <!-- Header text inside the right-hand Total block only (Totals = Right /
               Both): e.g. "Revenue" over the Sales summed across the verticals,
               while every vertical's own column still reads "Sales". -->
          <div class="control-group" *ngIf="isTableMatrixLayout && isTmDrilldown && measure.kind !== 'text' && measure.pivot !== false && measure.total && drillColumnDimensions.length && fieldEnabled('measures', 'rowTotalHeader')">
            <label>{{ fieldLabel('measures', 'rowTotalHeader', 'Header in Total block') }}</label>
            <input
              type="text"
              class="ss-input"
              [placeholder]="measure.label || measure.field || 'Same as the measure'"
              [ngModel]="measure.rowTotalHeader || ''"
              (ngModelChange)="measure.rowTotalHeader = $event; onDrillMeasureChange()"
            />
            <small class="control-hint">Only the column inside the trailing Total block is renamed (e.g. Revenue); the per-vertical columns keep the measure name. Re-generate after changing.</small>
          </div>
          <div class="control-group" *ngIf="isTableMatrixLayout && isTmDrilldown && isDrillClientMode">
            <label class="checkbox-label" matTooltip="Hide this complete column from the drill-down table, including its header, row values and total cell. The value remains available for formulas and saving.">
              <input
                type="checkbox"
                [ngModel]="measure.hidden === true"
                (ngModelChange)="measure.hidden = $event ? true : undefined; onDrillMeasureChange()"
              />
              Hide column
            </label>
          </div>
          <div class="control-group" *ngIf="isTableMatrixLayout && isTmDrilldown && drillColumnDimensions.length">
            <label class="checkbox-label" matTooltip="On: one column per pivot value (2024 Net Sales | 2025 Net Sales …). Off: a single column for this measure (e.g. Cost increase %, AOP Target).">
              <input
                type="checkbox"
                [ngModel]="measure.pivot !== false"
                (ngModelChange)="measure.pivot = $event ? undefined : false; onDrillMeasureChange()"
              />
              Split by pivot column
            </label>
          </div>
          <div class="control-group" *ngIf="fieldEnabled('measures', 'editable')">
            <label class="checkbox-label" matTooltip="Renders this measure's leaf-row cells as editable inputs (parents and the grand total re-sum as you edit); Save sends the changed values through the table's save pipeline">
              <input
                type="checkbox"
                [ngModel]="measure.editable"
                (ngModelChange)="measure.editable = $event; onDrillMeasureChange()"
              />
              {{ fieldLabel('measures', 'editable', 'Editable values') }}
            </label>
          </div>
          <!-- Edit control for the cell: a number / text input, or a dropdown
               over CUSTOM values (they need not exist in the data) and/or the
               values this column already holds in the table. -->
          <div class="control-group" *ngIf="measure.editable && fieldEnabled('measures', 'editor')">
            <label>{{ fieldLabel('measures', 'editor', 'Edit control') }}</label>
            <ng-select
              #ssWfMeasureEditor
              class="ss-select"
              [class.is-open]="!!ssWfMeasureEditor.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="drillEditorKinds"
              bindLabel="label"
              bindValue="value"
              [clearable]="true"
              [ngModel]="measure.editor || 'input'"
              (ngModelChange)="measure.editor = $event === 'dropdown' ? 'dropdown' : undefined; onDrillMeasureChange()"
            ></ng-select>
          </div>
          <div class="control-group" *ngIf="measure.editable && measure.editor === 'dropdown' && fieldEnabled('measures', 'editor')">
            <label>{{ fieldLabel('measures', 'dropdownOptions', 'Dropdown values') }}</label>
            <input
              type="text"
              [ngModel]="measure.dropdownOptions"
              (ngModelChange)="measure.dropdownOptions = $event; onDrillMeasureChange()"
              [placeholder]="measure.kind === 'text' ? 'e.g. Approved, Pending, Rejected' : 'e.g. 0, 25, 50, 100'"
            />
            <small class="control-hint">
              Comma-separated custom values — they don't have to exist in the data. Leave blank to list only the values from the table.<ng-container *ngIf="measure.kind !== 'text'"> A Number measure stores the pick as a number, so keep these numeric (or switch Value type to Text).</ng-container>
            </small>
            <label class="checkbox-label" matTooltip="Adds every value this column currently holds in the table to the dropdown, after the custom values">
              <input
                type="checkbox"
                [ngModel]="measure.dropdownFromData === true"
                (ngModelChange)="measure.dropdownFromData = $event ? true : undefined; onDrillMeasureChange()"
              />
              {{ fieldLabel('measures', 'dropdownFromData', 'Also list values from the table') }}
            </label>
          </div>
          <!-- Which tree levels the editable cell (input / dropdown) shows on.
               Multi-select over the dimensions: Level 1 only, Level 2 only, or
               any mix. Empty = default (Text: every level; Number: leaf rows). -->
          <div class="control-group" *ngIf="measure.editable && drillDownDimensions.length && fieldEnabled('measures', 'editableLevels')">
            <label>{{ fieldLabel('measures', 'editableLevels', 'Editable on levels') }}</label>
            <ng-select
              #ssWfMeasureLevels
              class="ss-select"
              [class.is-open]="!!ssWfMeasureLevels.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="drillEditableLevelOptions"
              bindLabel="label"
              bindValue="value"
              [multiple]="true"
              [closeOnSelect]="false"
              [clearable]="true"
              placeholder="Default (Text: every level · Number: leaf rows)"
              [ngModel]="measure.editableLevels"
              (ngModelChange)="onDrillMeasureLevelsChange(measure, $event)"
            ></ng-select>
            <small class="control-hint">
              Pick the levels whose rows get the editor — e.g. Level 1 only for a top-level dropdown. Total rows never edit. A Number measure edited on a parent keeps that value until saved; its own parents still re-sum.
            </small>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ Merged Drill-Down + Pivot extras (Table & Matrix drill-down type) ══
         Data computation mode, pivot column dimension(s) and formula columns.
         All optional — a plain hierarchy needs none of them. -->
    <div class="wf-section wf-section-plain drill-pivot-config" *ngIf="isTableMatrixLayout && isTmDrilldown">
      <div class="control-group">
        <label>{{ fieldLabel('tableMatrix', 'dataMode', 'Data computation') }}</label>
        <ng-select
          #ssDrillMode
          class="ss-select"
          [class.is-open]="!!ssDrillMode.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="drillDataModes"
          bindLabel="label"
          bindValue="value"
          [clearable]="true"
          [ngModel]="drillDataMode"
          (ngModelChange)="onDrillDataModeChange($event)"
        ></ng-select>
        <small class="control-hint" *ngIf="drillDataMode === 'client'">The joined flat rows are fetched and the hierarchy, pivot columns and formula columns are built in the browser — this enables multiple datasets, pivot columns and formulas.</small>
        <small class="control-warning" *ngIf="drillDataMode === 'server'">Backend tree (legacy): pivot column dimensions are ignored and every level / measure should come from the primary dataset. Switch to client-side to use them; formula columns still work.</small>
      </div>

      <!-- Totals placement: bottom grand-total row and/or the right-hand Total
           block across the pivot column values (Figma "TOTAL | Rev | Price %"). -->
      <div class="control-group" *ngIf="fieldEnabled('tableMatrix', 'totalsPlacement')">
        <label>{{ fieldLabel('tableMatrix', 'totalsPlacement', 'Totals') }}</label>
        <ng-select
          #ssDrillTotals
          class="ss-select"
          [class.is-open]="!!ssDrillTotals.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="drillTotalsPlacements"
          bindLabel="label"
          bindValue="value"
          [clearable]="true"
          [ngModel]="drillTotalsPlacement"
          (ngModelChange)="onDrillTotalsPlacementChange($event)"
        ></ng-select>
        <small class="control-hint" *ngIf="drillTotalsPlacement === 'right' || drillTotalsPlacement === 'both' || drillTotalsPlacement === 'left' || drillTotalsPlacement === 'both-left'">The Total block carries the measures ticked "Show in Grand Total", each summed across the pivot column values, plus any formula column whose inputs are all ticked (evaluated on those totals, like the bottom row); text attributes stay out. {{ drillTotalsPlacement === 'left' || drillTotalsPlacement === 'both-left' ? 'It sits directly after the first-level column, ahead of the pivot column blocks.' : 'It trails the pivot column blocks.' }} It needs a pivot column dimension{{ drillDataMode === 'client' ? '' : ' and client-side computation' }}.</small>
        <small class="control-hint" *ngIf="drillTotalsPlacement === 'none' || drillTotalsPlacement === 'right' || drillTotalsPlacement === 'left'">The bottom total row is hidden.</small>
      </div>

      <!-- Header view toggle for Drill-Down: "view 1" is the tree above;
           "view 2" swaps to a plain flat table with its own dataset + columns
           (a simple multi-select here rather than the Normal/Editable
           per-column card editor, which lives inside a differently-scoped
           template not reachable from this section). -->
      <div class="control-group tm-view-toggle-config">
        <label class="tm-editable-toggle" title="Show a two-option toggle in the table header that swaps between this tree and a flat table">
          <input type="checkbox" [ngModel]="tmToggleEnabled" (ngModelChange)="tmToggleEnabled = $event; onTableMatrixTypeChange()" />
          <span>Enable header view toggle</span>
        </label>
        <small class="control-hint">
          Adds a two-option switch to the table header. The first option shows this tree as configured above; the second shows a flat table from its own dataset and columns, requested with its own payload when picked.
        </small>

        <ng-container *ngIf="tmToggleEnabled">
          <div class="control-row">
            <div class="control-group">
              <label>Option 1 label</label>
              <input type="text" class="ss-input" placeholder="View 1" [ngModel]="tmToggleLabel1" (ngModelChange)="tmToggleLabel1 = $event; onTableMatrixTypeChange()" />
            </div>
            <div class="control-group">
              <label>Option 2 label</label>
              <input type="text" class="ss-input" placeholder="View 2" [ngModel]="tmToggleLabel2" (ngModelChange)="tmToggleLabel2 = $event; onTableMatrixTypeChange()" />
            </div>
          </div>

          <div class="control-group">
            <label>Option 2 — dataset</label>
            <ng-select
              #ssDrillToggleDataset
              class="ss-select"
              [class.is-open]="!!ssDrillToggleDataset.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="entityOptions"
              [loading]="entitiesLoading"
              [clearable]="true"
              [searchable]="true"
              placeholder="Same as primary"
              [ngModel]="tmToggleDataset || null"
              (ngModelChange)="onTmToggleDatasetChange($event)"
            ></ng-select>
          </div>

          <div class="control-group">
            <label>Option 2 — columns</label>
            <ng-select
              #ssDrillToggleColumns
              class="ss-select ss-select--multi"
              [class.is-open]="!!ssDrillToggleColumns.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="drillDatasetColumns"
              [multiple]="true"
              [closeOnSelect]="false"
              [clearable]="true"
              [searchable]="true"
              placeholder="Columns (empty = every column of that dataset)"
              [ngModel]="drillToggleColumnFields"
              (ngModelChange)="onDrillToggleColumnsChange($event)"
            ></ng-select>
            <small class="control-hint">These are the columns sent in the request when option 2 is selected — plain read-only columns, not the per-column edit/control configuration a Normal/Editable table's second view gets.</small>
          </div>
        </ng-container>
      </div>

      <!-- Pivot column dimension(s): every measure fans out per distinct value. -->
      <div class="drill-down-dimensions wf-field-drop-zone" (dragover)="allowFieldDrop($event)" (drop)="onDrillColumnDimensionFieldDrop($event)">
        <header class="wf-section-header wf-steps-head">
          <h3>{{ fieldLabel('tableMatrix', 'columnDimensions', 'Pivot column dimension(s)') }}</h3>
          <button type="button" mat-stroked-button (click)="addDrillColumnDimension()">+ Add Column</button>
        </header>
        <small class="control-hint">Optional (the Pivot Table's column fields). Every measure fans out per distinct value of this column — e.g. Fiscal Year → 2024 Net Sales | 2025 Net Sales | … — with a grand total per column. Leave empty for a plain hierarchy.</small>

        <div class="wf-step-card wf-field-drop-zone" *ngFor="let dimension of drillColumnDimensions; let i = index" (dragover)="allowFieldDrop($event)" (drop)="onDrillColumnDimensionFieldDrop($event, dimension)">
          <div class="wf-step-head">
            <span class="wf-step-index">{{ i + 1 }}</span>
            <ng-select
              #ssWfColDimField
              class="ss-select wf-step-field-select"
              [class.is-open]="!!ssWfColDimField.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="columnsForDrillRow(dimension)"
              [clearable]="true"
              [searchable]="true"
              [loading]="isDrillRowLoading(dimension)"
              [disabled]="!drillRowHasDataset(dimension)"
              placeholder="Column whose values become columns"
              [ngModel]="dimension.field || null"
              (ngModelChange)="dimension.field = $event || ''; onDrillColumnDimensionChange()"
            ></ng-select>
            <input
              type="text"
              class="wf-step-label"
              [ngModel]="dimension.label"
              (ngModelChange)="dimension.label = $event; onDrillColumnDimensionChange()"
              placeholder="Display name"
            />
            <div class="wf-step-actions">
              <button type="button" mat-icon-button class="row-remove-button" (click)="removeDrillColumnDimension(dimension)" matTooltip="Remove pivot column" aria-label="Remove pivot column">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </div>
          <div class="control-row">
            <div class="control-group">
              <label>Dataset</label>
              <ng-select
                #ssWfColDimDataset
                class="ss-select"
                [class.is-open]="!!ssWfColDimDataset.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="entityOptions"
                [loading]="entitiesLoading"
                [clearable]="true"
                [searchable]="true"
                placeholder="Same as primary"
                [ngModel]="dimension.dataset || drillDataset || null"
                (ngModelChange)="onDrillRowDatasetChange(dimension, $event, 'columnDimension')"
              ></ng-select>
            </div>
            <div class="control-group">
              <label>Value order (optional)</label>
              <input
                type="text"
                [ngModel]="dimension.valueOrder"
                (ngModelChange)="dimension.valueOrder = $event; onDrillColumnDimensionChange()"
                placeholder="e.g. Mining, Marine, Oil &amp; Gas, Rail (blank = design preset)"
              />
              <small class="control-hint">
                Comma-separated, in the order you want the pivot values to appear. Leave blank to keep the default order.
              </small>
            </div>
          </div>
          <div class="control-row">
            <div class="control-group control-group-full">
              <label>Sort (fallback for values above)</label>
              <select
                class="ss-input"
                [ngModel]="dimension.sortBy || ''"
                (ngModelChange)="dimension.sortBy = $event || undefined; onDrillColumnDimensionChange()"
              >
                <option value="">Default (data order)</option>
                <option value="custom">Custom order (use the list above)</option>
                <option value="alpha-asc">A to Z</option>
                <option value="alpha-desc">Z to A</option>
              </select>
              <small class="control-hint">
                Values listed above still come first, in that order. "Custom order" relies entirely on that list; A to Z / Z to A sorts every value you did not list.
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- Formula columns — computed per tree row from its aggregated measures
           (clone of the Bar Formula Builder, bound to the drill-down state). -->
      <div class="dvm-formula-builder" *ngIf="formulaBuilderEnabled">
        <button type="button" class="dvm-formula-head" (click)="drillFormulaAccordionOpen = !drillFormulaAccordionOpen">
          <span><mat-icon class="dvm-formula-head-icon">functions</mat-icon> {{ fieldLabel('tableMatrix', 'computedColumns', 'Formula columns') }} <span class="wf-step-count">{{ drillComputedColumns.length }}</span></span>
          <mat-icon>{{ drillFormulaAccordionOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
        </button>

        <div class="dvm-formula-body" *ngIf="drillFormulaAccordionOpen">
          <p class="dvm-formula-hint">Computed for every row of the tree from that row's aggregated measures (a ratio at a parent divides the sums) and re-evaluated as values are edited. Reference measures by their header text — e.g. <code>([Net Sales] - [Total Cost]) / [Net Sales] * 100</code>. Operators + - * / and parentheses; IF(cond, then, else), abs/round/min/max.</p>

          <div class="dvm-formula-templates" *ngIf="barFormulaTemplates.length">
            <small class="control-hint">{{ formulaBuilderConfig.templatesLabel || 'Quick templates:' }}</small>
            <button
              type="button"
              class="dvm-formula-chip"
              *ngFor="let tpl of barFormulaTemplates"
              [title]="tpl.description"
              (click)="applyDrillFormulaTemplate(tpl)"
            >{{ tpl.label }}</button>
          </div>

          <div class="dvm-formula-list" *ngIf="drillComputedColumns.length">
            <div class="dvm-formula-item" *ngFor="let col of drillComputedColumns">
              <div class="dvm-formula-item-info">
                <label>Column name (table header)
                  <input type="text" [value]="col.name"
                    (change)="renameDrillComputedColumn(col, $any($event.target))"
                    aria-label="Formula column name" />
                </label>
                <label>Expression
                  <input type="text" [value]="col.expression"
                    (change)="updateDrillComputedExpression(col, $any($event.target))"
                    aria-label="Formula column expression" />
                </label>
                <small class="control-hint">Uses this expression. Clear it to remove the formula.</small>
                <small *ngIf="col.after" class="control-hint">placed after: {{ drillFormulaAnchorLabel(col.after) }}</small>
              </div>
              <button type="button" mat-icon-button class="bar-refline-remove row-remove-button" (click)="removeDrillComputedColumn(col.name)" matTooltip="Remove column"><mat-icon>close</mat-icon></button>
            </div>
          </div>

          <div class="dvm-formula-add">
            <input type="text" [(ngModel)]="newDrillColumnName" [placeholder]="formulaBuilderConfig.namePlaceholder || 'Column name'" />
            <input type="text" [(ngModel)]="newDrillColumnExpression" placeholder="Expression (e.g. [Net Sales] / [Net Quantity])" />
            <!-- Column position: after which measure / formula this column shows
                 (e.g. SSA % right after Net Sales, before AOP Target). -->
            <ng-select
              #ssDrillFormulaAfter
              class="ss-select"
              [class.is-open]="!!ssDrillFormulaAfter.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="drillFormulaAnchorOptions"
              bindLabel="label"
              bindValue="value"
              [clearable]="true"
              [searchable]="true"
              placeholder="Place after…"
              [(ngModel)]="newDrillColumnAfter"
            ></ng-select>
            <button type="button" class="add-step-button" mat-stroked-button (click)="addDrillComputedColumn()"><mat-icon>add</mat-icon> {{ formulaBuilderConfig.addLabel || 'Add column' }}</button>
          </div>
          <small class="control-warning" *ngIf="newDrillColumnError">{{ newDrillColumnError }}</small>
          <small class="control-hint" *ngIf="!drillDownMeasures.length">Add at least one measure first — formulas reference measures.</small>
          <!-- The names this table's formulas may use (measure labels + raw
               fields + earlier formula columns) — click to insert [Name]. -->
          <div class="dvm-formula-templates" *ngIf="drillFormulaColumns.length">
            <small class="control-hint">Available names (click to insert):</small>
            <button type="button" class="dvm-formula-chip" *ngFor="let name of drillFormulaColumns" (click)="insertDrillFormulaRef(name)" title="Insert into the expression">[{{ name }}]</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Column order: ONE list of every output column (measure blocks +
         formula columns) in render order. Move up / down here overrides the
         measure order and the formulas' "placed after" anchors; it applies to
         the client-side tree (the backend tree fixes its own key order). -->
    <div class="dvm-formula-builder" *ngIf="drillDataMode === 'client' && fieldEnabled('tableMatrix', 'columnOrder')">
      <button type="button" class="dvm-formula-head" (click)="drillColumnOrderAccordionOpen = !drillColumnOrderAccordionOpen">
        <span><mat-icon class="dvm-formula-head-icon">view_column</mat-icon> {{ fieldLabel('tableMatrix', 'columnOrder', 'Column order') }} <span class="wf-step-count">{{ drillColumnOrderItems.length }}</span><small *ngIf="drillColumnOrder.length" class="control-hint"> (custom)</small><small *ngIf="!drillColumnOrder.length && drillColumnOrderPreset.length" class="control-hint"> (design preset)</small></span>
        <mat-icon>{{ drillColumnOrderAccordionOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
      </button>

      <div class="dvm-formula-body" *ngIf="drillColumnOrderAccordionOpen">
        <p class="dvm-formula-hint">Every column the table renders, top to bottom = left to right. Move a row to change where it shows; a measure split by the pivot column moves together with all its per-value columns. This overrides the default order — Reset returns to it.<ng-container *ngIf="drillColumnOrderPreset.length"> By default the columns follow the design preset automatically: whichever of <em>{{ drillColumnOrderPresetLabel }}</em> the table has render in that order, any other column follows.</ng-container><ng-container *ngIf="!drillColumnOrderPreset.length"> The default is the measure order plus the formulas' "placed after" anchors.</ng-container></p>
        <small class="control-hint" *ngIf="!drillColumnOrderItems.length">Add at least one measure first.</small>

        <div class="dvm-formula-list" *ngIf="drillColumnOrderItems.length">
          <div class="dvm-formula-item" *ngFor="let col of drillColumnOrderItems; let i = index; let first = first; let last = last">
            <div class="dvm-formula-item-info">
              <strong><span class="wf-step-index">{{ i + 1 }}</span> {{ col.key }}</strong>
              <small>{{ col.kind === 'formula' ? 'formula column' : (col.split ? 'one column per pivot value' : 'single column') }}</small>
            </div>
            <div class="wf-step-actions">
              <button type="button" mat-icon-button [disabled]="first" (click)="moveDrillColumn(i, -1)" matTooltip="Move up" aria-label="Move column up"><mat-icon>arrow_upward</mat-icon></button>
              <button type="button" mat-icon-button [disabled]="last" (click)="moveDrillColumn(i, 1)" matTooltip="Move down" aria-label="Move column down"><mat-icon>arrow_downward</mat-icon></button>
            </div>
          </div>
        </div>

        <button type="button" mat-stroked-button class="add-step-button" *ngIf="drillColumnOrder.length" (click)="resetDrillColumnOrder()"><mat-icon>restart_alt</mat-icon> Reset to default order</button>
      </div>
    </div>

    <!-- Drill-Down Table: Format accordions (registry `sections`-gated). The
         Table & Matrix drill-down variant gets its own accordion in its block. -->
    <ng-container *ngIf="isDrillDownLayout" [ngTemplateOutlet]="chartFormatAccordion"></ng-container>

    <!-- Shared reference-list template: one card per typed reference, scoped to
         a specific WATERFALL bar (`step` set) or a single formula (`step`
         null) — used by the Input-control target formulas below AND by a
         chart's OWN formula field (KPI / horizontal bar), keyed by that
         chart's own componentId rather than a target id. Declared here, above
         every section that uses it, so it's in scope for all of them. -->
    <!-- Dataset picker + status — one definition, used by every chart section. -->
    <ng-template #datasetPicker>
      <div class="control-group" *ngIf="fieldEnabled('config', 'dataset')">
        <label>{{ fieldLabel('config', 'dataset', 'Dataset') }}</label>
        <ng-select
          #ssSharedDataset
          class="ss-select"
          [class.is-open]="!!ssSharedDataset.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="entityOptions"
          [loading]="entitiesLoading"
          [clearable]="true"
          [searchable]="true"
          placeholder="Pick the data table to render"
          [ngModel]="drillDataset || null"
          (ngModelChange)="onSelectedDatasetsChange($event ? [$event] : [])"
        ></ng-select>
        <small class="control-hint" *ngIf="datasetStatus.kind === 'hint'">{{ datasetStatus.text }}</small>
        <small class="control-warning" *ngIf="datasetStatus.kind === 'error'">
          {{ datasetStatus.text }}
          <button type="button" class="target-reference-add" *ngIf="datasetStatus.canRetry" (click)="reloadDatasets()">Retry</button>
        </small>
      </div>
    </ng-template>

    <ng-template #referenceList let-id="id" let-step="step">
      <div class="target-reference-list">
        <div class="target-reference-card" *ngFor="let ref of targetReferences(id, step)">
          <div class="target-reference-card-header">
            <span>Reference</span>
            <button type="button" class="target-reference-remove" (click)="removeTargetReference(id, ref)" title="Remove reference">&times;</button>
          </div>
          <div class="target-reference-grid">
            <div class="control-group">
              <label>Token</label>
              <input type="text" [(ngModel)]="ref.token" placeholder="e.g. DN Price" />
            </div>
            <div class="control-group">
              <label>Input title</label>
              <input type="text" [(ngModel)]="ref.inputTitle" placeholder="e.g. Price Inflation %" />
            </div>
            <div class="control-group">
              <label>Target label</label>
              <input type="text" [(ngModel)]="ref.targetLabel" placeholder="e.g. Product Margins" />
            </div>
            <div class="control-group">
              <label>Row label</label>
              <input type="text" [(ngModel)]="ref.rowLabel" placeholder="e.g. DN Price" />
            </div>
          </div>
          <small class="control-warning" *ngIf="isDuplicateReferenceToken(id, ref, step)">
            Another reference already uses token "{{ ref.token.trim() }}" — only one is sent. Give each a unique token.
          </small>
        </div>
        <button type="button" class="target-reference-add" (click)="addTargetReference(id, step)">+ Add reference</button>
      </div>
    </ng-template>

    <!-- Horizontal Bar Chart: one group-by field (Y-axis) + one formula-driven
         value (X-axis). Single dataset (first of "Datasets" above). -->
    <div class="wf-section" *ngIf="isHorizontalBarLayout">
      <header class="wf-section-header">
        <h3>{{ layoutSectionTitle('axes', 'Bar axes') }}</h3>
      </header>
      <small class="control-hint" *ngIf="layoutSectionHint('axes')">{{ layoutSectionHint('axes') }}</small>
      <ng-container *ngTemplateOutlet="datasetPicker"></ng-container>

      <div class="control-row">
        <div class="control-group wf-field-drop-zone" *ngIf="fieldEnabled('axes', 'groupByField')" (dragover)="allowFieldDrop($event)" (drop)="onHorizontalBarGroupFieldDrop($event)">
          <label>{{ fieldLabel('axes', 'groupByField', 'Y-axis — group by') }}</label>
          <ng-select
            #ssHbGroup
            class="ss-select"
            [class.is-open]="!!ssHbGroup.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="drillDatasetColumns"
            [clearable]="true"
            [searchable]="true"
            [loading]="isDrillDatasetColumnsLoading"
            [disabled]="!drillDataset"
            [placeholder]="drillColumnPlaceholder('dimension')"
            [ngModel]="hbGroupByField || null"
            (ngModelChange)="hbGroupByField = $event || ''"
          ></ng-select>
        </div>
        <div class="control-group" *ngIf="fieldEnabled('axes', 'groupByLabel')">
          <label>{{ fieldLabel('axes', 'groupByLabel', 'Y-axis display name') }}</label>
          <input type="text" [(ngModel)]="hbGroupByLabel" placeholder="e.g. Type Grouping" />
        </div>
      </div>

      <div class="control-row">
        <div class="control-group" *ngIf="fieldEnabled('axes', 'valueLabel')">
          <label>{{ fieldLabel('axes', 'valueLabel', 'X-axis value label') }}</label>
          <input type="text" [(ngModel)]="hbValueLabel" placeholder="Value" />
        </div>
        <div class="control-group control-group-full wf-field-drop-zone" *ngIf="fieldEnabled('axes', 'valueFormula')" (dragover)="allowFieldDrop($event)" (drop)="onHorizontalBarValueFieldDrop($event)">
          <label>{{ fieldLabel('axes', 'valueFormula', 'X-axis value formula') }}</label>
          <input type="text" [(ngModel)]="hbValueFormula" placeholder="e.g. [price] + [sum_of_cost_2026]" />
          <small class="control-hint">Used for any group with no override below. Leave blank if every group has one.</small>
        </div>
      </div>

      <!-- Per-group formula overrides: some groups genuinely compute their value
           differently (e.g. a "Base Options" row folding in one adjustment while
           "Additional Options" folds in a different one) rather than one formula
           applying uniformly to every group. A group not listed here falls back
           to the shared formula above. -->
      <div class="control-row">
        <div class="control-group control-group-full">
          <label>Per-group formula overrides (optional)</label>
          <small class="control-hint">
            One formula per group value (matched against the Y-axis field above). A group listed here ignores the shared formula.
          </small>
          <div class="target-reference-row" *ngFor="let entry of hbGroupFormulas">
            <input type="text" [(ngModel)]="entry.group" placeholder="Group value, e.g. Base Options" />
            <input type="text" [(ngModel)]="entry.formula" placeholder="Formula for this group" />
            <button type="button" class="target-reference-remove" (click)="removeHbGroupFormula(entry)">Remove</button>
          </div>
          <button type="button" class="target-reference-add" (click)="addHbGroupFormula()">+ Add group formula</button>
        </div>
      </div>

      <!-- References: when this formula's [Token] isn't a plain dataset column
           but another input's live/recomputed output, type in where it comes
           from so the backend resolves the CURRENT value instead of a stale
           one. Keyed by THIS chart's own componentId. -->
      <ng-container *ngIf="formulaReferencesEnabledFor('HORIZONTAL_BAR_CHART')">
        <ng-container *ngTemplateOutlet="referenceList; context: { id: componentId }"></ng-container>
      </ng-container>

      <!-- Format accordions (registry `sections`-gated; shell formatControls) -->
      <ng-container [ngTemplateOutlet]="chartFormatAccordion"></ng-container>
    </div>
    <!-- KPI card: single dataset, one column aggregated (sum / distinct / …) to
         a single headline value. -->
    <div class="wf-section" *ngIf="isKpiLayout">
      <header class="wf-section-header">
        <h3>{{ layoutSectionTitle('kpi', 'KPI card') }}</h3>
      </header>
      <small class="control-hint" *ngIf="layoutSectionHint('kpi')">{{ layoutSectionHint('kpi') }}</small>
      <ng-container *ngTemplateOutlet="datasetPicker"></ng-container>

      <div class="control-row">
        <div class="control-group wf-field-drop-zone" (dragover)="allowFieldDrop($event)" (drop)="onKpiFieldDrop($event)" title="Drag a column here">
          <label>{{ fieldLabel('kpi', 'field', 'Column') }}</label>
          <ng-select
            #ssKpiField
            class="ss-select"
            [class.is-open]="!!ssKpiField.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="drillDatasetColumns"
            [clearable]="true"
            [searchable]="true"
            [loading]="isDrillDatasetColumnsLoading"
            [disabled]="!drillDataset"
            [placeholder]="drillColumnPlaceholder('measure')"
            [ngModel]="kpiField || null"
            (ngModelChange)="kpiField = $event || ''"
          ></ng-select>
        </div>
        <div class="control-group">
          <label>{{ fieldLabel('kpi', 'aggregation', 'Aggregation') }}</label>
          <ng-select
            #ssKpiAgg
            class="ss-select"
            [class.is-open]="!!ssKpiAgg.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="kpiAggregations"
            bindLabel="label"
            bindValue="value"
            [clearable]="true"
            [ngModel]="kpiAggregation"
            (ngModelChange)="kpiAggregation = $event ?? ''"
          ></ng-select>
        </div>
      </div>
      <div class="control-row" *ngIf="fieldEnabled('kpi', 'formula')">
        <div class="control-group control-group-full wf-field-drop-zone" (dragover)="allowFieldDrop($event)" (drop)="onKpiFormulaFieldDrop($event)" title="Drag a column here">
          <label>{{ fieldLabel('kpi', 'formula', 'Formula (optional)') }}</label>
          <input type="text" [(ngModel)]="kpiFormula" placeholder="e.g. [price] * [qty] — overrides Column + Aggregation when set" />
        </div>
      </div>

      <!-- References: when the formula's [Token] isn't a plain dataset column
           but another input's live/recomputed output, type in where it comes
           from so the backend resolves the CURRENT value instead of a stale
           one. Keyed by THIS KPI's own componentId. -->
      <ng-container *ngIf="fieldEnabled('kpi', 'formula') && formulaReferencesEnabledFor('KPI')">
        <ng-container *ngTemplateOutlet="referenceList; context: { id: componentId }"></ng-container>
      </ng-container>

      <!-- Format accordions (registry `sections`-gated; shell formatControls) -->
      <ng-container [ngTemplateOutlet]="chartFormatAccordion"></ng-container>
    </div>

    <!-- Bar & Column (BAR): ONE builder → the whole family. Orientation toggle
         (Horizontal/Vertical) + variant picker (Simple/Grouped/Stacked/100%/
         Clustered) decide how the mapped category + measures render. -->
    <div class="wf-section wf-section-plain" *ngIf="isBarLayout">
      <ng-container *ngTemplateOutlet="datasetPicker"></ng-container>

      <!-- Family card — mirrors the "Chart configuration" card style (icon + divider
           heading, padded white card) so the two sections read consistently. -->
      <div class="bridge-chart-settings bar-family-card">
        <div class="bridge-settings-heading"><mat-icon>bar_chart</mat-icon><span>{{ layoutSectionTitle('bar', 'Bar & column') }}</span></div>
        <small class="control-hint" *ngIf="layoutSectionHint('bar')">{{ layoutSectionHint('bar') }}</small>

        <!-- Orientation toggle -->
        <div class="control-group bar-orientation-field" *ngIf="fieldEnabled('bar', 'orientation')">
          <label>{{ fieldLabel('bar', 'orientation', 'Orientation') }}</label>
          <div class="wf-step-mode-toggle" role="group" aria-label="Bar orientation">
            <button type="button" [class.is-active]="barOrientation === 'vertical'" (click)="barOrientation = 'vertical'; onBarChange()">
              <mat-icon>bar_chart</mat-icon> Vertical
            </button>
            <button type="button" [class.is-active]="barOrientation === 'horizontal'" (click)="barOrientation = 'horizontal'; onBarChange()">
              <mat-icon>align_horizontal_left</mat-icon> Horizontal
            </button>
          </div>
        </div>

        <!-- Variant — its selection decides what the builder needs (Simple = no
             split; Grouped/Clustered/Stacked/100% = a required Split column). -->
        <div class="control-group" *ngIf="fieldEnabled('bar', 'variant')">
          <label>{{ fieldLabel('bar', 'variant', 'Variant') }}</label>
          <ng-select
            #ssBarVariant
            class="ss-select"
            [class.is-open]="!!ssBarVariant.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="barVariants"
            bindLabel="label"
            bindValue="value"
            [clearable]="true"
            [ngModel]="barVariant"
            (ngModelChange)="barVariant = $event ?? ''; onBarChange()"
          ></ng-select>
          <small class="control-hint wf-variant-hint" *ngIf="barVariantHint">{{ barVariantHint }}</small>
        </div>
      </div>

      <!-- ══ Chart Data accordion — axis / measure / series mapping ══ -->
      <div class="bar-options-accordion">
        <div class="dvm-format-group" [class.is-open]="barDataAccordionOpen">
          <button type="button" class="dvm-format-group-head" (click)="barDataAccordionOpen = !barDataAccordionOpen">
            <span>Chart Data</span>
            <mat-icon>{{ barDataAccordionOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
          <div class="dvm-format-group-body" *ngIf="barDataAccordionOpen">

      <div class="control-row">
        <!-- Category = the group axis (X when vertical, Y when horizontal). -->
        <div class="control-group wf-field-drop-zone" *ngIf="fieldEnabled('bar', 'category')" (dragover)="allowFieldDrop($event)" (drop)="onBarCategoryFieldDrop($event)" title="Drag a column here">
          <label>{{ barCategoryAxisLabel }} <span class="wf-axis-hint">— group by</span></label>
          <ng-select
            #ssBarCategory
            class="ss-select"
            [class.is-open]="!!ssBarCategory.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="barColumns"
            [clearable]="true"
            [searchable]="true"
            [loading]="isDrillDatasetColumnsLoading"
            [disabled]="!drillDataset"
            [placeholder]="barColumnPlaceholder()"
            [ngModel]="barCategory || null"
            (ngModelChange)="barCategory = $event || ''; onBarChange()"
          ></ng-select>
        </div>
        <!-- Hierarchical Category Axis — optional OUTER group (parent → child). -->
        <div class="control-group wf-field-drop-zone" (dragover)="allowFieldDrop($event)" (drop)="onBarParentFieldDrop($event)" title="Drag a column here">
          <label>Group by (parent) <span class="wf-axis-hint">(optional — hierarchical)</span></label>
          <ng-select
            #ssBarParent
            class="ss-select"
            [class.is-open]="!!ssBarParent.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="barColumns"
            [clearable]="true"
            [searchable]="true"
            [loading]="isDrillDatasetColumnsLoading"
            [disabled]="!drillDataset"
            [placeholder]="barColumnPlaceholder()"
            [ngModel]="barCategoryParent || null"
            (ngModelChange)="barCategoryParent = $event || ''; onBarChange()"
          ></ng-select>
        </div>
      </div>

      <div class="control-row">
        <!-- Series = split each group into sub-bars → legend. REQUIRED for the
             split-based variants (grouped/clustered/stacked/100%); hidden for Simple. -->
        <div class="control-group wf-field-drop-zone" [class.is-required-empty]="barSeriesRequired && !barSeries" *ngIf="fieldEnabled('bar', 'series') && barVariantUsesSeries" (dragover)="allowFieldDrop($event)" (drop)="onBarSeriesFieldDrop($event)" title="Drag a column here">
          <label>Split / Legend <span class="wf-axis-hint">{{ barSeriesRequired ? '(required)' : '(optional)' }}</span></label>
          <ng-select
            #ssBarSeries
            class="ss-select"
            [class.is-open]="!!ssBarSeries.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="barColumns"
            [clearable]="!barSeriesRequired"
            [searchable]="true"
            [loading]="isDrillDatasetColumnsLoading"
            [disabled]="!drillDataset"
            [placeholder]="barSeriesRequired ? 'Pick a column to split by' : barColumnPlaceholder()"
            [ngModel]="barSeries || null"
            (ngModelChange)="barSeries = $event || ''; onBarChange()"
          ></ng-select>
          <label *ngIf="barSeries">Series order (optional)</label>
          <input
            *ngIf="barSeries"
            type="text"
            class="ss-input"
            [ngModel]="barSeriesOrder"
            (ngModelChange)="barSeriesOrder = $event; onBarChange()"
            placeholder="e.g. K, Q, G"
          />
          <small class="control-hint" *ngIf="barSeries">Comma-separated, in the order you want the series to appear. Leave blank to keep the default order.</small>
          <!-- <small class="control-warning" *ngIf="barSeriesRequired && !barSeries">This variant needs a Split column to render as {{ barVariant }}.</small> -->
          <small class="control-hint" *ngIf="barSeriesComesFromMeasures">Leave empty to use one series per measure below — that way a series can be a formula (add it under Measures).</small>
        </div>
      </div>

      <!-- Secondary axes — a bar chart's second VALUE axis (dual). BOTH pickers
           (Secondary X + Secondary Y) are ALWAYS shown for every variant and
           orientation; the active one folds into capabilities via
           barActiveSecondaryField (Sec. Y when Vertical, Sec. X when Horizontal). -->
      <div class="control-row">
        <div class="control-group wf-field-drop-zone" (dragover)="allowFieldDrop($event)" (drop)="onBarSecondaryAxisFieldDrop($event, 'x')" title="Drag a column here">
          <label>Secondary X-axis <span class="wf-axis-hint">(values)</span></label>
          <ng-select
            #ssBarSecX
            class="ss-select"
            [class.is-open]="!!ssBarSecX.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="barColumns"
            [clearable]="true"
            [searchable]="true"
            [loading]="isDrillDatasetColumnsLoading"
            [disabled]="!drillDataset"
            [placeholder]="barColumnPlaceholder()"
            [ngModel]="barSecondaryXField || null"
            (ngModelChange)="barSecondaryXField = $event || ''; onBarChange()"
          ></ng-select>
        </div>
        <div class="control-group wf-field-drop-zone" (dragover)="allowFieldDrop($event)" (drop)="onBarSecondaryAxisFieldDrop($event, 'y')" title="Drag a column here">
          <label>Secondary Y-axis <span class="wf-axis-hint">(values)</span></label>
          <ng-select
            #ssBarSecY
            class="ss-select"
            [class.is-open]="!!ssBarSecY.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="barColumns"
            [clearable]="true"
            [searchable]="true"
            [loading]="isDrillDatasetColumnsLoading"
            [disabled]="!drillDataset"
            [placeholder]="barColumnPlaceholder()"
            [ngModel]="barSecondaryYField || null"
            (ngModelChange)="barSecondaryYField = $event || ''; onBarChange()"
          ></ng-select>
        </div>
      </div>

      <!-- Per-axis label FONT WEIGHTS — highlight an axis's tick labels + title
           independently. '' = default (falls back to the Format popup's
           tile-wide "Axis label weight"). CONFIG-DRIVEN via layoutSteps entries
           in bar-column.json; options from the shared shell `fontWeights` list.
           X/Y mean the RENDERED axes (orientation decides which is category);
           the Secondary select covers the dual value axis on either side and
           only shows once one is mapped. -->
      <div class="control-row" *ngIf="fieldEnabled('bar', 'xAxisFontWeight') || fieldEnabled('bar', 'yAxisFontWeight')">
        <div class="control-group" *ngIf="fieldEnabled('bar', 'xAxisFontWeight')">
          <label>{{ fieldLabel('bar', 'xAxisFontWeight', 'X axis label weight') }}</label>
          <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="fontWeightOptions" bindLabel="label" bindValue="value" [clearable]="true" [searchable]="false" [ngModel]="barXAxisFontWeight" (ngModelChange)="barXAxisFontWeight = $event ?? ''; onBarChange()"></ng-select>
        </div>
        <div class="control-group" *ngIf="fieldEnabled('bar', 'yAxisFontWeight')">
          <label>{{ fieldLabel('bar', 'yAxisFontWeight', 'Y axis label weight') }}</label>
          <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="fontWeightOptions" bindLabel="label" bindValue="value" [clearable]="true" [searchable]="false" [ngModel]="barYAxisFontWeight" (ngModelChange)="barYAxisFontWeight = $event ?? ''; onBarChange()"></ng-select>
        </div>
      </div>
      <div class="control-row" *ngIf="barHasSecondaryAxis && fieldEnabled('bar', 'axis2FontWeight')">
        <div class="control-group">
          <label>{{ fieldLabel('bar', 'axis2FontWeight', 'Secondary axis label weight') }}</label>
          <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="fontWeightOptions" bindLabel="label" bindValue="value" [clearable]="true" [searchable]="false" [ngModel]="barSecondaryAxisFontWeight" (ngModelChange)="barSecondaryAxisFontWeight = $event ?? ''; onBarChange()"></ng-select>
        </div>
      </div>

      <!-- Measures = the value axis (Y when vertical, X when horizontal). Each row
           is one measure: column + draw-as + primary/secondary axis (+ reorder). -->
      <div class="wf-subsection-header">
        <span>{{ barValueAxisLabel }} <span class="wf-axis-hint">— values</span> <span class="wf-step-count">{{ barMeasures.length }}</span></span>
        <!-- Add measure only where extra measures actually render (Simple Bar,
             Comparison). Series-split variants use ONE measure, so it's hidden. -->
        <button type="button" class="add-step-button" mat-stroked-button *ngIf="barAllowsMultipleMeasures" (click)="addBarMeasure()"><mat-icon>add</mat-icon> Add measure</button>
      </div>
      <small class="control-hint" *ngIf="barVariantUsesSeries && barSeries">Series split active — the first measure supplies the value.</small>
      <small class="control-hint" *ngIf="barSeriesComesFromMeasures">No Split column picked — each measure below renders as its own series. Use the Formula Builder to add a computed column, then select it here.</small>

      <div class="wf-field-drop-zone bar-measures-zone" (dragover)="allowFieldDrop($event)" (drop)="onBarMeasureFieldDrop($event)">
        <div class="wf-empty" *ngIf="!barMeasures.length">No measures yet — add one, or drag a column here.</div>

        <div class="bar-measure-row" *ngFor="let measure of barMeasures; let i = index; let first = first; let last = last">
          <div class="control-row">
            <div class="control-group wf-field-drop-zone" (dragover)="allowFieldDrop($event)" (drop)="onBarMeasureFieldDrop($event, measure)" title="Drag a column here">
              <label>Column</label>
              <ng-select
                class="ss-select"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="barColumns"
                [clearable]="true"
                [searchable]="true"
                [loading]="isDrillDatasetColumnsLoading"
                [disabled]="!drillDataset"
                [placeholder]="barColumnPlaceholder()"
                [ngModel]="measure.field || null"
                (ngModelChange)="measure.field = $event || ''; onBarMeasureChange()"
              ></ng-select>
            </div>
            <div class="control-group">
              <label>Label</label>
              <input type="text" [ngModel]="measure.label" (ngModelChange)="measure.label = $event; onBarMeasureChange()" [placeholder]="measure.field || 'Measure'" />
            </div>
          </div>
          <div class="control-row">
            <!-- Draw-as (Bar/Line/Area) — combos. Hidden for series-split variants. -->
            <div class="control-group" *ngIf="fieldEnabled('bar', 'renderAs') && !(barVariantUsesSeries && barSeries)">
              <label>{{ fieldLabel('bar', 'renderAs', 'Draw as') }}</label>
              <ng-select
                class="ss-select"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="barRenderTypes"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [ngModel]="measure.renderAs"
                (ngModelChange)="measure.renderAs = $event ?? ''; onBarMeasureChange()"
              ></ng-select>
            </div>
            <!-- Axis (primary/secondary) — dual axis. Hidden for series-split variants. -->
            <div class="control-group" *ngIf="fieldEnabled('bar', 'axis') && !(barVariantUsesSeries && barSeries)">
              <label>{{ fieldLabel('bar', 'axis', 'Axis') }}</label>
              <ng-select
                class="ss-select"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="barAxisOptions"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [ngModel]="measure.axis"
                (ngModelChange)="measure.axis = $event ?? ''; onBarMeasureChange()"
              ></ng-select>
            </div>
            <!-- Reorder / remove only when multiple measures apply AND more than
                 one exists (a single required measure has nothing to reorder). -->
            <div class="control-group bar-measure-actions" *ngIf="barAllowsMultipleMeasures && barMeasures.length > 1">
              <button type="button" mat-icon-button (click)="moveBarMeasureUp(i)" [disabled]="first" matTooltip="Move up"><mat-icon>arrow_upward</mat-icon></button>
              <button type="button" mat-icon-button (click)="moveBarMeasureDown(i)" [disabled]="last" matTooltip="Move down"><mat-icon>arrow_downward</mat-icon></button>
              <button type="button" mat-icon-button class="row-remove-button" (click)="removeBarMeasure(measure)" matTooltip="Remove measure"><mat-icon>close</mat-icon></button>
            </div>
          </div>
        </div>
      </div>

      <!-- Reference / target lines — drawn across the value axis. -->
      <div class="wf-subsection-header">
        <span>Reference lines <span class="wf-step-count">{{ barReferenceLines.length }}</span></span>
        <button type="button" class="add-step-button" mat-stroked-button (click)="addBarReferenceLine()"><mat-icon>add</mat-icon> Add line</button>
      </div>
      <div class="bar-refline-list" *ngIf="barReferenceLines.length">
        <div class="control-row bar-refline-row" *ngFor="let line of barReferenceLines">
          <div class="control-group">
            <label>Value</label>
            <input type="number" [ngModel]="line.value" (ngModelChange)="line.value = $event; onBarReferenceLineChange()" placeholder="e.g. 100" />
          </div>
          <div class="control-group">
            <label>Label</label>
            <input type="text" [ngModel]="line.label" (ngModelChange)="line.label = $event; onBarReferenceLineChange()" placeholder="Target" />
          </div>
          <div class="control-group bar-refline-color">
            <label>Color</label>
            <input type="color" [ngModel]="line.color" (ngModelChange)="line.color = $event; onBarReferenceLineChange()" />
          </div>
          <button type="button" mat-icon-button class="bar-refline-remove row-remove-button" (click)="removeBarReferenceLine(line.id)" matTooltip="Remove line"><mat-icon>close</mat-icon></button>
        </div>
      </div>

      <!-- Value formula — computed by the BACKEND, so unlike the Formula Builder
           below it can reference a live input value via [Token] + References. -->
      <div class="control-row" *ngIf="fieldEnabled('bar', 'valueFormula')">
        <div class="control-group control-group-full wf-field-drop-zone" (dragover)="allowFieldDrop($event)" (drop)="onBarValueFormulaFieldDrop($event)">
          <label>{{ fieldLabel('bar', 'valueFormula', 'Value formula (computed)') }}</label>
          <input type="text" [(ngModel)]="barValueFormula" placeholder="e.g. (AVG(base_price)*(1+[Price Inflation %]/100))" />
          <small class="control-hint">
            Computed by the backend, so it can use a live input value via [Token] (add it under References below).
            Leave blank to just aggregate the Measures above. Used for any group with no override below.
          </small>
        </div>
      </div>

      <div class="control-row" *ngIf="fieldEnabled('bar', 'valueFormula') && barUsesValueFormula">
        <div class="control-group">
          <label>Value label</label>
          <input type="text" [(ngModel)]="barValueLabel" placeholder="Value" />
        </div>
      </div>

      <div class="control-row" *ngIf="fieldEnabled('bar', 'valueFormula')">
        <div class="control-group control-group-full">
          <label>Per-group formula overrides (optional)</label>
          <small class="control-hint">
            One formula per category value (matched against the {{ barCategoryAxisLabel }} field above). A group listed here ignores the shared formula.
          </small>
          <div class="target-reference-row" *ngFor="let entry of barGroupFormulas">
            <input type="text" [(ngModel)]="entry.group" placeholder="Group value, e.g. Base Options" />
            <input type="text" [(ngModel)]="entry.formula" placeholder="Formula for this group" />
            <button type="button" class="target-reference-remove" (click)="removeBarGroupFormula(entry)">Remove</button>
          </div>
          <button type="button" class="target-reference-add" (click)="addBarGroupFormula()">+ Add group formula</button>
        </div>
      </div>

      <!-- Resolves each [Token] above to another tile's current value. -->
      <ng-container *ngIf="fieldEnabled('bar', 'valueFormula') && formulaReferencesEnabledFor('BAR')">
        <ng-container *ngTemplateOutlet="referenceList; context: { id: componentId }"></ng-container>
      </ng-container>

      <!-- Formula Builder — row-level COMPUTED columns from the dataset's columns.
           A computed column (e.g. "Margin = ([Profit] / [Revenue]) * 100") becomes
           a mappable column in Category / Measure / Split above. Fully config-driven
           (options.formulaBuilder): text, templates, validation, and the enabled
           flag all come from JSON. -->
      <div class="dvm-formula-builder" *ngIf="formulaBuilderEnabled">
        <button type="button" class="dvm-formula-head" (click)="barFormulaAccordionOpen = !barFormulaAccordionOpen">
          <span><mat-icon class="dvm-formula-head-icon">functions</mat-icon> {{ formulaBuilderConfig.title || 'Formula Builder' }} <span class="wf-step-count">{{ barComputedColumns.length }}</span></span>
          <mat-icon>{{ barFormulaAccordionOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
        </button>

        <div class="dvm-formula-body" *ngIf="barFormulaAccordionOpen">
          <p class="dvm-formula-hint" *ngIf="formulaBuilderConfig.hint">{{ formulaBuilderConfig.hint }}</p>

          <!-- Quick templates — click to fill the form below. -->
          <div class="dvm-formula-templates" *ngIf="barFormulaTemplates.length">
            <small class="control-hint">{{ formulaBuilderConfig.templatesLabel || 'Quick templates:' }}</small>
            <button
              type="button"
              class="dvm-formula-chip"
              *ngFor="let tpl of barFormulaTemplates"
              [title]="tpl.description"
              (click)="applyBarFormulaTemplate(tpl)"
            >{{ tpl.label }}</button>
          </div>

          <!-- Existing computed columns. -->
          <div class="dvm-formula-list" *ngIf="barComputedColumns.length">
            <div class="dvm-formula-item" *ngFor="let col of barComputedColumns">
              <div class="dvm-formula-item-info">
                <strong>{{ col.name }}</strong>
                <small>= {{ col.expression }}</small>
              </div>
              <button type="button" mat-icon-button class="bar-refline-remove row-remove-button" (click)="removeBarComputedColumn(col.name)" matTooltip="Remove column"><mat-icon>close</mat-icon></button>
            </div>
          </div>

          <!-- Add a new computed column. -->
          <div class="dvm-formula-add">
            <input type="text" [(ngModel)]="newBarColumnName" [placeholder]="formulaBuilderConfig.namePlaceholder || 'Column name'" />
            <input type="text" [(ngModel)]="newBarColumnExpression" [placeholder]="formulaBuilderConfig.expressionPlaceholder || 'Expression'" />
            <button type="button" class="add-step-button" mat-stroked-button (click)="addBarComputedColumn()"><mat-icon>add</mat-icon> {{ formulaBuilderConfig.addLabel || 'Add column' }}</button>
          </div>
          <small class="control-warning" *ngIf="newBarColumnError">{{ newBarColumnError }}</small>
        </div>
      </div>

          </div><!-- /Chart Data body -->
        </div><!-- /Chart Data group -->
      </div><!-- /Chart Data accordion -->

      <!-- Bar chart options — Title / Axis / Legend / Labels / Tooltip / Formatting /
           Interaction / Advanced accordions (shared template below). -->
      <ng-container [ngTemplateOutlet]="chartFormatAccordion"></ng-container>
    </div>

    <!-- ══════════════════ Line & Area builder ══════════════════
         Mirrors the Bar builder: a family card (variant picker), a Chart-Data
         accordion (X / Series / Secondary-Y pickers + N measures + reference
         lines), then the config-driven Format accordions. Renders client-side. -->
    <div class="wf-section wf-section-plain" *ngIf="isLineAreaLayout">
      <small class="control-warning" *ngIf="drillColumnsErrorMessage">{{ drillColumnsErrorMessage }} (see console for details)</small>

      <div class="bridge-chart-settings bar-family-card">
        <div class="bridge-settings-heading"><mat-icon>show_chart</mat-icon><span>{{ layoutSectionTitle('lineArea', 'Line & area') }}</span></div>
        <small class="control-hint" *ngIf="layoutSectionHint('lineArea')">{{ layoutSectionHint('lineArea') }}</small>
        <ng-container *ngTemplateOutlet="datasetPicker"></ng-container>

        <div class="control-group">
          <label>Variant</label>
          <ng-select
            #ssLaVariant
            class="ss-select"
            [class.is-open]="!!ssLaVariant.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="lineAreaVariants"
            bindLabel="label"
            bindValue="value"
            groupBy="group"
            [clearable]="true"
            [ngModel]="laVariant"
            (ngModelChange)="laVariant = $event ?? ''; onLineAreaChange()"
          ></ng-select>
          <small class="control-hint wf-variant-hint" *ngIf="laVariantHint">{{ laVariantHint }}</small>
        </div>
      </div>

      <!-- ══ Chart Data accordion ══ -->
      <div class="bar-options-accordion">
        <div class="dvm-format-group is-open">
          <div class="dvm-format-group-head" style="cursor: default;">
            <span>Chart Data</span>
          </div>
          <div class="dvm-format-group-body">

            <div class="control-group control-group-full">
              <label>Alternate data API <span class="wf-axis-hint">(optional)</span></label>
              <input type="text" [ngModel]="laApiUrl" (ngModelChange)="laApiUrl = $event; onLineAreaChange()"
                placeholder="Leave blank to use the dynamic dataset API" />
              <small class="control-hint">When set, this API supplies the line-chart rows instead of the dynamic API.</small>
            </div>
            <div class="control-group control-group-full" *ngIf="laApiUrl.trim()">
              <label>Alternate API payload</label>
              <textarea rows="6" [ngModel]="laApiPayload" (ngModelChange)="laApiPayload = $event; onLineAreaChange()"
                placeholder='{"requestData": {"uuid": "null"}, "endpoints": []}'></textarea>
              <small class="control-hint">Enter the complete POST body. The selected X-axis column remains the response category key.</small>
            </div>
            <div class="control-group control-group-full" *ngIf="laApiUrl.trim()">
              <label>Response rows path <span class="wf-axis-hint">(optional)</span></label>
              <input type="text" [ngModel]="laApiResponsePath" (ngModelChange)="laApiResponsePath = $event; onLineAreaChange()"
                placeholder="e.g. responseList.0.data" />
              <small class="control-hint">Use this only when rows are nested at a custom path; otherwise common data/rows/results envelopes are detected automatically.</small>
            </div>

            <div class="control-row">
              <!-- Category = the X axis. -->
              <div class="control-group wf-field-drop-zone" (dragover)="allowFieldDrop($event)" (drop)="onLaCategoryFieldDrop($event)" title="Drag a column here">
                <label>X axis <span class="wf-axis-hint">— group by</span></label>
                <ng-select
                  #ssLaCategory class="ss-select" [class.is-open]="!!ssLaCategory.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
                  [items]="laColumnsForDataset(laCategoryDataset || drillDataset)" [clearable]="true" [searchable]="true" [loading]="isDatasetColumnsLoading(laCategoryDataset || drillDataset)" [disabled]="!(laCategoryDataset || drillDataset)"
                  [placeholder]="barColumnPlaceholder()" [ngModel]="laCategory || null" (ngModelChange)="laCategory = $event || ''; onLineAreaChange()"
                ></ng-select>
                <label>Dataset</label>
                <ng-select class="ss-select" [items]="entityOptions" [clearable]="false" [searchable]="true"
                  [ngModel]="laCategoryDataset || drillDataset || null" (ngModelChange)="onLaAxisDatasetChange('category', $event)"></ng-select>
              </div>
              <!-- Series split — REQUIRED for Multi-Line / Stacked Area / 100%. -->
              <div class="control-group wf-field-drop-zone" [class.is-required-empty]="laSeriesRequired && !laSeries" *ngIf="laVariantUsesSeries" (dragover)="allowFieldDrop($event)" (drop)="onLaSeriesFieldDrop($event)" title="Drag a column here">
                <label>Split / Legend <span class="wf-axis-hint">{{ laSeriesRequired ? '(required)' : '(optional)' }}</span></label>
                <ng-select
                  #ssLaSeries class="ss-select" [class.is-open]="!!ssLaSeries.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
                  [items]="laColumnsForDataset(laSeriesDataset || drillDataset)" [clearable]="!laSeriesRequired" [searchable]="true" [loading]="isDatasetColumnsLoading(laSeriesDataset || drillDataset)" [disabled]="!(laSeriesDataset || drillDataset)"
                  [placeholder]="laSeriesRequired ? 'Pick a column to split by' : barColumnPlaceholder()" [ngModel]="laSeries || null" (ngModelChange)="laSeries = $event || ''; onLineAreaChange()"
                ></ng-select>
                <label *ngIf="laSeries">Series order (optional)</label>
                <input
                  *ngIf="laSeries"
                  type="text"
                  class="ss-input"
                  [ngModel]="laSeriesOrder"
                  (ngModelChange)="laSeriesOrder = $event; onLineAreaChange()"
                  placeholder="e.g. K, Q, G"
                />
                <small class="control-hint" *ngIf="laSeries">Comma-separated, in the order you want the series to appear. Leave blank to keep the default order.</small>
                <label>Dataset</label>
                <ng-select class="ss-select" [items]="entityOptions" [clearable]="false" [searchable]="true"
                  [ngModel]="laSeriesDataset || drillDataset || null" (ngModelChange)="onLaAxisDatasetChange('series', $event)"></ng-select>
              </div>
            </div>

            <!-- Secondary axes — ALWAYS shown. Secondary X = a second category
                 column on a top X-axis (its own series); Secondary Y = dual value axis. -->
            <div class="control-row">
              <div class="control-group wf-field-drop-zone" (dragover)="allowFieldDrop($event)" (drop)="onLaSecondaryXFieldDrop($event)" title="Drag a column here">
                <label>Secondary X-axis <span class="wf-axis-hint">(category)</span></label>
                <ng-select
                  #ssLaSecX class="ss-select" [class.is-open]="!!ssLaSecX.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
                  [items]="laColumnsForDataset(laSecondaryXDataset || drillDataset)" [clearable]="true" [searchable]="true" [loading]="isDatasetColumnsLoading(laSecondaryXDataset || drillDataset)" [disabled]="!(laSecondaryXDataset || drillDataset)"
                  [placeholder]="barColumnPlaceholder()" [ngModel]="laSecondaryXField || null" (ngModelChange)="laSecondaryXField = $event || ''; onLineAreaChange()"
                ></ng-select>
                <label>Dataset</label>
                <ng-select class="ss-select" [items]="entityOptions" [clearable]="false" [searchable]="true"
                  [ngModel]="laSecondaryXDataset || drillDataset || null" (ngModelChange)="onLaAxisDatasetChange('secondaryX', $event)"></ng-select>
              </div>
              <div class="control-group wf-field-drop-zone" (dragover)="allowFieldDrop($event)" (drop)="onLaSecondaryYFieldDrop($event)" title="Drag a column here">
                <label>Secondary Y-axis <span class="wf-axis-hint">(values — dual)</span></label>
                <ng-select
                  #ssLaSecY class="ss-select" [class.is-open]="!!ssLaSecY.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
                  [items]="laColumnsForDataset(laSecondaryYDataset || drillDataset)" [clearable]="true" [searchable]="true" [loading]="isDatasetColumnsLoading(laSecondaryYDataset || drillDataset)" [disabled]="!(laSecondaryYDataset || drillDataset)"
                  [placeholder]="barColumnPlaceholder()" [ngModel]="laSecondaryYField || null" (ngModelChange)="laSecondaryYField = $event || ''; onLineAreaChange()"
                ></ng-select>
                <label>Dataset</label>
                <ng-select class="ss-select" [items]="entityOptions" [clearable]="false" [searchable]="true"
                  [ngModel]="laSecondaryYDataset || drillDataset || null" (ngModelChange)="onLaAxisDatasetChange('secondaryY', $event)"></ng-select>
              </div>
            </div>
            <!-- Value plotted against the secondary X axis (defaults to the first measure). -->
            <div class="control-row" *ngIf="laSecondaryXField">
              <div class="control-group">
                <label>Secondary X value <span class="wf-axis-hint">(defaults to first measure)</span></label>
                <ng-select
                  #ssLaSecXVal class="ss-select" [class.is-open]="!!ssLaSecXVal.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
                  [items]="laColumnsForDataset(laSecondaryXValueDataset || drillDataset)" [clearable]="true" [searchable]="true" [loading]="isDatasetColumnsLoading(laSecondaryXValueDataset || drillDataset)" [disabled]="!(laSecondaryXValueDataset || drillDataset)"
                  [placeholder]="barColumnPlaceholder()" [ngModel]="laSecondaryXValueField || null" (ngModelChange)="laSecondaryXValueField = $event || ''; onLineAreaChange()"
                ></ng-select>
                <label>Dataset</label>
                <ng-select class="ss-select" [items]="entityOptions" [clearable]="false" [searchable]="true"
                  [ngModel]="laSecondaryXValueDataset || drillDataset || null" (ngModelChange)="onLaAxisDatasetChange('secondaryXValue', $event)"></ng-select>
              </div>
              <div class="control-group">
                <label>Secondary X position</label>
                <ng-select
                  class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="laSecondaryXPositions" bindLabel="label" bindValue="value"
                  [clearable]="true" [searchable]="false" [ngModel]="laSecondaryXPosition" (ngModelChange)="laSecondaryXPosition = $event ?? ''; onLineAreaChange()"
                ></ng-select>
              </div>
            </div>

            <!-- Axis labels — override the auto-derived axis titles (column /
                 measure names). Blank = auto. The Format Axis accordion's
                 titles still win when set there. CONFIG-DRIVEN: each input
                 exists/relabels via its layoutSteps entry in line-area.json
                 (fields categoryLabel / yAxisLabel / xAxisLabel2 / yAxisLabel2). -->
            <div class="control-row" *ngIf="fieldEnabled('lineArea', 'categoryLabel') || fieldEnabled('lineArea', 'yAxisLabel')">
              <div class="control-group" *ngIf="fieldEnabled('lineArea', 'categoryLabel')">
                <label>{{ fieldLabel('lineArea', 'categoryLabel', 'X axis label') }}</label>
                <input type="text" [ngModel]="laCategoryLabel" (ngModelChange)="laCategoryLabel = $event; onLineAreaChange()" [placeholder]="laCategory || 'Auto'" />
              </div>
              <div class="control-group" *ngIf="fieldEnabled('lineArea', 'yAxisLabel')">
                <label>{{ fieldLabel('lineArea', 'yAxisLabel', 'Y axis label') }}</label>
                <input type="text" [ngModel]="laYAxisLabel" (ngModelChange)="laYAxisLabel = $event; onLineAreaChange()" placeholder="Auto — first measure" />
              </div>
            </div>
            <div class="control-row" *ngIf="(laSecondaryXField && fieldEnabled('lineArea', 'xAxisLabel2')) || (laHasSecondaryY && fieldEnabled('lineArea', 'yAxisLabel2'))">
              <div class="control-group" *ngIf="laSecondaryXField && fieldEnabled('lineArea', 'xAxisLabel2')">
                <label>{{ fieldLabel('lineArea', 'xAxisLabel2', 'Secondary X axis label') }}</label>
                <input type="text" [ngModel]="laSecondaryXLabel" (ngModelChange)="laSecondaryXLabel = $event; onLineAreaChange()" [placeholder]="laSecondaryXField || 'Auto'" />
              </div>
              <div class="control-group" *ngIf="laHasSecondaryY && fieldEnabled('lineArea', 'yAxisLabel2')">
                <label>{{ fieldLabel('lineArea', 'yAxisLabel2', 'Secondary Y axis label') }}</label>
                <label class="checkbox-label">
                  <input type="checkbox" [(ngModel)]="laShowSecondaryYAxis" (ngModelChange)="onLineAreaChange()" />
                  Show secondary Y-axis
                </label>
                <input type="text" [ngModel]="laSecondaryYLabel" (ngModelChange)="laSecondaryYLabel = $event; onLineAreaChange()" placeholder="Auto — secondary measure" />
              </div>
            </div>

            <!-- Per-axis label FONT WEIGHTS — highlight an axis's tick labels +
                 title independently. '' = default (falls back to the Format
                 popup's tile-wide "Axis label weight"). CONFIG-DRIVEN via
                 layoutSteps entries in line-area.json; options from the shared
                 shell `fontWeights` list. Secondary rows only show once the
                 matching secondary axis is mapped. -->
            <div class="control-row" *ngIf="fieldEnabled('lineArea', 'xAxisFontWeight') || fieldEnabled('lineArea', 'yAxisFontWeight')">
              <div class="control-group" *ngIf="fieldEnabled('lineArea', 'xAxisFontWeight')">
                <label>{{ fieldLabel('lineArea', 'xAxisFontWeight', 'X axis label weight') }}</label>
                <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="fontWeightOptions" bindLabel="label" bindValue="value" [clearable]="true" [searchable]="false" [ngModel]="laXAxisFontWeight" (ngModelChange)="laXAxisFontWeight = $event ?? ''; onLineAreaChange()"></ng-select>
              </div>
              <div class="control-group" *ngIf="fieldEnabled('lineArea', 'yAxisFontWeight')">
                <label>{{ fieldLabel('lineArea', 'yAxisFontWeight', 'Y axis label weight') }}</label>
                <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="fontWeightOptions" bindLabel="label" bindValue="value" [clearable]="true" [searchable]="false" [ngModel]="laYAxisFontWeight" (ngModelChange)="laYAxisFontWeight = $event ?? ''; onLineAreaChange()"></ng-select>
              </div>
            </div>
            <div class="control-row" *ngIf="(laSecondaryXField && fieldEnabled('lineArea', 'xAxis2FontWeight')) || (laHasSecondaryY && fieldEnabled('lineArea', 'yAxis2FontWeight'))">
              <div class="control-group" *ngIf="laSecondaryXField && fieldEnabled('lineArea', 'xAxis2FontWeight')">
                <label>{{ fieldLabel('lineArea', 'xAxis2FontWeight', 'Secondary X axis label weight') }}</label>
                <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="fontWeightOptions" bindLabel="label" bindValue="value" [clearable]="true" [searchable]="false" [ngModel]="laSecondaryXFontWeight" (ngModelChange)="laSecondaryXFontWeight = $event ?? ''; onLineAreaChange()"></ng-select>
              </div>
              <div class="control-group" *ngIf="laHasSecondaryY && fieldEnabled('lineArea', 'yAxis2FontWeight')">
                <label>{{ fieldLabel('lineArea', 'yAxis2FontWeight', 'Secondary Y axis label weight') }}</label>
                <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="fontWeightOptions" bindLabel="label" bindValue="value" [clearable]="true" [searchable]="false" [ngModel]="laSecondaryYFontWeight" (ngModelChange)="laSecondaryYFontWeight = $event ?? ''; onLineAreaChange()"></ng-select>
              </div>
            </div>

            <!-- Measures = the value axis. Each row: column + draw-as + axis (+ reorder). -->
            <div class="wf-subsection-header">
              <span>Y values <span class="wf-axis-hint">— measures</span> <span class="wf-step-count">{{ laMeasures.length }}</span></span>
              <button type="button" class="add-step-button" mat-stroked-button *ngIf="laAllowsMultipleMeasures" (click)="addLaMeasure()"><mat-icon>add</mat-icon> Add measure</button>
            </div>
            <small class="control-hint" *ngIf="laVariantUsesSeries && laSeries">Series split active — the first measure supplies the value.</small>

            <div class="wf-field-drop-zone bar-measures-zone" (dragover)="allowFieldDrop($event)" (drop)="onLaMeasureFieldDrop($event)">
              <div class="wf-empty" *ngIf="!laMeasures.length">No measures yet — add one, or drag a column here.</div>
              <div class="bar-measure-row" *ngFor="let measure of laMeasures; let i = index; let first = first; let last = last">
                <div class="control-row">
                  <div class="control-group wf-field-drop-zone" (dragover)="allowFieldDrop($event)" (drop)="onLaMeasureFieldDrop($event, measure)" title="Drag a column here">
                    <label>Column</label>
                    <ng-select
                      class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="laColumnsForDataset(measure.dataset || drillDataset)" [clearable]="true" [searchable]="true"
                      [loading]="isDatasetColumnsLoading(measure.dataset || drillDataset)" [disabled]="!(measure.dataset || drillDataset)" [placeholder]="barColumnPlaceholder()"
                      [ngModel]="measure.field || null" (ngModelChange)="measure.field = $event || ''; onLaMeasureChange()"
                    ></ng-select>
                    <label>Dataset</label>
                    <ng-select class="ss-select" [items]="entityOptions" [clearable]="false" [searchable]="true"
                      [ngModel]="measure.dataset || drillDataset || null" (ngModelChange)="onLaMeasureDatasetChange(measure, $event)"></ng-select>
                  </div>
                  <div class="control-group">
                    <label>Label</label>
                    <input type="text" [ngModel]="measure.label" (ngModelChange)="measure.label = $event; onLaMeasureChange()" [placeholder]="measure.field || 'Measure'" />
                  </div>
                </div>
                <div class="control-row">
                  <div class="control-group" *ngIf="!(laVariantUsesSeries && laSeries)">
                    <label>Draw as</label>
                    <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="lineAreaRenderTypes" bindLabel="label" bindValue="value" [clearable]="true" [ngModel]="measure.renderAs" (ngModelChange)="measure.renderAs = $event ?? ''; onLaMeasureChange()"></ng-select>
                  </div>
                  <div class="control-group" *ngIf="!(laVariantUsesSeries && laSeries)">
                    <label>Axis</label>
                    <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="lineAreaAxisOptions" bindLabel="label" bindValue="value" [clearable]="true" [ngModel]="measure.axis" (ngModelChange)="measure.axis = $event ?? ''; onLaMeasureChange()"></ng-select>
                  </div>
                  <!-- Line colour for THIS measure. Blank = take the palette
                       slot, so the swatch shows the colour actually in use and
                       the reset arrow puts it back. Hidden under a series split,
                       where lines come from the split's values, not measures. -->
                  <div class="control-group bar-measure-color" *ngIf="!(laVariantUsesSeries && laSeries)">
                    <label>Line color</label>
                    <div class="la-measure-color-row">
                      <input
                        type="color"
                        [ngModel]="laMeasureColor(measure, i)"
                        (ngModelChange)="measure.color = $event; onLaMeasureChange()"
                        [title]="measure.color ? 'Line color' : 'Palette default — pick to override'"
                      />
                      <button
                        type="button"
                        mat-icon-button
                        class="la-measure-color-reset"
                        *ngIf="measure.color"
                        (click)="measure.color = ''; onLaMeasureChange()"
                        matTooltip="Reset to palette color"
                      ><mat-icon>restart_alt</mat-icon></button>
                    </div>
                  </div>
                  <div class="control-group" *ngIf="!(laVariantUsesSeries && laSeries)">
                    <label>Data point shape</label>
                    <ng-select
                      class="ss-select" appendTo="body" ngClass="ss-dropdown-panel"
                      [items]="lineAreaSymbolOptions" bindLabel="label" bindValue="value"
                      [clearable]="true" [searchable]="false"
                      [ngModel]="measure.symbol || 'circle'"
                      (ngModelChange)="onLaMeasureSymbolChange(measure, $event)"
                    ></ng-select>
                  </div>
                  <div class="control-group" *ngIf="!(laVariantUsesSeries && laSeries)">
                    <label>Point size (px)</label>
                    <input
                      type="number" min="0" max="24" step="1"
                      [ngModel]="measure.symbolSize ?? 6"
                      (ngModelChange)="onLaMeasureSymbolSizeChange(measure, $event)"
                      title="Set this line's data point size in pixels"
                    />
                  </div>
                  <div class="control-group bar-measure-actions">
                    <button type="button" mat-icon-button *ngIf="laAllowsMultipleMeasures" (click)="moveLaMeasureUp(i)" [disabled]="first" matTooltip="Move up"><mat-icon>arrow_upward</mat-icon></button>
                    <button type="button" mat-icon-button *ngIf="laAllowsMultipleMeasures" (click)="moveLaMeasureDown(i)" [disabled]="last" matTooltip="Move down"><mat-icon>arrow_downward</mat-icon></button>
                    <button type="button" mat-icon-button class="row-remove-button" (click)="removeLaMeasure(measure)" matTooltip="Remove line" aria-label="Remove line"><mat-icon>close</mat-icon></button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Reference / target lines. -->
            <div class="wf-subsection-header">
              <span>Reference lines <span class="wf-step-count">{{ laReferenceLines.length }}</span></span>
              <button type="button" class="add-step-button" mat-stroked-button (click)="addLaReferenceLine()"><mat-icon>add</mat-icon> Add line</button>
            </div>
            <div class="bar-refline-list" *ngIf="laReferenceLines.length">
              <div class="control-row bar-refline-row" *ngFor="let line of laReferenceLines">
                <div class="control-group">
                  <label>Value</label>
                  <input type="number" [ngModel]="line.value" (ngModelChange)="line.value = $event; onLaReferenceLineChange()" placeholder="e.g. 100" />
                </div>
                <div class="control-group">
                  <label>Label</label>
                  <input type="text" [ngModel]="line.label" (ngModelChange)="line.label = $event; onLaReferenceLineChange()" placeholder="Target" />
                </div>
                <div class="control-group bar-refline-color">
                  <label>Color</label>
                  <input type="color" [ngModel]="line.color" (ngModelChange)="line.color = $event; onLaReferenceLineChange()" />
                </div>
                <button type="button" mat-icon-button class="bar-refline-remove row-remove-button" (click)="removeLaReferenceLine(line.id)" matTooltip="Remove line"><mat-icon>close</mat-icon></button>
              </div>
            </div>

          </div><!-- /Chart Data body -->
        </div>
      </div>

      <!-- Line & Area options — Title / Axis / Legend / Labels / Tooltip / Formatting /
           Analytics / Interaction / Advanced accordions (shared template). -->
      <ng-container [ngTemplateOutlet]="chartFormatAccordion"></ng-container>
    </div>

    <!-- ══════════════════ Scatter & Bubble builder ══════════════════
         Mirrors the Bar builder: a family card (variant picker), a Chart-Data
         accordion (X / Y / Size / Color / Series drop-zone pickers), then the
         config-driven Format accordions. Renders client-side via the pure
         scatter-bubble option builder. -->
    <div class="wf-section wf-section-plain" *ngIf="isScatterBubbleLayout">
      <ng-container *ngTemplateOutlet="datasetPicker"></ng-container>

      <div class="bridge-chart-settings bar-family-card">
        <div class="bridge-settings-heading"><mat-icon>scatter_plot</mat-icon><span>{{ layoutSectionTitle('scatterBubble', 'Scatter & bubble') }}</span></div>
        <small class="control-hint" *ngIf="layoutSectionHint('scatterBubble')">{{ layoutSectionHint('scatterBubble') }}</small>

        <!-- Orientation segmented toggle — sides are FULLY config-driven
             (scatterBubbleModes: label + icon + value); the Variant dropdown
             below lists only the active side's base variants. -->
        <div class="control-group" *ngIf="fieldEnabled('scatterBubble', 'mode')">
          <label>{{ fieldLabel('scatterBubble', 'mode', 'Orientation') }}</label>
          <div class="wf-step-mode-toggle wf-sb-mode-toggle" role="group" [attr.aria-label]="fieldLabel('scatterBubble', 'mode', 'Orientation')">
            <button type="button" *ngFor="let m of sbModes" [class.is-active]="sbMode === m.value" (click)="setSbMode(m.value)">
              <mat-icon *ngIf="m.icon">{{ m.icon }}</mat-icon> {{ m.label }}
            </button>
          </div>
        </div>

        <!-- Variant — only the active toggle side's BASIC variants (Scatter /
             Multi Scatter, or Bubble / Bubble Matrix); advanced ones live in
             the accordion below. -->
        <div class="control-group" *ngIf="fieldEnabled('scatterBubble', 'variant')">
          <label>{{ fieldLabel('scatterBubble', 'variant', 'Variant') }}</label>
          <ng-select
            #ssSbVariant
            class="ss-select"
            [class.is-open]="!!ssSbVariant.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="sbVariantItems"
            bindLabel="label"
            bindValue="value"
            [clearable]="true"
            [ngModel]="sbVariant"
            (ngModelChange)="sbVariant = $event ?? ''; onScatterBubbleChange()"
          ></ng-select>
          <small class="control-hint wf-variant-hint" *ngIf="sbVariantHint">{{ sbVariantHint }}</small>
        </div>

        <!-- Advanced OVERLAYS accordion — Quadrant / Regression / Correlation /
             Cluster Scatter layer ON TOP of the selected base variant (Quadrant
             + Bubble, Regression + Scatter, …). Single-select rows; click the
             active one again to turn the overlay off. -->
        <div class="dvm-format-group wf-sb-advanced" [class.is-open]="sbAdvancedAccordionOpen" *ngIf="fieldEnabled('scatterBubble', 'advanced') && sbAdvancedVariantItems.length">
          <button type="button" class="dvm-format-group-head" (click)="sbAdvancedAccordionOpen = !sbAdvancedAccordionOpen">
            <span>{{ fieldLabel('scatterBubble', 'advanced', 'Multivariate Combinations') }}</span>
            <mat-icon>{{ sbAdvancedAccordionOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
          <div class="dvm-format-group-body wf-sb-advanced-list" *ngIf="sbAdvancedAccordionOpen">
            <button
              type="button"
              class="wf-sb-advanced-option"
              *ngFor="let v of sbAdvancedVariantItems"
              [class.is-active]="sbAdvanced === v.value"
              (click)="toggleSbAdvanced(v.value)"
            >
              <span class="wf-sb-advanced-label">{{ v.label }}</span>
              <mat-icon *ngIf="sbAdvanced === v.value">check</mat-icon>
            </button>
            <small class="control-hint wf-variant-hint" *ngIf="sbAdvancedHint">{{ sbAdvancedHint }}</small>
          </div>
        </div>
      </div>

      <!-- ══ Chart Data accordion — X / Y / Size / Color / Series ══ -->
      <div class="bar-options-accordion">
        <div class="dvm-format-group" [class.is-open]="sbDataAccordionOpen">
          <button type="button" class="dvm-format-group-head" (click)="sbDataAccordionOpen = !sbDataAccordionOpen">
            <span>Chart Data</span>
            <mat-icon>{{ sbDataAccordionOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
          <div class="dvm-format-group-body" *ngIf="sbDataAccordionOpen">

            <div class="control-row">
              <div class="control-group wf-field-drop-zone" (dragover)="allowFieldDrop($event)" (drop)="onSbXFieldDrop($event)" title="Drag a column here">
                <label>X axis <span class="wf-axis-hint">— measure</span></label>
                <ng-select
                  #ssSbX class="ss-select" [class.is-open]="!!ssSbX.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
                  [items]="scatterBubbleColumns" [clearable]="true" [searchable]="true"
                  [loading]="isDrillDatasetColumnsLoading" [disabled]="!drillDataset"
                  [placeholder]="drillDataset ? 'Pick a column' : 'Select a dataset first'"
                  [ngModel]="sbXField || null" (ngModelChange)="sbXField = $event || ''; onScatterBubbleChange()"
                ></ng-select>
              </div>
              <div class="control-group">
                <label>X aggregation</label>
                <ng-select
                  #ssSbXAgg class="ss-select" [class.is-open]="!!ssSbXAgg.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
                  [items]="measureAggregations" bindLabel="label" bindValue="value" [clearable]="true"
                  [ngModel]="sbXAggregation" (ngModelChange)="sbXAggregation = $event ?? ''; onScatterBubbleChange()"
                ></ng-select>
              </div>
            </div>

            <div class="control-row">
              <div class="control-group wf-field-drop-zone" (dragover)="allowFieldDrop($event)" (drop)="onSbYFieldDrop($event)" title="Drag a column here">
                <label>Y axis <span class="wf-axis-hint">— measure</span></label>
                <ng-select
                  #ssSbY class="ss-select" [class.is-open]="!!ssSbY.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
                  [items]="scatterBubbleColumns" [clearable]="true" [searchable]="true"
                  [loading]="isDrillDatasetColumnsLoading" [disabled]="!drillDataset"
                  [placeholder]="drillDataset ? 'Pick a column' : 'Select a dataset first'"
                  [ngModel]="sbYField || null" (ngModelChange)="sbYField = $event || ''; onScatterBubbleChange()"
                ></ng-select>
              </div>
              <div class="control-group">
                <label>Y aggregation</label>
                <ng-select
                  #ssSbYAgg class="ss-select" [class.is-open]="!!ssSbYAgg.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
                  [items]="measureAggregations" bindLabel="label" bindValue="value" [clearable]="true"
                  [ngModel]="sbYAggregation" (ngModelChange)="sbYAggregation = $event ?? ''; onScatterBubbleChange()"
                ></ng-select>
              </div>
            </div>

            <div class="control-row">
              <!-- Size — REQUIRED for Bubble variants only. -->
              <div class="control-group wf-field-drop-zone" [class.is-required-empty]="sbVariantUsesSize && !sbSizeField" *ngIf="sbVariantUsesSize" (dragover)="allowFieldDrop($event)" (drop)="onSbSizeFieldDrop($event)" title="Drag a column here">
                <label>Bubble size <span class="wf-axis-hint">(required)</span></label>
                <ng-select
                  #ssSbSize class="ss-select" [class.is-open]="!!ssSbSize.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
                  [items]="scatterBubbleColumns" [clearable]="true" [searchable]="true"
                  [loading]="isDrillDatasetColumnsLoading" [disabled]="!drillDataset"
                  placeholder="Pick a size measure"
                  [ngModel]="sbSizeField || null" (ngModelChange)="sbSizeField = $event || ''; onScatterBubbleChange()"
                ></ng-select>
              </div>
              <div class="control-group" *ngIf="sbVariantUsesSize">
                <label>Size aggregation</label>
                <ng-select
                  #ssSbSizeAgg class="ss-select" [class.is-open]="!!ssSbSizeAgg.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
                  [items]="measureAggregations" bindLabel="label" bindValue="value" [clearable]="true"
                  [ngModel]="sbSizeAggregation" (ngModelChange)="sbSizeAggregation = $event ?? ''; onScatterBubbleChange()"
                ></ng-select>
              </div>
              <!-- Color by — optional. -->
              <div class="control-group wf-field-drop-zone" (dragover)="allowFieldDrop($event)" (drop)="onSbColorFieldDrop($event)" title="Drag a column here">
                <label>Color by <span class="wf-axis-hint">(optional)</span></label>
                <ng-select
                  #ssSbColor class="ss-select" [class.is-open]="!!ssSbColor.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
                  [items]="scatterBubbleColumns" [clearable]="true" [searchable]="true"
                  [loading]="isDrillDatasetColumnsLoading" [disabled]="!drillDataset"
                  placeholder="Optional grouping color"
                  [ngModel]="sbColorField || null" (ngModelChange)="sbColorField = $event || ''; onScatterBubbleChange()"
                ></ng-select>
              </div>
            </div>

            <div class="control-row">
              <!-- Series — REQUIRED for Multi Scatter only. -->
              <div class="control-group wf-field-drop-zone" [class.is-required-empty]="sbVariantUsesSeries && !sbSeries" *ngIf="sbVariantUsesSeries" (dragover)="allowFieldDrop($event)" (drop)="onSbSeriesFieldDrop($event)" title="Drag a column here">
                <label>Series / group <span class="wf-axis-hint">(required)</span></label>
                <ng-select
                  #ssSbSeries class="ss-select" [class.is-open]="!!ssSbSeries.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
                  [items]="scatterBubbleColumns" [clearable]="true" [searchable]="true"
                  [loading]="isDrillDatasetColumnsLoading" [disabled]="!drillDataset"
                  placeholder="Pick a column to split into series"
                  [ngModel]="sbSeries || null" (ngModelChange)="sbSeries = $event || ''; onScatterBubbleChange()"
                ></ng-select>
              </div>
            </div>

            <!-- Point label column (optional) — the text drawn on each point
                 (an entity name). Config-driven via layoutSteps `labelField`. -->
            <div class="control-row" *ngIf="fieldEnabled('scatterBubble', 'labelField')">
              <div class="control-group wf-field-drop-zone" (dragover)="allowFieldDrop($event)" (drop)="onSbLabelFieldDrop($event)" title="Drag a column here">
                <label>{{ fieldLabel('scatterBubble', 'labelField', 'Point label') }} <span class="wf-axis-hint">(optional)</span></label>
                <ng-select
                  #ssSbLabel class="ss-select" [class.is-open]="!!ssSbLabel.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
                  [items]="scatterBubbleColumns" [clearable]="true" [searchable]="true"
                  [loading]="isDrillDatasetColumnsLoading" [disabled]="!drillDataset"
                  placeholder="Column drawn as each point's label"
                  [ngModel]="sbLabelField || null" (ngModelChange)="sbLabelField = $event || ''; onScatterBubbleChange()"
                ></ng-select>
              </div>
            </div>

            <!-- Category colors — one swatch per distinct Color-by value. Unpicked
                 values keep their palette slot (the renderer's own fallback), so
                 the swatch always shows the color the chart is using. -->
            <div class="wf-sb-cat-colors" *ngIf="sbColorField && fieldEnabled('scatterBubble', 'categoryColors') && sbColorCategories.length">
              <div class="wf-sb-cat-colors-head">
                <span>{{ fieldLabel('scatterBubble', 'categoryColors', 'Category colors') }} <span class="wf-axis-hint">— {{ sbColorField }}</span></span>
                <button type="button" class="wf-sb-cat-colors-reset" *ngIf="hasSbCategoryColors" (click)="resetSbCategoryColors()">Reset to palette</button>
              </div>
              <div class="wf-sb-cat-colors-list">
                <label class="wf-sb-cat-color" *ngFor="let cat of sbColorCategories; let i = index" [title]="cat || '(blank)'">
                  <input type="color" [ngModel]="sbCategoryColor(cat, i)" (ngModelChange)="onSbCategoryColorChange(cat, $event)" />
                  <span class="wf-sb-cat-color-name">{{ cat || '(blank)' }}</span>
                </label>
              </div>
            </div>

            <!-- Per-axis label FONT WEIGHTS — highlight an axis's tick labels +
                 title independently. '' = default (falls back to the Format
                 popup's tile-wide "Axis label weight"). CONFIG-DRIVEN via
                 layoutSteps entries in scatter-bubble.json; options from the
                 shared shell `fontWeights` list. -->
            <div class="control-row" *ngIf="fieldEnabled('scatterBubble', 'xAxisFontWeight') || fieldEnabled('scatterBubble', 'yAxisFontWeight')">
              <div class="control-group" *ngIf="fieldEnabled('scatterBubble', 'xAxisFontWeight')">
                <label>{{ fieldLabel('scatterBubble', 'xAxisFontWeight', 'X axis label weight') }}</label>
                <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="fontWeightOptions" bindLabel="label" bindValue="value" [clearable]="true" [searchable]="false" [ngModel]="sbXAxisFontWeight" (ngModelChange)="sbXAxisFontWeight = $event ?? ''; onScatterBubbleChange()"></ng-select>
              </div>
              <div class="control-group" *ngIf="fieldEnabled('scatterBubble', 'yAxisFontWeight')">
                <label>{{ fieldLabel('scatterBubble', 'yAxisFontWeight', 'Y axis label weight') }}</label>
                <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="fontWeightOptions" bindLabel="label" bindValue="value" [clearable]="true" [searchable]="false" [ngModel]="sbYAxisFontWeight" (ngModelChange)="sbYAxisFontWeight = $event ?? ''; onScatterBubbleChange()"></ng-select>
              </div>
            </div>

            <div class="control-row">
              <!-- Aggregation — how rows sharing the same X (+ group) roll up. -->
              <div class="control-group" *ngIf="fieldEnabled('scatterBubble', 'aggregation')">
                <label>{{ fieldLabel('scatterBubble', 'aggregation', 'Aggregation') }} <span class="wf-axis-hint">— rows sharing X</span></label>
                <ng-select
                  #ssSbAgg class="ss-select" [class.is-open]="!!ssSbAgg.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
                  [items]="sbAggregationOptions" bindLabel="label" bindValue="value" [clearable]="true"
                  [ngModel]="sbAggregation" (ngModelChange)="sbAggregation = $event ?? ''; onScatterBubbleChange()"
                ></ng-select>
              </div>
              <!-- Tooltip columns — extra dataset columns shown on hover. -->
              <div class="control-group" *ngIf="fieldEnabled('scatterBubble', 'tooltipColumns')">
                <label>{{ fieldLabel('scatterBubble', 'tooltipColumns', 'Tooltip columns') }} <span class="wf-axis-hint">(optional)</span></label>
                <ng-select
                  #ssSbTooltipCols class="ss-select" [class.is-open]="!!ssSbTooltipCols.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
                  [items]="scatterBubbleColumns" [multiple]="true" [clearable]="true" [searchable]="true"
                  [loading]="isDrillDatasetColumnsLoading" [disabled]="!drillDataset"
                  placeholder="Extra columns on hover"
                  [ngModel]="sbTooltipColumns" (ngModelChange)="sbTooltipColumns = $event || []; onScatterBubbleChange()"
                ></ng-select>
              </div>
            </div>

          </div><!-- /Chart Data body -->
        </div><!-- /Chart Data group -->
      </div><!-- /Chart Data accordion -->

      <!-- Scatter & Bubble Format accordions (config-driven; own sbFormat state). -->
      <div class="bar-options-accordion" *ngIf="scatterBubbleFormatGroups.length">
        <div class="dvm-format-group" *ngFor="let group of scatterBubbleFormatGroups" [class.is-open]="isSbFormatGroupOpen(group.name)">
          <button type="button" class="dvm-format-group-head" (click)="toggleSbFormatGroup(group.name)">
            <span>{{ group.name }}</span>
            <mat-icon>{{ isSbFormatGroupOpen(group.name) ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
          <div class="dvm-format-group-body" *ngIf="isSbFormatGroupOpen(group.name)">
            <ng-container *ngFor="let control of group.controls">
            <div class="dvm-format-row" *ngIf="formatControlVisible(control, sbFormat)">
              <label>{{ control.label }}</label>
              <input *ngIf="control.control === 'text'" type="text" [ngModel]="sbFormat[control.key]" (ngModelChange)="sbFormat[control.key] = $event; onScatterBubbleFormatChange()" [placeholder]="control.label" />
              <input *ngIf="control.control === 'number'" type="number" [min]="control.min ?? null" [max]="control.max ?? null" [step]="control.step ?? 1" [ngModel]="sbFormat[control.key]" (ngModelChange)="sbFormat[control.key] = $event; onScatterBubbleFormatChange()" />
              <input *ngIf="control.control === 'color'" type="color" [ngModel]="sbFormat[control.key]" (ngModelChange)="sbFormat[control.key] = $event; onScatterBubbleFormatChange()" />
              <label *ngIf="control.control === 'toggle'" class="dvm-format-toggle">
                <input type="checkbox" [ngModel]="sbFormat[control.key]" (ngModelChange)="sbFormat[control.key] = $event; onScatterBubbleFormatChange()" />
              </label>
              <ng-select *ngIf="control.control === 'select'" class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="formatControlOptions(control)" bindLabel="label" bindValue="value" [clearable]="true" [ngModel]="sbFormat[control.key]" (ngModelChange)="sbFormat[control.key] = $event ?? ''; onScatterBubbleFormatChange()"></ng-select>
            </div>
            </ng-container>
          </div>
        </div>
      </div>
    </div>

    <!-- Chart format options — SHARED accordion template (rendered for Bar AND
         Waterfall). Config-driven: formatControls filtered to the ACTIVE layout
         type + grouped by `group`; the capabilityRegistry's `sections` list gates
         which accordions appear. Values fold into the tile on Generate. -->
    <ng-template #chartFormatAccordion>
      <div class="bar-options-accordion" *ngIf="barFormatGroups.length">
        <div class="dvm-format-group" *ngFor="let group of barFormatGroups" [class.is-open]="isBarFormatGroupOpen(group.name)">
          <button type="button" class="dvm-format-group-head" (click)="toggleBarFormatGroup(group.name)">
            <span>{{ group.name }}</span>
            <mat-icon>{{ isBarFormatGroupOpen(group.name) ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>

          <div class="dvm-format-group-body" *ngIf="isBarFormatGroupOpen(group.name)">
            <ng-container *ngFor="let control of group.controls">
            <div class="dvm-format-row" *ngIf="formatControlVisible(control, barFormat)">
              <label>{{ control.label }}</label>

              <!-- CRUD endpoints: each gets its OWN request-body editor, right
                   beside its label, so every URL can post a different contract. -->
              <button
                type="button"
                class="dvm-pt-open-btn"
                *ngIf="control.requestBodyEditor"
                [class.is-set]="hasPayloadTemplate(barFormat, control.key)"
                [matTooltip]="'Configure the request body posted to this endpoint'"
                (click)="openPayloadTemplateModalForBar(control)"
              >{{ hasPayloadTemplate(barFormat, control.key) ? 'Body ✓' : 'Body' }}</button>

              <input
                *ngIf="control.control === 'text'"
                type="text"
                [ngModel]="barFormat[control.key]"
                (ngModelChange)="barFormat[control.key] = $event; onBarFormatChange()"
                [placeholder]="control.label"
              />

              <input
                *ngIf="control.control === 'number'"
                type="number"
                [min]="control.min ?? null"
                [max]="control.max ?? null"
                [step]="control.step ?? 1"
                [ngModel]="barFormat[control.key]"
                (ngModelChange)="barFormat[control.key] = $event; onBarFormatChange()"
              />

              <input
                *ngIf="control.control === 'color'"
                type="color"
                [ngModel]="barFormat[control.key]"
                (ngModelChange)="barFormat[control.key] = $event; onBarFormatChange()"
              />

              <label *ngIf="control.control === 'toggle'" class="dvm-format-toggle">
                <input
                  type="checkbox"
                  [ngModel]="barFormat[control.key]"
                  (ngModelChange)="barFormat[control.key] = $event; onBarFormatChange()"
                />
              </label>

              <ng-select
                *ngIf="control.control === 'select'"
                class="ss-select"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="formatControlOptions(control)"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [ngModel]="barFormat[control.key]"
                (ngModelChange)="barFormat[control.key] = $event ?? ''; onBarFormatChange()"
              ></ng-select>

              <ng-select
                *ngIf="control.control === 'column-select'"
                class="ss-select"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="formatControlOptions(control)"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [ngModel]="barFormat[control.key]"
                (ngModelChange)="barFormat[control.key] = $event || ''; onBarFormatChange()"
              ></ng-select>

              <ng-select
                *ngIf="control.control === 'multi-select'"
                class="ss-select ss-multi-select"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="formatControlOptions(control)"
                bindLabel="label"
                bindValue="value"
                [multiple]="true"
                [closeOnSelect]="false"
                [clearable]="true"
                [addTag]="addTypedValueTag"
                [placeholder]="control.placeholder || 'Pick from the dataset…'"
                [ngModel]="multiSelectModel(barFormat, control.key)"
                (ngModelChange)="barFormat[control.key] = $event || []; onBarFormatChange()"
              ></ng-select>
            </div>
            </ng-container>
          </div>
        </div>
      </div>
    </ng-template>

    <!-- Dropdown control: distinct values from one field become the available
         options and the selected value is applied to the chosen targets. -->
    <div class="wf-section" *ngIf="isDropdownLayout">
      <header class="wf-section-header">
        <h3>{{ layoutSectionTitle('dropdown', 'Dropdown settings') }}</h3>
      </header>
      <small class="control-hint">
        {{ layoutSectionHint('dropdown', 'Choose the field that supplies the options and the dashboard components this dropdown controls.') }}
      </small>
      <ng-container *ngTemplateOutlet="datasetPicker"></ng-container>

      <div
        class="control-group control-group-full wf-field-drop-zone"
        *ngIf="fieldEnabled('dropdown', 'field')"
        (dragover)="allowFieldDrop($event)"
        (drop)="onDropdownFieldDrop($event)"
        title="Drag a dataset column here"
      >
        <label>{{ fieldLabel('dropdown', 'field', 'Option field') }}</label>
        <ng-select
          #ssDropdownField
          class="ss-select"
          [class.is-open]="!!ssDropdownField.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="drillDatasetColumns"
          [clearable]="true"
          [searchable]="true"
          [loading]="isDrillDatasetColumnsLoading"
          [disabled]="!drillDataset"
          [placeholder]="drillColumnPlaceholder('dimension')"
          [(ngModel)]="dropdownField"
        ></ng-select>
      </div>

      <div class="control-row">
        <div class="control-group" *ngIf="fieldEnabled('dropdown', 'displayLabel')">
          <label>{{ fieldLabel('dropdown', 'displayLabel', 'Display label') }}</label>
          <input type="text" [(ngModel)]="dropdownDisplayLabel" placeholder="e.g. Region" />
        </div>
        <div class="control-group" *ngIf="fieldEnabled('dropdown', 'defaultValue')">
          <label>{{ fieldLabel('dropdown', 'defaultValue', 'Default value') }}</label>
          <input type="text" [(ngModel)]="dropdownDefaultValue" placeholder="Optional" />
        </div>
      </div>

      <!-- Display as: classic select vs always-visible filter buttons/tiles. -->
      <div class="control-group control-group-full" *ngIf="fieldEnabled('dropdown', 'displayMode')">
        <label>{{ fieldLabel('dropdown', 'displayMode', 'Display as') }}</label>
        <ng-select
          #ssDropdownDisplayMode
          class="ss-select"
          [class.is-open]="!!ssDropdownDisplayMode.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="dropdownDisplayModes"
          bindLabel="label"
          bindValue="value"
          [clearable]="true"
          [searchable]="false"
          [(ngModel)]="dropdownDisplayMode"
        ></ng-select>
      </div>

      <!-- Submenu groups (buttons/tiles only): nest option values under a
           parent pill — clicking the parent opens its values as an inline
           submenu with an "All <parent>" entry. Values are validated against
           the live backend options at render time, so stale entries drop. -->
      <div
        class="control-group control-group-full"
        *ngIf="fieldEnabled('dropdown', 'optionGroups') && (dropdownDisplayMode === 'buttons' || dropdownDisplayMode === 'tiles')"
      >
        <label>{{ fieldLabel('dropdown', 'optionGroups', 'Submenu groups (optional)') }}</label>
        <small class="control-hint">
          Group option values under a parent — e.g. put MARI and MXDR inside Mining. The submenu shows exactly the values you pick; if the parent is itself an option value, add it to "Values inside" to keep it selectable. You can type values that aren't listed yet.
        </small>
        <div class="dropdown-group-card" *ngFor="let group of dropdownGroups; let i = index">
          <div class="dropdown-group-card-header">
            <span>{{ group.displayLabel || group.label || 'Group ' + (i + 1) }}</span>
            <button
              type="button"
              class="dropdown-group-remove"
              title="Remove this group"
              (click)="removeDropdownGroup(i)"
            >&times;</button>
          </div>
          <div class="dropdown-group-field">
            <label>Parent</label>
            <ng-select
              class="ss-select"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="dropdownFieldOptions"
              [addTag]="dropdownGroupTag"
              [clearable]="true"
              [searchable]="true"
              placeholder="e.g. Mining"
              [(ngModel)]="group.label"
            ></ng-select>
          </div>
          <!-- Display-only pill text; matching stays on Parent (the value). -->
          <div class="dropdown-group-field">
            <label>Display label (optional)</label>
            <input type="text" [(ngModel)]="group.displayLabel" placeholder="Shown on the pill — defaults to Parent" />
          </div>
          <div class="dropdown-group-field">
            <label>Values inside</label>
            <ng-select
              class="ss-select ss-select--multi ss-select--full"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="dropdownFieldOptions"
              [addTag]="dropdownGroupTag"
              [multiple]="true"
              [closeOnSelect]="false"
              [hideSelected]="true"
              [clearable]="true"
              [searchable]="true"
              placeholder="e.g. MARI, MXDR"
              [(ngModel)]="group.children"
            ></ng-select>
          </div>
          <label class="checkbox-label dropdown-group-showall" *ngIf="fieldEnabled('dropdown', 'groupShowAll')">
            <input type="checkbox" [(ngModel)]="group.showAll" />
            Show "All {{ group.displayLabel || group.label || 'group' }}" entry in the submenu
          </label>
        </div>
        <button type="button" class="target-reference-add" (click)="addDropdownGroup()">+ Add group</button>
      </div>

      <div class="control-group control-group-full" *ngIf="fieldEnabled('dropdown', 'targetCharts')">
        <label>{{ fieldLabel('dropdown', 'targetCharts', 'Target chart(s) / input(s)') }}</label>
        <ng-select
          #ssDropdownTargets
          class="ss-select ss-select--multi ss-select--full"
          [class.is-open]="!!ssDropdownTargets.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="targetCharts"
          bindLabel="label"
          bindValue="value"
          [multiple]="true"
          [closeOnSelect]="false"
          [hideSelected]="false"
          [clearable]="true"
          [searchable]="true"
          placeholder="Select the chart(s) or input(s) this dropdown filters"
          [(ngModel)]="selectedTargetCharts"
        ></ng-select>
        <small class="control-hint" *ngIf="!targetCharts.length">Add a chart or input to the dashboard first so it can be selected as a target.</small>
      </div>
      <ng-container *ngTemplateOutlet="apiCallConfigBlock; context: { kind: 'dropdown' }"></ng-container>

      <div class="control-group control-group-full" *ngIf="fieldEnabled('dropdown', 'isMultiSelect')">
        <label class="checkbox-label">
          <input type="checkbox" [(ngModel)]="dropdownIsMultiSelect" />
          {{ fieldLabel('dropdown', 'isMultiSelect', 'Allow multiple selections') }}
        </label>
      </div>
      <div class="control-group control-group-full" *ngIf="fieldEnabled('dropdown', 'showAllOption')">
        <label class="checkbox-label">
          <input type="checkbox" [(ngModel)]="dropdownShowAllOption" />
          {{ fieldLabel('dropdown', 'showAllOption', 'Show All option') }}
        </label>
      </div>
      <div class="control-group control-group-full">
        <label class="checkbox-label">
          <input type="checkbox" [(ngModel)]="dropdownAutoSelectFirst" />
          Select first option by default
        </label>
        <small class="control-hint">Selects the first available option when no valid selection exists, including after a targeted filter changes its options.</small>
      </div>

      <div
        class="dvm-format-group"
        [class.is-open]="dropdownAdvancedAccordionOpen"
        *ngIf="fieldEnabled('dropdownAdvanced', 'optionOrderMode') || fieldEnabled('dropdownAdvanced', 'customOptionOrder')"
      >
        <button type="button" class="dvm-format-group-head" (click)="dropdownAdvancedAccordionOpen = !dropdownAdvancedAccordionOpen">
          <span>{{ layoutSectionTitle('dropdownAdvanced', 'Advanced') }}</span>
          <mat-icon>{{ dropdownAdvancedAccordionOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
        </button>
        <div class="dvm-format-group-body" *ngIf="dropdownAdvancedAccordionOpen">
        <small class="control-hint">{{ layoutSectionHint('dropdownAdvanced', '') }}</small>

        <div class="control-group control-group-full" *ngIf="fieldEnabled('dropdownAdvanced', 'optionOrderMode')">
          <label>{{ fieldLabel('dropdownAdvanced', 'optionOrderMode', 'Option order') }}</label>
          <ng-select
            #ssDropdownOptionOrder
            class="ss-select"
            [class.is-open]="!!ssDropdownOptionOrder.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="dropdownOptionOrderModes"
            bindLabel="label"
            bindValue="value"
            [clearable]="true"
            [searchable]="false"
            [(ngModel)]="dropdownOptionOrderMode"
          ></ng-select>
        </div>

        <div class="control-group control-group-full" *ngIf="fieldEnabled('dropdownAdvanced', 'customOptionOrder') && dropdownOptionOrderMode === 'custom'">
          <label>{{ fieldLabel('dropdownAdvanced', 'customOptionOrder', 'Custom option order') }}</label>
          <input type="text" [(ngModel)]="dropdownCustomOptionOrder" placeholder="e.g. High, Medium, Low" />
        </div>
        </div>
      </div>

      <!-- Appearance accordion — the SHARED config-driven format accordion
           (formatControls appliesTo DROPDOWN: pill colors / radius / font
           size). Same values as the tile's Format popup; live-applied via
           onBarFormatChange → foldFormatIntoEditingDropdownTile. -->
      <ng-container *ngTemplateOutlet="chartFormatAccordion"></ng-container>
    </div>

    <!-- File upload (FILE_UPLOAD): a standalone uploader whose picked file POSTs
         to the compute API and re-renders the selected target chart(s)/table(s). -->
    <div class="wf-section" *ngIf="isFileUploadLayout">
      <header class="wf-section-header">
        <h3>{{ layoutSectionTitle('upload', 'Upload settings') }}</h3>
      </header>
      <small class="control-hint">{{ layoutSectionHint('upload', 'The uploaded file is sent to the compute API and the response re-renders the selected target(s).') }}</small>

      <div class="control-group control-group-full" *ngIf="fieldEnabled('upload', 'targetCharts')">
        <label>{{ fieldLabel('upload', 'targetCharts', 'Target chart(s) / table(s)') }}</label>
        <ng-select
          #ssUploadTargets
          class="ss-select ss-select--multi ss-select--full"
          [class.is-open]="!!ssUploadTargets.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="targetCharts"
          bindLabel="label"
          bindValue="value"
          [multiple]="true"
          [closeOnSelect]="false"
          [hideSelected]="false"
          [clearable]="true"
          [searchable]="true"
          placeholder="Select the chart(s) or table(s) the uploaded file updates"
          [(ngModel)]="selectedTargetCharts"
        ></ng-select>
        <small class="control-hint" *ngIf="!targetCharts.length">Add a chart or table to the dashboard first so it can be selected as a target.</small>
      </div>
      <ng-container *ngTemplateOutlet="apiCallConfigBlock; context: { kind: 'upload' }"></ng-container>

      <div class="control-row">
        <div class="control-group" *ngIf="fieldEnabled('upload', 'buttonLabel')">
          <label>{{ fieldLabel('upload', 'buttonLabel', 'Upload button label') }}</label>
          <input type="text" [(ngModel)]="fileUploadButtonLabel" placeholder="Upload file" />
        </div>
        <div class="control-group" *ngIf="fieldEnabled('upload', 'accept')">
          <label>{{ fieldLabel('upload', 'accept', 'Accepted file types') }}</label>
          <input type="text" [(ngModel)]="fileUploadAccept" placeholder=".xlsx,.xls,.csv" />
        </div>
      </div>
      <!-- No Format accordion here by design: File Upload tiles don't take
           card-header formatting (builder or published). -->
    </div>

    <!-- Popup component (POPUP): a trigger (icon / button / tile) that opens a
         configurable popup dialog of content blocks. Display-only — no compute
         call; table blocks snapshot rows client-side at Generate time. -->
    <div class="wf-section dvm-popup-form" *ngIf="isPopupLayout">
      <header class="wf-section-header">
        <h3>{{ layoutSectionTitle('popupTrigger', 'Trigger') }}</h3>
      </header>
      <small class="control-hint">{{ layoutSectionHint('popupTrigger', 'How the popup opens on the dashboard: a compact info icon, a button, or a card-style tile.') }}</small>

      <div class="control-row">
        <div class="control-group">
          <label>{{ fieldLabel('popupTrigger', 'triggerMode', 'Open on') }}</label>
          <ng-select
            #ssPopupTriggerMode
            class="ss-select"
            [class.is-open]="!!ssPopupTriggerMode.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="popupTriggerModes"
            bindLabel="label"
            bindValue="value"
            [clearable]="true"
            [searchable]="false"
            [(ngModel)]="popupTriggerMode" (ngModelChange)="onPopupFormChange()"
          ></ng-select>
        </div>
        <div class="control-group">
          <label>{{ fieldLabel('popupTrigger', 'triggerIcon', 'Icon') }}</label>
          <ng-select
            #ssPopupTriggerIcon
            class="ss-select"
            [class.is-open]="!!ssPopupTriggerIcon.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="popupIcons"
            bindLabel="label"
            bindValue="value"
            [clearable]="true"
            [searchable]="false"
            [(ngModel)]="popupTriggerIcon" (ngModelChange)="onPopupFormChange()"
          ></ng-select>
        </div>
      </div>
      <div class="control-row">
        <div class="control-group">
          <label>{{ fieldLabel('popupTrigger', 'triggerLabel', 'Trigger label') }}</label>
          <input type="text" [(ngModel)]="popupTriggerLabel" (ngModelChange)="onPopupFormChange()" placeholder="Defaults to the Title" />
        </div>
        <div class="control-group">
          <label>{{ fieldLabel('popupTrigger', 'triggerHint', 'Hover hint') }}</label>
          <input type="text" [(ngModel)]="popupTriggerHint" (ngModelChange)="onPopupFormChange()" placeholder="Optional tooltip" />
        </div>
      </div>

      <header class="wf-section-header">
        <h3>{{ layoutSectionTitle('popupBlocks', 'Popup content') }}</h3>
      </header>
      <small class="control-hint">{{ layoutSectionHint('popupBlocks', 'Build the popup body from blocks: headings, text, key-value audit rows, data tables and action buttons. Blocks render top-to-bottom.') }}</small>

      <div class="dvm-popup-block-card" *ngFor="let block of popupBlocks; let i = index; let first = first; let last = last">
        <div class="dvm-popup-block-head">
          <span class="wf-step-index">{{ i + 1 }}</span>
          <span class="dvm-popup-block-actions">
            <button type="button" class="dvm-popup-mini-btn" [disabled]="first" (click)="movePopupBlock(i, -1)" matTooltip="Move up"><mat-icon>arrow_upward</mat-icon></button>
            <button type="button" class="dvm-popup-mini-btn" [disabled]="last" (click)="movePopupBlock(i, 1)" matTooltip="Move down"><mat-icon>arrow_downward</mat-icon></button>
            <button type="button" class="dvm-popup-mini-btn is-danger" (click)="removePopupBlock(i)" matTooltip="Remove block"><mat-icon>close</mat-icon></button>
          </span>
        </div>
        <div class="control-group control-group-full">
          <ng-select
            #ssPopupBlockType
            class="ss-select dvm-popup-block-type"
            [class.is-open]="!!ssPopupBlockType.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="popupBlockTypes"
            bindLabel="label"
            bindValue="value"
            [clearable]="true"
            [searchable]="false"
            [(ngModel)]="block.type" (ngModelChange)="onPopupFormChange()"
          ></ng-select>
        </div>

        <!-- Heading block -->
        <ng-container *ngIf="block.type === 'heading'">
          <div class="control-group control-group-full">
            <label>Heading text</label>
            <input type="text" [(ngModel)]="block.text" (ngModelChange)="onPopupFormChange()" placeholder="e.g. Audit information" />
          </div>
          <div class="control-row">
            <div class="control-group">
              <label>Size</label>
              <ng-select
                #ssPopupHeadingLevel
                class="ss-select"
                [class.is-open]="!!ssPopupHeadingLevel.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="popupHeadingLevels"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [searchable]="false"
                [(ngModel)]="block.level" (ngModelChange)="onPopupFormChange()"
              ></ng-select>
            </div>
          </div>
        </ng-container>

        <!-- Text block -->
        <div class="control-group control-group-full" *ngIf="block.type === 'text'">
          <label>Text</label>
          <textarea rows="3" [(ngModel)]="block.text" (ngModelChange)="onPopupFormChange()" placeholder="Paragraph text — line breaks are kept."></textarea>
        </div>

        <!-- Key-value block -->
        <ng-container *ngIf="block.type === 'keyValue'">
          <div class="control-row dvm-popup-pair-row" *ngFor="let pair of block.pairs; let pi = index">
            <div class="control-group">
              <label *ngIf="pi === 0">Label</label>
              <input type="text" [(ngModel)]="pair.label" (ngModelChange)="onPopupFormChange()" placeholder="e.g. Last updated by" />
            </div>
            <div class="control-group">
              <label *ngIf="pi === 0">Value</label>
              <input type="text" [(ngModel)]="pair.value" (ngModelChange)="onPopupFormChange()" placeholder="e.g. Finance team" />
            </div>
            <button type="button" class="dvm-popup-mini-btn dvm-popup-row-remove is-danger" (click)="removePopupPair(block, pi)" matTooltip="Remove row"><mat-icon>close</mat-icon></button>
          </div>
          <button type="button" mat-stroked-button class="dvm-popup-add-row" (click)="addPopupPair(block)"><mat-icon>add</mat-icon> Add row</button>
        </ng-container>

        <!-- Table block -->
        <ng-container *ngIf="block.type === 'table'">
          <div class="control-group control-group-full">
            <label>Dataset</label>
            <ng-select
              #ssPopupBlockDataset
              class="ss-select"
              [class.is-open]="!!ssPopupBlockDataset.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="entityOptions"
              [loading]="entitiesLoading"
              [clearable]="true"
              [searchable]="true"
              placeholder="Table/entity whose rows the popup shows"
              [(ngModel)]="block.dataset"
              (ngModelChange)="onPopupBlockDatasetChange(block)"
            ></ng-select>
          </div>
          <div class="control-group control-group-full">
            <label>Columns</label>
            <ng-select
              #ssPopupBlockColumns
              class="ss-select ss-select--multi ss-select--full"
              [class.is-open]="!!ssPopupBlockColumns.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="popupBlockColumnOptions(block)"
              [multiple]="true"
              [closeOnSelect]="false"
              [hideSelected]="false"
              [loading]="isDatasetColumnsLoading(block.dataset)"
              [clearable]="true"
              [searchable]="true"
              placeholder="Empty = all columns"
              [(ngModel)]="block.columns" (ngModelChange)="onPopupFormChange()"
            ></ng-select>
          </div>
          <div class="control-row">
            <div class="control-group">
              <label>Max rows</label>
              <input type="number" min="1" [(ngModel)]="block.maxRows" (ngModelChange)="onPopupFormChange()" placeholder="50" />
            </div>
          </div>
        </ng-container>

        <!-- API table block — rows fetched from an API when the popup opens;
             optional grouped (change-history) rendering with colored values. -->
        <ng-container *ngIf="block.type === 'apiTable'">
          <div class="control-group control-group-full">
            <label>API URL</label>
            <input type="text" [(ngModel)]="block.url" (ngModelChange)="onPopupFormChange()" placeholder="https://… or /path (resolved against apiEndpoints.baseUrl)" />
          </div>
          <div class="control-row">
            <div class="control-group">
              <label>Method</label>
              <ng-select
                #ssPopupApiMethod
                class="ss-select"
                [class.is-open]="!!ssPopupApiMethod.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="popupApiMethods"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [searchable]="false"
                [(ngModel)]="block.method"
                (ngModelChange)="onPopupFormChange()"
              ></ng-select>
            </div>
            <div class="control-group dvm-popup-checkbox">
              <label class="checkbox-label">
                <input type="checkbox" [(ngModel)]="block.refreshOnOpen" (ngModelChange)="onPopupFormChange()" />
                Refresh on every open
              </label>
            </div>
          </div>
          <div class="control-group control-group-full" *ngIf="block.method === 'POST'">
            <label>Request body (JSON)</label>
            <textarea rows="3" [(ngModel)]="block.body" (ngModelChange)="onPopupFormChange()" placeholder='e.g. {"customerId": 463396}'></textarea>
          </div>
          <div class="control-row">
            <div class="control-group">
              <label>Groups path (optional)</label>
              <input type="text" [(ngModel)]="block.groupsPath" (ngModelChange)="onPopupFormChange()" placeholder="e.g. data.history" />
              <small class="control-hint">Dot-path to a LIST of groups — each renders as a collapsible section with its own table (change-history style). Leave empty for one flat table.</small>
            </div>
            <div class="control-group" *ngIf="block.groupsPath">
              <label>Rows field inside each group</label>
              <input type="text" [(ngModel)]="block.groupRowsField" (ngModelChange)="onPopupFormChange()" placeholder="e.g. changes" />
            </div>
            <div class="control-group" *ngIf="!block.groupsPath">
              <label>Rows path (optional)</label>
              <input type="text" [(ngModel)]="block.rowsPath" (ngModelChange)="onPopupFormChange()" placeholder="e.g. data.rows — empty auto-detects" />
            </div>
          </div>

          <ng-container *ngIf="block.groupsPath">
            <label class="dvm-popup-subhead">Group header fields</label>
            <div class="control-row dvm-popup-pair-row" *ngFor="let meta of block.metaFields; let mi = index">
              <div class="control-group">
                <label *ngIf="mi === 0">Response field</label>
                <input type="text" [(ngModel)]="meta.field" (ngModelChange)="onPopupFormChange()" placeholder="e.g. updatedBy" />
              </div>
              <div class="control-group">
                <label *ngIf="mi === 0">Display label</label>
                <input type="text" [(ngModel)]="meta.label" (ngModelChange)="onPopupFormChange()" placeholder="e.g. Updated By" />
              </div>
              <button type="button" class="dvm-popup-mini-btn dvm-popup-row-remove is-danger" (click)="removePopupMetaField(block, mi)" matTooltip="Remove field"><mat-icon>close</mat-icon></button>
            </div>
            <button type="button" mat-stroked-button class="dvm-popup-add-row" (click)="addPopupMetaField(block)"><mat-icon>add</mat-icon> Add header field</button>
          </ng-container>

          <label class="dvm-popup-subhead">Columns</label>
          <small class="control-hint">Leave empty to show every field of the response rows. A value color tints that column's values (and adds it to the legend).</small>
          <div class="dvm-popup-button-row" *ngFor="let col of block.apiColumns; let ci = index">
            <div class="control-row">
              <div class="control-group">
                <label *ngIf="ci === 0">Response field</label>
                <input type="text" [(ngModel)]="col.field" (ngModelChange)="onPopupFormChange()" placeholder="e.g. previousValue" />
              </div>
              <div class="control-group">
                <label *ngIf="ci === 0">Header label</label>
                <input type="text" [(ngModel)]="col.label" (ngModelChange)="onPopupFormChange()" placeholder="e.g. Previous Value" />
              </div>
              <div class="control-group">
                <label *ngIf="ci === 0">Value color</label>
                <ng-select
                  #ssPopupApiColColor
                  class="ss-select"
                  [class.is-open]="!!ssPopupApiColColor.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="popupValueColors"
                  bindLabel="label"
                  bindValue="value"
                  [clearable]="true"
                  [searchable]="false"
                  [(ngModel)]="col.color"
                  (ngModelChange)="onPopupFormChange()"
                ></ng-select>
              </div>
              <button type="button" class="dvm-popup-mini-btn dvm-popup-row-remove is-danger" (click)="removePopupApiColumn(block, ci)" matTooltip="Remove column"><mat-icon>close</mat-icon></button>
            </div>
          </div>
          <button type="button" mat-stroked-button class="dvm-popup-add-row" (click)="addPopupApiColumn(block)"><mat-icon>add</mat-icon> Add column</button>

          <div class="control-row">
            <div class="control-group">
              <label>Max rows</label>
              <input type="number" min="1" [(ngModel)]="block.maxRows" (ngModelChange)="onPopupFormChange()" placeholder="All" />
            </div>
          </div>
        </ng-container>

        <!-- Buttons block -->
        <ng-container *ngIf="block.type === 'buttons'">
          <div class="dvm-popup-button-row" *ngFor="let btn of block.buttons; let bi = index">
            <div class="control-row">
              <div class="control-group">
                <label *ngIf="bi === 0">Button label</label>
                <input type="text" [(ngModel)]="btn.label" (ngModelChange)="onPopupFormChange()" placeholder="e.g. View details" />
              </div>
              <div class="control-group">
                <label *ngIf="bi === 0">Action</label>
                <ng-select
                  #ssPopupBtnAction
                  class="ss-select"
                  [class.is-open]="!!ssPopupBtnAction.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="popupButtonActions"
                  bindLabel="label"
                  bindValue="value"
                  [clearable]="true"
                  [searchable]="false"
                  [(ngModel)]="btn.action" (ngModelChange)="onPopupFormChange()"
                ></ng-select>
              </div>
              <div class="control-group">
                <label *ngIf="bi === 0">Style</label>
                <ng-select
                  #ssPopupBtnStyle
                  class="ss-select"
                  [class.is-open]="!!ssPopupBtnStyle.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="popupButtonStyles"
                  bindLabel="label"
                  bindValue="value"
                  [clearable]="true"
                  [searchable]="false"
                  [(ngModel)]="btn.style" (ngModelChange)="onPopupFormChange()"
                ></ng-select>
              </div>
              <button type="button" class="dvm-popup-mini-btn dvm-popup-row-remove is-danger" (click)="removePopupButton(block, bi)" matTooltip="Remove button"><mat-icon>close</mat-icon></button>
            </div>
            <div class="control-group control-group-full" *ngIf="btn.action === 'link'">
              <label>Link URL</label>
              <input type="text" [(ngModel)]="btn.url" (ngModelChange)="onPopupFormChange()" placeholder="https://…" />
            </div>
            <div class="control-group control-group-full" *ngIf="btn.action === 'navigate'">
              <label>Target dashboard</label>
              <ng-select
                #ssPopupBtnDashboard
                class="ss-select"
                [class.is-open]="!!ssPopupBtnDashboard.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="tmNavTargetDashboards"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [searchable]="true"
                placeholder="Pick a saved/published dashboard"
                [(ngModel)]="btn.targetDashboard" (ngModelChange)="onPopupFormChange()"
              ></ng-select>
            </div>
          </div>
          <button type="button" mat-stroked-button class="dvm-popup-add-row" (click)="addPopupButton(block)"><mat-icon>add</mat-icon> Add button</button>
        </ng-container>
      </div>

      <button type="button" mat-stroked-button class="dvm-popup-add-block" (click)="addPopupBlock()"><mat-icon>add</mat-icon> Add content block</button>

      <header class="wf-section-header">
        <h3>{{ layoutSectionTitle('popupBehaviour', 'Popup behaviour') }}</h3>
      </header>
      <div class="control-row">
        <div class="control-group">
          <label>{{ fieldLabel('popupBehaviour', 'popupSize', 'Size') }}</label>
          <ng-select
            #ssPopupSize
            class="ss-select"
            [class.is-open]="!!ssPopupSize.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="popupSizes"
            bindLabel="label"
            bindValue="value"
            [clearable]="true"
            [searchable]="false"
            [(ngModel)]="popupSize" (ngModelChange)="onPopupFormChange()"
          ></ng-select>
        </div>
        <div class="control-group">
          <label>{{ fieldLabel('popupBehaviour', 'popupPosition', 'Position') }}</label>
          <ng-select
            #ssPopupPosition
            class="ss-select"
            [class.is-open]="!!ssPopupPosition.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="popupPositions"
            bindLabel="label"
            bindValue="value"
            [clearable]="true"
            [searchable]="false"
            [(ngModel)]="popupPosition" (ngModelChange)="onPopupFormChange()"
          ></ng-select>
        </div>
      </div>
      <div class="control-row">
        <div class="control-group">
          <label>{{ fieldLabel('popupBehaviour', 'closeLabel', 'Close button label') }}</label>
          <input type="text" [(ngModel)]="popupCloseLabel" (ngModelChange)="onPopupFormChange()" placeholder="Empty hides the footer button" />
        </div>
        <div class="control-group dvm-popup-checkbox">
          <label class="checkbox-label">
            <input type="checkbox" [(ngModel)]="popupDismissOnBackdrop" (ngModelChange)="onPopupFormChange()" />
            {{ fieldLabel('popupBehaviour', 'dismissOnBackdrop', 'Close on backdrop click') }}
          </label>
        </div>
      </div>

      <!-- Formatting accordion — trigger colors/radius + popup-wide font
           settings. Same collapsed-accordion pattern as the Dropdown form. -->
      <div class="dvm-format-group" [class.is-open]="popupFormatAccordionOpen">
        <button type="button" class="dvm-format-group-head" (click)="popupFormatAccordionOpen = !popupFormatAccordionOpen">
          <span>{{ layoutSectionTitle('popupStyle', 'Formatting') }}</span>
          <mat-icon>{{ popupFormatAccordionOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
        </button>
        <div class="dvm-format-group-body" *ngIf="popupFormatAccordionOpen">
          <small class="control-hint">{{ layoutSectionHint('popupStyle', 'Trigger colors plus popup font settings. Leave unset to keep the theme defaults.') }}</small>

          <div class="control-group control-group-full">
            <label>{{ fieldLabel('popupStyle', 'triggerBg', 'Trigger background') }}</label>
            <input type="color" [(ngModel)]="popupTriggerBg" (ngModelChange)="onPopupFormChange()" />
          </div>
          <div class="control-group control-group-full">
            <label>{{ fieldLabel('popupStyle', 'triggerText', 'Trigger text color') }}</label>
            <input type="color" [(ngModel)]="popupTriggerText" (ngModelChange)="onPopupFormChange()" />
          </div>
          <div class="control-group control-group-full">
            <label>{{ fieldLabel('popupStyle', 'triggerRadius', 'Corner radius (px)') }}</label>
            <input type="number" min="0" max="30" [(ngModel)]="popupTriggerRadius" (ngModelChange)="onPopupFormChange()" placeholder="Default" />
          </div>
          <div class="control-group control-group-full">
            <label>{{ fieldLabel('popupStyle', 'cardHeaderBg', 'Card header background') }}</label>
            <input type="color" [(ngModel)]="popupCardHeaderBg" (ngModelChange)="onPopupFormChange()" />
          </div>
          <div class="control-group control-group-full">
            <label>{{ fieldLabel('popupStyle', 'cardHeaderText', 'Card header text color') }}</label>
            <input type="color" [(ngModel)]="popupCardHeaderText" (ngModelChange)="onPopupFormChange()" />
          </div>
          <div class="control-group control-group-full">
            <label>{{ fieldLabel('popupStyle', 'headerBg', 'Popup header background') }}</label>
            <input type="color" [(ngModel)]="popupHeaderBg" (ngModelChange)="onPopupFormChange()" />
          </div>
          <div class="control-group control-group-full">
            <label>{{ fieldLabel('popupStyle', 'headerText', 'Popup header text color') }}</label>
            <input type="color" [(ngModel)]="popupHeaderText" (ngModelChange)="onPopupFormChange()" />
          </div>
          <div class="control-group control-group-full">
            <label>{{ fieldLabel('popupStyle', 'bodyBg', 'Popup background') }}</label>
            <input type="color" [(ngModel)]="popupBodyBg" (ngModelChange)="onPopupFormChange()" />
          </div>
          <div class="control-group control-group-full">
            <label>{{ fieldLabel('popupStyle', 'bodyText', 'Popup text color') }}</label>
            <input type="color" [(ngModel)]="popupBodyText" (ngModelChange)="onPopupFormChange()" />
          </div>
          <div class="control-group control-group-full">
            <label>{{ fieldLabel('popupStyle', 'buttonBg', 'Button color') }}</label>
            <input type="color" [(ngModel)]="popupButtonBg" (ngModelChange)="onPopupFormChange()" />
          </div>
          <div class="control-group control-group-full">
            <label>{{ fieldLabel('popupStyle', 'buttonText', 'Button text color') }}</label>
            <input type="color" [(ngModel)]="popupButtonText" (ngModelChange)="onPopupFormChange()" />
          </div>
          <div class="control-group control-group-full">
            <label>{{ fieldLabel('popupStyle', 'buttonRadius', 'Button corner radius (px)') }}</label>
            <input type="number" min="0" max="30" [(ngModel)]="popupButtonRadius" (ngModelChange)="onPopupFormChange()" placeholder="Default" />
          </div>
          <div class="control-group control-group-full">
            <label>{{ fieldLabel('popupStyle', 'popupRadius', 'Popup corner radius (px)') }}</label>
            <input type="number" min="0" max="40" [(ngModel)]="popupRadius" (ngModelChange)="onPopupFormChange()" placeholder="Default" />
          </div>
          <div class="control-group control-group-full">
            <label>{{ fieldLabel('popupStyle', 'fontSize', 'Font size (px)') }}</label>
            <input type="number" min="0" max="48" [(ngModel)]="popupFontSize" (ngModelChange)="onPopupFormChange()" placeholder="Default" />
          </div>
          <div class="control-group control-group-full">
            <label>{{ fieldLabel('popupStyle', 'fontFamily', 'Font family') }}</label>
            <ng-select
              #ssPopupFontFamily
              class="ss-select"
              [class.is-open]="!!ssPopupFontFamily.isOpen()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="popupFontFamilies"
              bindLabel="label"
              bindValue="value"
              [clearable]="true"
              [searchable]="false"
              [(ngModel)]="popupFontFamily" (ngModelChange)="onPopupFormChange()"
            ></ng-select>
          </div>
        </div>
      </div>
    </div>

    <!-- Input controls (INPUT_BOX / SLIDER): a dashboard input that feeds a
         formula/measure value into one or more target charts. -->
    <div class="wf-section" *ngIf="isInputControlLayout">
      <header class="wf-section-header">
        <h3>{{ layoutSectionTitle('input', isSliderLayout ? 'Slider input' : 'Input box') }}</h3>
      </header>
      <small class="control-hint">{{ layoutSectionHint('input', 'This control feeds its value into the target chart(s) selected below.') }}</small>

      <div class="control-group control-group-full" *ngIf="fieldEnabled('input', 'targetCharts')">
        <label>{{ fieldLabel('input', 'targetCharts', 'Target chart(s) / input(s)') }}</label>
        <ng-select
          #ssInputTargets
          class="ss-select ss-select--multi ss-select--full"
          [class.is-open]="!!ssInputTargets.isOpen()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="targetCharts"
          bindLabel="label"
          bindValue="value"
          [multiple]="true"
          [closeOnSelect]="false"
          [hideSelected]="false"
          [clearable]="true"
          [searchable]="true"
          placeholder="Select the chart(s) or input(s) this control drives"
          [(ngModel)]="selectedTargetCharts"
        ></ng-select>
        <small class="control-hint" *ngIf="!targetCharts.length">Add charts or other inputs to the dashboard first — they'll appear here as targets.</small>
      </div>
      <ng-container *ngTemplateOutlet="apiCallConfigBlock; context: { kind: 'input' }"></ng-container>

      <!-- Per-target formula: each selected target gets its own expression fed by
           this control's `value` (e.g. KPI: [price] * value; another input:
           value * 2). A WATERFALL target instead lists one formula per bridge
           step, since each bar is independently calculated. Each row is a drop
           zone — drag a column from the Data pane to add its token. -->
      <div class="control-group control-group-full" *ngIf="selectedTargetCharts.length">
        <label>{{ fieldLabel('input', 'formula', 'Formula per target') }}</label>
        <ng-container *ngFor="let id of selectedTargetCharts">
          <div class="target-formula-group" *ngIf="isWaterfallTarget(id)">
            <span class="target-formula-name" [title]="targetLabelForId(id)">{{ targetLabelForId(id) }} — per bar</span>
            <div class="target-formula-bar" *ngFor="let step of targetSteps(id); trackBy: trackByStepLabel">
              <div
                class="target-formula-row wf-field-drop-zone"
                (dragover)="allowFieldDrop($event)"
                (drop)="onTargetFormulaDrop($event, id, step.label)"
                title="Drag a column here to add it to this bar's formula"
              >
                <span class="target-formula-name" [title]="step.label">{{ step.label || step.stepType }}</span>
                <input
                  type="text"
                  [ngModel]="targetStepFormula(id, step.label)"
                  (ngModelChange)="setTargetStepFormula(id, step.label, $event)"
                  placeholder="e.g. [price] * value"
                />
              </div>

              <!-- This bar's own references — same [Token] mechanism as below,
                   scoped to THIS bar's formula only. -->
              <ng-container *ngIf="formulaReferencesEnabledFor('WATERFALL')">
                <ng-container
                  *ngTemplateOutlet="referenceList; context: { id: id, step: step.label }"
                ></ng-container>
              </ng-container>
            </div>
          </div>
          <div *ngIf="!isWaterfallTarget(id)">
            <div
              class="target-formula-row wf-field-drop-zone"
              (dragover)="allowFieldDrop($event)"
              (drop)="onTargetFormulaDrop($event, id)"
              title="Drag a column here to add it to this target's formula"
            >
              <span class="target-formula-name" [title]="targetLabelForId(id)">{{ targetLabelForId(id) }}</span>
              <input
                type="text"
                [ngModel]="targetFormulas[id] || ''"
                (ngModelChange)="targetFormulas[id] = $event"
                [placeholder]="isInputControlTargetId(id) ? 'value (default — passes this control’s value through)' : 'e.g. [price] * value'"
              />
            </div>
            <!-- A CHART target with no formula is silently skipped by the
                 recompute (nothing to calculate) — surface that instead. -->
            <small class="control-warning" *ngIf="!isInputControlTargetId(id) && !(targetFormulas[id] || '').trim()">
              No formula yet — "{{ targetLabelForId(id) }}" will NOT update when this control changes. Add one, e.g. [{{ targetLabelForId(id) }}]*(1+(value/100))
            </small>

            <!-- References: when a formula's [Token] isn't a plain dataset
                 column but another input's live/recomputed output (e.g. a row
                 inside a different target chart), type in where it actually
                 comes from so the backend resolves the CURRENT value instead
                 of a stale one. Fully typed by hand — no drag-and-drop. -->
            <ng-container *ngIf="formulaReferencesEnabledFor(targetLayoutTypeForId(id))">
              <ng-container
                *ngTemplateOutlet="referenceList; context: { id: id }"
              ></ng-container>
            </ng-container>
          </div>
        </ng-container>
      </div>

      <div class="control-row" *ngIf="isSliderLayout && (fieldEnabled('input', 'min') || fieldEnabled('input', 'max') || fieldEnabled('input', 'step'))">
        <div class="control-group" *ngIf="fieldEnabled('input', 'min')">
          <label>{{ fieldLabel('input', 'min', 'Min') }}</label>
          <input type="number" [(ngModel)]="sliderMin" />
        </div>
        <div class="control-group" *ngIf="fieldEnabled('input', 'max')">
          <label>{{ fieldLabel('input', 'max', 'Max') }}</label>
          <input type="number" [(ngModel)]="sliderMax" />
        </div>
        <div class="control-group" *ngIf="fieldEnabled('input', 'step')">
          <label>{{ fieldLabel('input', 'step', 'Step') }}</label>
          <input type="number" [(ngModel)]="sliderStep" />
        </div>
      </div>

      <div class="control-group control-group-full" *ngIf="fieldEnabled('input', 'defaultValue')">
        <label>{{ fieldLabel('input', 'defaultValue', 'Default value') }}</label>
        <input
          type="number"
          [(ngModel)]="inputDefaultValue"
          [placeholder]="isSliderLayout ? 'Handle start position (optional)' : 'Pre-filled value (optional)'"
        />
      </div>
      <!-- No Format accordion here by design: Slider / Input Box tiles don't
           take card-header formatting (builder or published). -->
    </div>

    <!-- Chart interactions: besides filterKey/filterListeners, a CHART can name
         the component(s) it interacts with. Persisted as payload.targets (same
         shape the input controls use, minus per-target formulas) — also
         editable later from the tile header's link icon. OPTIONAL, so it lives
         in a collapsed accordion (steps/axes stay always visible — those are
         required); the badge shows how many targets are wired. -->
    <div class="tm-nav-config" *ngIf="isChartTargetLayout">
      <div class="tm-nav-head" (click)="interactionsSectionOpen = !interactionsSectionOpen">
        <mat-icon>{{ interactionsSectionOpen ? 'expand_more' : 'chevron_right' }}</mat-icon>
        <span>{{ layoutSectionTitle('interactions', 'Chart interactions') }}</span>
        <span class="tm-nav-badge" *ngIf="selectedTargetCharts.length">{{ selectedTargetCharts.length }}</span>
      </div>
      <div class="tm-nav-body" *ngIf="interactionsSectionOpen">
        <small class="control-hint">
          {{ layoutSectionHint('interactions', 'Select the chart(s)/input(s) this chart interacts with — its interactions are sent to these targets.') }}
        </small>
        <div class="control-group control-group-full">
          <label>{{ fieldLabel('interactions', 'targetCharts', 'Target chart(s) / input(s)') }}</label>
          <ng-select
            #ssChartTargets
            class="ss-select ss-select--multi ss-select--full"
            [class.is-open]="!!ssChartTargets.isOpen()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="targetCharts"
            bindLabel="label"
            bindValue="value"
            [multiple]="true"
            [closeOnSelect]="false"
            [hideSelected]="false"
            [clearable]="true"
            [searchable]="true"
            placeholder="Select target chart(s)/input(s)"
            [(ngModel)]="selectedTargetCharts"
          ></ng-select>
          <small class="control-hint" *ngIf="!targetCharts.length">Add more charts or inputs to the dashboard first — they'll appear here as targets.</small>
        </div>
      </div>
    </div>

    <!-- Drill-Down: its format accordions (Formatting / Columns / Drill-Down)
         render HERE — below the Hierarchy dimensions / Measures / Chart-
         interactions block — so the required hierarchy mapping stays at the top
         and the optional formatting sits after the interactions accordion. -->
    <!-- Notes: Drill-Down tables AND every non-table chart (Bar / Line / Waterfall /
         Scatter / KPI / …) — flat tables render the same block higher up. -->
    <ng-container *ngIf="supportsTileNotes && (!isTableMatrixLayout || isTmDrilldown)" [ngTemplateOutlet]="tmNotesConfig"></ng-container>
    <ng-container *ngIf="isTableMatrixLayout && isTmDrilldown" [ngTemplateOutlet]="chartFormatAccordion"></ng-container>

    <!-- "Notes" accordion (every chart + table type) — a heading line + one
         note per text line, drawn UNDER the card, outside it (tile.tileNotes).
         Changes push into the edited tile live. -->
    <ng-template #tmNotesConfig>
      <div class="tm-nav-config tm-notes-config">
        <div class="tm-nav-head" (click)="toggleTmNotesAccordion()">
          <mat-icon>{{ tmNotesAccordionOpen ? 'expand_more' : 'chevron_right' }}</mat-icon>
          <span>Notes</span>
          <span class="tm-nav-badge" *ngIf="tmNotesEnabled">on</span>
        </div>
        <div class="tm-nav-body" *ngIf="tmNotesAccordionOpen">
          <label class="dvm-format-toggle tm-nav-enable">
            <input type="checkbox" [ngModel]="tmNotesEnabled" (ngModelChange)="tmNotesEnabled = $event; onTileNotesChange()" />
            Show notes with this {{ isTableMatrixLayout ? 'table' : 'chart' }}
          </label>

          <ng-container *ngIf="tmNotesEnabled">
            <div class="control-group">
              <label>Heading (optional)</label>
              <input type="text" [ngModel]="tmNotesHeading" (ngModelChange)="tmNotesHeading = $event; onTileNotesChange()" placeholder="Note: Price increase modelled at:" />
            </div>

            <div class="control-group">
              <label>Notes (one per line)</label>
              <textarea rows="4" [ngModel]="tmNotesText" (ngModelChange)="tmNotesText = $event; onTileNotesChange()" placeholder="85% realization for Mining, Oil&amp;Gas and Rail&#10;80% realization on Marine Engines"></textarea>
            </div>

            <div class="control-row">
              <div class="control-group">
                <label>Line marker</label>
                <ng-select
                  #ssTmNotesStyle
                  class="ss-select"
                  [class.is-open]="!!ssTmNotesStyle.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="tmNotesListStyles"
                  bindLabel="label"
                  bindValue="value"
                  [clearable]="false"
                  [searchable]="false"
                  [ngModel]="tmNotesListStyle"
                  (ngModelChange)="tmNotesListStyle = $event || 'bullet'; onTileNotesChange()"
                ></ng-select>
              </div>
              <div class="control-group">
                <label>Position</label>
                <ng-select
                  #ssTmNotesPos
                  class="ss-select"
                  [class.is-open]="!!ssTmNotesPos.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="tmNotesPositions"
                  bindLabel="label"
                  bindValue="value"
                  [clearable]="false"
                  [searchable]="false"
                  [ngModel]="tmNotesPosition"
                  (ngModelChange)="tmNotesPosition = $event || 'below'; onTileNotesChange()"
                ></ng-select>
              </div>
            </div>
          </ng-container>
        </div>
      </div>
    </ng-template>

    <div class="wf-generate-row">
      <!-- The span carries the tooltip: a disabled <button> swallows pointer
           events, so a title on the button itself would never show. -->
      <span class="wf-generate-button-wrap" [title]="generateBlockerText">
        <button type="button" class="primary-action" [disabled]="!canGenerate" (click)="generateConfiguration()">
          {{ isGenerating ? (editingTileId ? 'Updating...' : 'Generating...') : (editingTileId ? 'Update Configuration' : 'Generate Configuration') }}
        </button>
      </span>
      <!-- Say WHAT is missing — the greyed-out button alone gives the user no
           way to work out which required field is still empty. -->
      <div class="message wf-generate-missing" *ngIf="!canGenerate && !isGenerating && generateBlockers.length">
        <strong>Still needed:</strong>
        <ul>
          <li *ngFor="let item of generateBlockers">{{ item }}</li>
        </ul>
      </div>
      <div class="message warning-message" *ngIf="generateError">{{ generateError }}</div>
    </div>
    </div>
  </aside>

  <div class="pane-resizer steps-pane-resizer" [class.is-disabled]="isDividerDisabled(0)" (mousedown)="startDividerResize(0, $event)" aria-hidden="true"></div>

  <!-- ══════════════════ RIGHT: dynamic preview canvas ══════════════════ -->
  <!-- ══════════════════ RIGHT: dashboard preview canvas ══════════════════ -->
  <div
    class="dvm-layout-preview-panel"
    [class.is-collapsed]="previewPanelCollapsed"
    [class.is-fullscreen]="isDashboardFullscreen"
    [style.grid-column]="paneGridColumn('preview')"
    (dragover)="onPaneDragOver($event)"
    (drop)="onPaneDrop('preview', $event)"
  >
    <button
      *ngIf="previewPanelCollapsed"
      type="button"
      class="collapsed-preview-reopen"
      mat-icon-button
      (mousedown)="$event.stopPropagation()"
      (click)="$event.stopPropagation(); previewPanelCollapsed = false"
      matTooltip="Open dashboard"
      aria-label="Open dashboard panel"
    ><mat-icon>{{ paneCollapseIcon('preview') }}</mat-icon></button>
    <div class="wf-preview-header">
      <span *ngIf="!isDashboardFullscreen" class="pane-drag-handle preview-drag-grip" draggable="true" (dragstart)="onPaneDragStart('preview', $event)" (dragend)="onPaneDragEnd()" matTooltip="Drag to reorder panel"><mat-icon>drag_indicator</mat-icon></span>
      <div class="dvm-dashboard-titlebar">
        <span class="wf-section-kicker">Dashboard</span>
        <input class="dvm-dashboard-name" type="text" [(ngModel)]="dashboardName" placeholder="Untitled dashboard" aria-label="Dashboard name" />
      </div>
      <span class="wf-step-count dvm-tile-count">{{ dashboardTiles.length }} chart{{ dashboardTiles.length === 1 ? '' : 's' }}</span>

      <div class="dvm-preview-actions">
        <!-- Full-screen preview: a single Back button to return to the builder. -->
        <button *ngIf="isDashboardFullscreen" type="button" class="add-step-button dvm-back-button" mat-flat-button color="primary" (click)="toggleDashboardFullscreen()" matTooltip="Back to builder">
          <mat-icon>arrow_back</mat-icon> Back
        </button>
        <button *ngIf="isDashboardFullscreen" type="button" class="add-step-button" mat-stroked-button [class.is-active]="previewEditLayout" (click)="previewEditLayout = !previewEditLayout" [matTooltip]="previewEditLayout ? 'Done editing layout' : 'Rearrange / resize the tiles'">
          <mat-icon>{{ previewEditLayout ? 'done' : 'dashboard_customize' }}</mat-icon> {{ previewEditLayout ? 'Done' : 'Edit Layout' }}
        </button>
        <!-- <button *ngIf="isDashboardFullscreen" type="button" class="add-step-button" mat-stroked-button [disabled]="!canSaveDashboard" (click)="saveDashboard()" matTooltip="Save this dashboard draft">
          <mat-icon>save</mat-icon> {{ isSavingDashboard ? 'Saving…' : 'Save' }}
        </button> -->
        <button *ngIf="isDashboardFullscreen" type="button" class="add-step-button dvm-publish-button" mat-flat-button color="primary" [disabled]="!canPublish" (click)="publishDashboard()" [matTooltip]="isExistingPublishedDashboard ? 'Update the published dashboard' : 'Publish the dashboard to the left menu'">
          <mat-icon>{{ isExistingPublishedDashboard ? 'sync' : 'publish' }}</mat-icon> {{ publishButtonLabel }}
        </button>

        <ng-container *ngIf="!isDashboardFullscreen">
          <button type="button" class="add-step-button" mat-stroked-button (click)="startNewChart()" matTooltip="Clear the builder to start a new chart"><mat-icon>add</mat-icon> New chart</button>
          <button type="button" class="add-step-button" mat-stroked-button [disabled]="!dashboardTiles.length && !dashboardName.trim()" (click)="startNewDashboard()" matTooltip="Clear all charts and start a fresh dashboard"><mat-icon>note_add</mat-icon> New dashboard</button>
          <button type="button" class="add-step-button" mat-stroked-button (click)="openLandingPageBuilder()" matTooltip="Create a landing page from a predefined template"><mat-icon>web</mat-icon> Landing page</button>
          <button type="button" class="add-step-button" mat-stroked-button [disabled]="dashboardTiles.length < 2" [class.is-active]="groupSelectMode" (click)="toggleGroupSelectMode()" [matTooltip]="groupSelectMode ? 'Cancel selecting charts to group' : 'Select charts to club into one card'">
            <mat-icon>{{ groupSelectMode ? 'close' : 'select_all' }}</mat-icon> {{ groupSelectMode ? 'Cancel' : 'Group charts' }}
          </button>
          <button *ngIf="groupSelectMode" type="button" class="add-step-button dvm-publish-button" mat-flat-button color="primary" [disabled]="!canGroupSelectedTiles" (click)="groupSelectedTiles()" matTooltip="Club the selected charts into one titled card">
            <mat-icon>dashboard_customize</mat-icon> Group ({{ selectedTileIds.size }})
          </button>
          <!-- <button type="button" class="add-step-button" mat-stroked-button [disabled]="!dashboardTiles.length" (click)="toggleDashboardFullscreen()" matTooltip="Preview full screen">
            <mat-icon>fullscreen</mat-icon> Preview
          </button> -->
          <!-- <button type="button" class="add-step-button" mat-stroked-button [disabled]="!canSaveDashboard" (click)="saveDashboard()" matTooltip="Save this dashboard draft">
            <mat-icon>save</mat-icon> {{ isSavingDashboard ? 'Saving…' : 'Save' }}
          </button> -->
          <button type="button" class="add-step-button dvm-publish-button" mat-flat-button color="primary" [disabled]="!canPublish" (click)="publishDashboard()" [matTooltip]="isExistingPublishedDashboard ? 'Update the published dashboard' : 'Publish the dashboard to the left menu'">
            <mat-icon>{{ isExistingPublishedDashboard ? 'sync' : 'publish' }}</mat-icon> {{ publishButtonLabel }}
          </button>
          <button
            type="button"
            class="panel-collapse-button preview-collapse-button"
            [class.is-hidden-when-collapsed]="previewPanelCollapsed"
            mat-icon-button
            (click)="previewPanelCollapsed = !previewPanelCollapsed"
            [matTooltip]="previewPanelCollapsed ? 'Open dashboard' : 'Collapse dashboard'"
            [attr.aria-label]="previewPanelCollapsed ? 'Open dashboard panel' : 'Collapse dashboard panel'"
          ><mat-icon>{{ paneCollapseIcon('preview') }}</mat-icon></button>
        </ng-container>
      </div>
    </div>

    <div class="preview-panel-content panel-content">
      <div class="dvm-dashboard-canvas" #dashboardCanvas>
        <!-- Active cross-filter chip: shows what a bar-click filtered the other
             tiles by, with a Clear button to reset. -->
        <div class="dvm-crossfilter-chip" *ngIf="activeCrossFilter">
          <mat-icon>filter_alt</mat-icon>
          <span>Filtered: <strong>{{ activeCrossFilter.column }}</strong> = <strong>{{ activeCrossFilter.values.join(', ') }}</strong></span>
          <button type="button" mat-icon-button matTooltip="Clear filter" (click)="clearCrossFilter()"><mat-icon>close</mat-icon></button>
        </div>

        <div class="empty-state dvm-canvas-empty" *ngIf="!dashboardTiles.length">
          <div class="empty-state-icon"><mat-icon>dashboard</mat-icon></div>
          <p>Name your dashboard, build a chart on the left,<br />and click <strong>Generate Configuration</strong><br />to add it here. Drag a tile to reorder, drag a corner to resize.</p>
        </div>

        <!-- Reusable Format popup body (JSON-driven controls) — used for a normal
             top-level tile AND for each member inside a GROUP card, so a grouped
             chart keeps its own independent Format popup, same as before. -->
        <ng-template #formatPopup let-tile="tile">
          <div class="dvm-format-popup-head">
            <span>Format</span>
            <button type="button" mat-icon-button (click)="toggleTileFormat(tile, $event)" aria-label="Close"><mat-icon>close</mat-icon></button>
          </div>
          <div class="dvm-format-popup-body">
            <!-- Accordion sections: Title / Axis / Tooltip / Formatting / Advanced
                 (grouped by each control's `group`, JSON-driven). -->
            <div class="dvm-format-group" *ngFor="let group of formatGroupsFor(tile); let gi = index" [class.is-open]="isFormatGroupOpen(tile, group.name, gi)">
              <button type="button" class="dvm-format-group-head" (click)="toggleFormatGroup(tile, group.name, gi)">
                <span>{{ group.name }}</span>
                <mat-icon>{{ isFormatGroupOpen(tile, group.name, gi) ? 'expand_less' : 'expand_more' }}</mat-icon>
              </button>

              <div class="dvm-format-group-body" *ngIf="isFormatGroupOpen(tile, group.name, gi)">
                <ng-container *ngFor="let control of group.controls">
                <div class="dvm-format-row" *ngIf="formatControlVisible(control, tile.format)">
                  <label>{{ control.label }}</label>

                  <!-- CRUD endpoints: each gets its OWN request-body editor, right
                       beside its label, so every URL can post a different contract. -->
                  <button
                    type="button"
                    class="dvm-pt-open-btn"
                    *ngIf="control.requestBodyEditor"
                    [class.is-set]="hasPayloadTemplate(tile.format, control.key)"
                    [matTooltip]="'Configure the request body posted to this endpoint'"
                    (click)="openPayloadTemplateModalForTile(tile, control, $event)"
                  >{{ hasPayloadTemplate(tile.format, control.key) ? 'Body ✓' : 'Body' }}</button>

                  <input
                    *ngIf="control.control === 'text'"
                    type="text"
                    [ngModel]="tile.format[control.key]"
                    (ngModelChange)="tile.format[control.key] = $event; onTileFormatChange(tile)"
                    [placeholder]="control.key === 'title' ? tile.title : (control.placeholder || '')"
                  />

                  <input
                    *ngIf="control.control === 'number'"
                    type="number"
                    [min]="control.min ?? null"
                    [max]="control.max ?? null"
                    [step]="control.step ?? 1"
                    [ngModel]="tile.format[control.key]"
                    (ngModelChange)="tile.format[control.key] = $event; onTileFormatChange(tile)"
                  />

                  <input
                    *ngIf="control.control === 'color'"
                    type="color"
                    [ngModel]="tile.format[control.key]"
                    (ngModelChange)="tile.format[control.key] = $event; onTileFormatChange(tile)"
                  />

                  <label *ngIf="control.control === 'toggle'" class="dvm-format-toggle">
                    <input
                      type="checkbox"
                      [ngModel]="tile.format[control.key]"
                      (ngModelChange)="tile.format[control.key] = $event; onTileFormatChange(tile)"
                    />
                  </label>

                  <ng-select
                    *ngIf="control.control === 'select'"
                    class="ss-select"
                    appendTo="body"
                    ngClass="ss-dropdown-panel"
                    [items]="formatControlOptions(control, tile)"
                    bindLabel="label"
                    bindValue="value"
                    [clearable]="true"
                    [ngModel]="tile.format[control.key]"
                    (ngModelChange)="tile.format[control.key] = $event ?? ''; onTileFormatChange(tile); openFormatTileId = null"
                  ></ng-select>

                  <ng-select
                    *ngIf="control.control === 'column-select'"
                    class="ss-select"
                    appendTo="body"
                    ngClass="ss-dropdown-panel"
                    [items]="formatControlOptions(control, tile)"
                    bindLabel="label"
                    bindValue="value"
                    [clearable]="true"
                    [ngModel]="tile.format[control.key]"
                    (ngModelChange)="tile.format[control.key] = $event || ''; onTileFormatChange(tile); openFormatTileId = null"
                  ></ng-select>

                  <ng-select
                    *ngIf="control.control === 'multi-select'"
                    class="ss-select ss-multi-select"
                    appendTo="body"
                    ngClass="ss-dropdown-panel"
                    [items]="formatControlOptions(control, tile)"
                    bindLabel="label"
                    bindValue="value"
                    [multiple]="true"
                    [closeOnSelect]="false"
                    [clearable]="true"
                    [addTag]="addTypedValueTag"
                    [placeholder]="control.placeholder || 'Pick from the dataset…'"
                    [ngModel]="multiSelectModel(tile.format, control.key)"
                    (ngModelChange)="tile.format[control.key] = $event || []; onTileFormatChange(tile)"
                  ></ng-select>
                </div>
                </ng-container>
              </div>
            </div>
          </div>
        </ng-template>

        <!-- Notes under a chart/table (builder-form "Notes" accordion →
             tile.tileNotes). A sibling of the tile body, NOT inside it: the tile
             keeps its grid footprint, `.has-tile-notes` moves the card chrome
             onto the header + body, and the notes sit BELOW the card on the page
             background. No per-chart layout changes — the chart just gets shorter. -->
        <ng-template #tileNotesBlock let-tile="tile">
          <div class="dvm-tile-notes" [class.is-above]="notes.position === 'above'" *ngIf="tileNotesOf(tile) as notes">
            <div class="dvm-tile-notes-heading" *ngIf="notes.heading">{{ notes.heading }}</div>
            <ul class="dvm-tile-notes-list" [attr.data-list-style]="notes.listStyle || 'bullet'" *ngIf="notes.items?.length">
              <li *ngFor="let item of notes.items">{{ item }}</li>
            </ul>
          </div>
        </ng-template>

        <!-- Reusable "render this tile's actual chart" block — used for a normal
             top-level tile AND for each member inside a GROUP card, so grouping
             never needs a second copy of the per-type render logic. -->
        <ng-template #tileBody let-tile="tile">
          <!-- Waterfall -->
          <app-waterfall-chart *ngIf="tile.waterfallData" class="dvm-tile-chart" [data]="tile.waterfallData" (crossFilterChange)="onTileCrossFilter(tile, $event)"></app-waterfall-chart>

          <!-- Drill-down table -->
          <lib-drill-down *ngIf="tile.drillData" [data]="tile.drillData"></lib-drill-down>

          <!-- Table & Matrix (Normal / Editable / Drill-Down) -->
          <app-unified-table-matrix-chart
            *ngIf="tile.tableMatrixData"
            class="dvm-tile-chart"
            [data]="tile.tableMatrixData"
            (crossFilterChange)="onTileCrossFilter(tile, $event)"
            (navigate)="onTileNavigate(tile, $event)"
            (refresh)="onTileTableRefresh(tile)"
            (saved)="onTileTableRefresh(tile)"
            (columnOrderChange)="onTileColumnReorder(tile, $event)"
            (save)="onTileTableSave(tile, $event)"
          ></app-unified-table-matrix-chart>

          <!-- Horizontal bar chart -->
          <app-horizontal-bar-chart *ngIf="tile.hbData" [data]="tile.hbData"></app-horizontal-bar-chart>

          <!-- Unified Bar & Column chart (Simple/Grouped/Stacked/100%/Clustered, H/V) -->
          <app-unified-bar-column-chart *ngIf="tile.barData" class="dvm-tile-chart" [data]="tile.barData" (crossFilterChange)="onTileCrossFilter(tile, $event)"></app-unified-bar-column-chart>

          <!-- Unified Line & Area chart (Line/Multi/Step/Spline; Area/Stacked/100%; analytics) -->
          <app-unified-line-area-chart *ngIf="tile.lineAreaData" class="dvm-tile-chart" [data]="tile.lineAreaData" (crossFilterChange)="onTileCrossFilter(tile, $event)"></app-unified-line-area-chart>

          <!-- Unified Scatter & Bubble chart (Scatter / Multi / Bubble / Quadrant) -->
          <app-unified-scatter-bubble-chart *ngIf="tile.scatterBubbleData" class="dvm-tile-chart" [data]="tile.scatterBubbleData" (crossFilterChange)="onTileCrossFilter(tile, $event)"></app-unified-scatter-bubble-chart>

          <!-- KPI card -->
          <app-number-stats-shared *ngIf="tile.kpiData" [data]="tile.kpiData"></app-number-stats-shared>

          <!-- Dataset-backed dropdown input. -->
          <div class="dvm-tile-input" *ngIf="tile.dropdownControl">
            <app-global-dropdown
              [data]="tile.dropdownControl.widgetData"
              [options]="tile.dropdownControl.options"
              [ngModel]="tile.dropdownControl.value"
              (ngModelChange)="onTileDropdownChange(tile, $event)"
            ></app-global-dropdown>
            <small class="control-hint" *ngIf="tile.dropdownControl.targets.length">
              Filters {{ tile.dropdownControl.targets.length }} target(s).
            </small>
          </div>

          <!-- Input control (slider / input box) — reuses the shared widgets. -->
          <div class="dvm-tile-input" *ngIf="tile.inputControl">
            <app-mat-slider
              *ngIf="tile.inputControl.kind === 'SLIDER'"
              [data]="tile.inputControl.widgetData"
              (valueChange)="onTileSliderChange(tile, $event)"
            ></app-mat-slider>
            <app-input-box
              *ngIf="tile.inputControl.kind === 'INPUT_BOX'"
              [data]="tile.inputControl.widgetData"
              (valueChange)="onTileInputValueChange(tile, $event)"
            ></app-input-box>
            <small class="control-hint" *ngIf="tile.inputControl.targets.length">
              Drives {{ tile.inputControl.targets.length }} chart(s) with per-target formulas.
            </small>
          </div>

          <!-- File upload control — reusable uploader; the picked file POSTs to
               compute + drives targets (handled by onTileFileSelected). -->
          <div class="dvm-tile-input dvm-tile-upload" *ngIf="tile.fileUploadControl">
            <app-dvm-file-upload
              [title]="tile.title"
              [buttonLabel]="tile.fileUploadControl.buttonLabel"
              [accept]="tile.fileUploadControl.accept"
              [uploading]="isTileUploading(tile)"
              [message]="tileUploadMessage(tile)"
              [targetCount]="tile.fileUploadControl.targets.length"
              (filePicked)="onTileFilePicked(tile, $event)"
            ></app-dvm-file-upload>
          </div>

          <!-- Popup component — trigger + self-contained overlay dialog. -->
          <div class="dvm-tile-input dvm-tile-popup" *ngIf="tile.popupControl">
            <app-dvm-popup
              [data]="tile.popupControl.widgetData"
              (navigateTo)="onPopupNavigate($event)"
            ></app-dvm-popup>
          </div>
        </ng-template>

        <div
          class="dvm-dashboard-tile"
          [class.is-group-tile]="tile.layoutType === 'GROUP'"
          [class.has-tile-notes]="!!tileNotesOf(tile)"
          [ngStyle]="groupCardStyles(tile)"
          *ngFor="let tile of dashboardTiles; let i = index; trackBy: trackTileById"
          [class.is-drop-target]="tileDragIndex !== null && tileDragIndex !== i"
          [class.is-formatting]="openFormatTileId === tile.id"
          [style.--dvm-card-header-bg]="tileTakesHeaderFormat(tile) ? (tile.format?.['cardHeaderBackground'] || null) : null"
          [style.--dvm-card-header-color]="tileTakesHeaderFormat(tile) ? (tile.format?.['cardHeaderColor'] || null) : null"
          [style.--dvm-card-header-align]="tileTakesHeaderFormat(tile) ? (tile.format?.['titleAlign'] || null) : null"
          [style.--dvm-tile-font-family]="tileFontFamily(tile.format)"
          [style.--dvm-tile-font-size]="tileFontSizePx(tile.format)"
          [class.dvm-tile-no-header-format]="!tileTakesHeaderFormat(tile)"
          [class.is-selectable]="groupSelectMode && tile.layoutType !== 'GROUP'"
          [class.is-selected]="isTileSelected(tile)"
          [style.grid-column]="'span ' + tile.colSpan"
          [style.grid-row]="'span ' + tile.rowSpan"
          (dragover)="onTileReorderOver($event)"
          (drop)="onTileReorderDrop(i, $event)"
          (click)="groupSelectMode && tile.layoutType !== 'GROUP' ? toggleTileSelected(tile) : null"
        >
          <!-- Recompute overlay — shown ONLY on the tile(s) this input control
               targets while its recompute POST is in flight, not the whole canvas. -->
          <div class="dvm-recompute-overlay" *ngIf="isTileRecomputing(tile)">
            <span class="dvm-recompute-spinner" aria-hidden="true"></span>
          </div>
          <!-- Builder chrome (grip / title / type badge / format / remove) is
               hidden in Preview so the tile renders as the clean published card —
               unless "Edit Layout" is toggled on to rearrange/resize the tiles. -->
          <div
            *ngIf="!isDashboardFullscreen || previewEditLayout"
            class="dvm-tile-header"
            draggable="true"
            (dragstart)="onTileReorderStart(i, $event)"
            (dragend)="onTileReorderEnd()"
            matTooltip=""
          >
            <input
              *ngIf="groupSelectMode && tile.layoutType !== 'GROUP'"
              type="checkbox"
              class="dvm-tile-select-check"
              [checked]="isTileSelected(tile)"
              (click)="$event.stopPropagation()"
              (change)="toggleTileSelected(tile)"
              aria-label="Select chart to group"
            />
            <span *ngIf="tile.layoutType !== 'GROUP'" class="dvm-tile-title" [title]="tile.title | capitalCase">{{ tile.title | capitalCase }}</span>
            <input
              *ngIf="tile.layoutType === 'GROUP'"
              type="text"
              class="dvm-tile-title dvm-group-title-input"
              [(ngModel)]="tile.groupTitle"
              (click)="$event.stopPropagation()"
              placeholder="Group card title"
            />
            <!-- Actions overlay the right edge and appear on tile hover, so the
                 title keeps the full header width on narrow tiles. -->
            <div class="dvm-tile-actions">
              <button
                *ngIf="tile.layoutType !== 'GROUP'"
                type="button"
                mat-icon-button
                class="dvm-tile-edit"
                [class.is-active]="editingTileId === tile.id"
                (click)="editTile(tile, $event)"
                matTooltip="Edit chart configuration"
                aria-label="Edit chart configuration"
              >
                <mat-icon>edit</mat-icon>
              </button>
            <!-- Cross-filter listener editor — publishes a filter key + picks which
                 tiles react. Shown for any registered client-side chart tile
                 (registry-driven, so Pie/Line/… get it automatically) plus
                 source-only publishers like the waterfall. -->
            <button
              *ngIf="canConfigureCrossFilter(tile)"
              type="button"
              mat-icon-button
              class="dvm-tile-filter"
              [class.is-active]="expandedListenerTileId === tile.id"
              (click)="toggleListenerEditor(tile, $event)"
              matTooltip="Cross-filter (publishes filter key)"
              aria-label="Cross-filter settings"
            >
              <mat-icon>filter_alt</mat-icon>
            </button>
              <button
                *ngIf="isFormattableTile(tile)"
                type="button"
                mat-icon-button
                class="dvm-tile-format"
                [class.is-active]="openFormatTileId === tile.id"
                (click)="toggleTileFormat(tile, $event)"
                matTooltip="Format chart"
                aria-label="Format chart"
              >
                <mat-icon>tune</mat-icon>
              </button>
              <button
                *ngIf="canEditInteractions(tile)"
                type="button"
                mat-icon-button
                class="dvm-tile-interactions"
                [class.is-active]="openInteractionTileId === tile.id"
                (click)="toggleTileInteractions(tile, $event)"
                matTooltip="Chart interactions (targets & references)"
                aria-label="Edit chart interactions"
              >
                <mat-icon>link</mat-icon>
              </button>
              <button *ngIf="tile.layoutType === 'GROUP'" type="button" mat-icon-button class="dvm-tile-columns" (click)="toggleGroupColumns(tile); $event.stopPropagation()" [matTooltip]="(tile.groupColumns || 1) >= 2 ? 'Switch to 1 column' : 'Switch to 2 columns'" aria-label="Toggle group columns">
                <mat-icon>{{ (tile.groupColumns || 1) >= 2 ? 'view_agenda' : 'view_column' }}</mat-icon>
              </button>
              <button *ngIf="tile.layoutType === 'GROUP'" type="button" mat-icon-button class="dvm-tile-ungroup" (click)="ungroupTile(tile); $event.stopPropagation()" matTooltip="Ungroup — restore as separate charts" aria-label="Ungroup">
                <mat-icon>call_split</mat-icon>
              </button>
              <button type="button" mat-icon-button class="dvm-tile-remove" (click)="removeTile(tile)" matTooltip="Remove from dashboard" aria-label="Remove tile">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </div>

          <!-- Per-tile Format popup (JSON-driven controls). -->
          <div class="dvm-tile-format-popup" [class.align-left]="formatPopupAlign === 'left'" *ngIf="openFormatTileId === tile.id && tile.format">
            <ng-container *ngTemplateOutlet="formatPopup; context: { tile: tile }"></ng-container>
          </div>

          <!-- Cross-filter listener editor (mirrors the dashboard-builder): the
               column this tile PUBLISHES on click + which tiles REACT to it. -->
          <div class="dvm-listener-editor" *ngIf="expandedListenerTileId === tile.id">
            <div class="dvm-listener-row">
              <label>Publishes filter key</label>
              <ng-select
                #ssTileFilterKey
                class="ss-select"
                [class.is-open]="!!ssTileFilterKey.isOpen()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="tileFilterColumns(tile)"
                [clearable]="true"
                [searchable]="true"
                placeholder="(defaults to the category column)"
                [ngModel]="effectiveTileFilterKey(tile) || null"
                (ngModelChange)="setTileFilterKey(tile, $event || '')"
                (clear)="setTileFilterKey(tile, '')"
              ></ng-select>
            </div>
            <div class="dvm-listener-row">
              <label>Components that react to this card</label>
              <div class="dvm-listener-checkboxes" *ngIf="reactableTilesFor(tile).length; else noOtherTiles">
                <label class="dvm-listener-checkbox" *ngFor="let target of reactableTilesFor(tile)">
                  <input
                    type="checkbox"
                    [checked]="isTileListener(tile, target.id)"
                    (change)="toggleTileListener(tile, target.id)"
                  />
                  <span>{{ target.title || target.layoutType }} <small>({{ target.layoutType | lowercase }})</small></span>
                </label>
              </div>
              <ng-template #noOtherTiles>
                <small class="control-hint">Add more charts to wire cross-filter listeners.</small>
              </ng-template>
            </div>
            <div class="dvm-listener-actions">
              <button type="button" class="dvm-listener-reset" (click)="resetTileListeners(tile)">Reset to chart defaults</button>
              <button type="button" class="dvm-listener-done" (click)="toggleListenerEditor(tile)">Done</button>
            </div>
          </div>

          <div class="dvm-tile-body" *ngIf="tile.layoutType !== 'GROUP'">
            <ng-container *ngTemplateOutlet="tileBody; context: { tile: tile }"></ng-container>
          </div>
          <ng-container *ngTemplateOutlet="tileNotesBlock; context: { tile: tile }"></ng-container>

          <!-- GROUP card: members stack inside, each keeping its OWN render AND
               its own Edit/Format/Interactions — grouping only changes WHERE a
               chart renders, not what you can still do to it individually. -->
          <div class="dvm-group-body" [class.two-col]="(tile.groupColumns || 1) >= 2" *ngIf="tile.layoutType === 'GROUP'">
            <div class="dvm-group-empty" *ngIf="!tile.groupMembers?.length">Empty group — ungroup or remove it.</div>
            <div class="dvm-group-member" [class.is-formatting]="openFormatTileId === member.id" *ngFor="let member of tile.groupMembers" [style.--dvm-card-header-align]="member.format?.['titleAlign'] || null">
              <div class="dvm-group-member-head">
                <span [title]="member.title">{{ member.title }}</span>
                <div class="dvm-group-member-actions">
                  <!-- Order inside the card IS the layout: members render (and
                       publish) in this sequence, so these move the chart within
                       the group. -->
                  <button type="button" mat-icon-button class="dvm-group-member-move"
                          [disabled]="!canMoveGroupMember(tile, member, -1)"
                          (click)="moveGroupMember(tile, member, -1, $event)"
                          matTooltip="Move earlier in the card" aria-label="Move earlier in the card">
                    <mat-icon>arrow_upward</mat-icon>
                  </button>
                  <button type="button" mat-icon-button class="dvm-group-member-move"
                          [disabled]="!canMoveGroupMember(tile, member, 1)"
                          (click)="moveGroupMember(tile, member, 1, $event)"
                          matTooltip="Move later in the card" aria-label="Move later in the card">
                    <mat-icon>arrow_downward</mat-icon>
                  </button>
                  <button type="button" mat-icon-button [class.is-active]="editingTileId === member.id" (click)="editTile(member, $event)" matTooltip="Edit chart configuration" aria-label="Edit chart configuration">
                    <mat-icon>edit</mat-icon>
                  </button>
                  <button *ngIf="isFormattableTile(member)" type="button" mat-icon-button [class.is-active]="openFormatTileId === member.id" (click)="toggleTileFormat(member, $event)" matTooltip="Format chart" aria-label="Format chart">
                    <mat-icon>tune</mat-icon>
                  </button>
                  <button *ngIf="canEditInteractions(member)" type="button" mat-icon-button [class.is-active]="openInteractionTileId === member.id" (click)="toggleTileInteractions(member, $event)" matTooltip="Chart interactions (targets & references)" aria-label="Edit chart interactions">
                    <mat-icon>link</mat-icon>
                  </button>
                  <button type="button" mat-icon-button (click)="removeGroupMember(tile, member)" matTooltip="Remove from group" aria-label="Remove from group">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </div>
              <div class="dvm-tile-format-popup dvm-group-member-format-popup" [class.align-left]="formatPopupAlign === 'left'" *ngIf="openFormatTileId === member.id && member.format">
                <ng-container *ngTemplateOutlet="formatPopup; context: { tile: member }"></ng-container>
              </div>
              <div class="dvm-tile-body dvm-group-member-body">
                <ng-container *ngTemplateOutlet="tileBody; context: { tile: member }"></ng-container>
              </div>
              <ng-container *ngTemplateOutlet="tileNotesBlock; context: { tile: member }"></ng-container>
            </div>
          </div>

          <span *ngIf="!isDashboardFullscreen || previewEditLayout" class="dvm-tile-resize" (mousedown)="startTileResize(tile, $event)" matTooltip="Drag to resize" aria-hidden="true"></span>
        </div>
      </div>
    </div>
  </div>

  <div class="pane-resizer pane-resizer--data" [class.is-disabled]="isDividerDisabled(1)" (mousedown)="startDividerResize(1, $event)" aria-hidden="true"></div>

  <aside class="dvm-layout-data-panel wf-section" [class.is-collapsed]="dataPanelCollapsed" [style.grid-column]="paneGridColumn('data')" (dragover)="onPaneDragOver($event)" (drop)="onPaneDrop('data', $event)">
    <header class="wf-section-header data-panel-header pane-drag-handle" draggable="true" (dragstart)="onPaneDragStart('data', $event)" (dragend)="onPaneDragEnd()" matTooltip="Drag to reorder panel">
      <div><span class="wf-section-kicker">Data explorer</span><h3>Tables &amp; fields</h3></div>
      <div class="wf-header-actions">
        <mat-icon matTooltip="Drag a field onto a bridge step">dataset</mat-icon>
        <button
          type="button"
          class="panel-collapse-button"
          mat-icon-button
          (click)="dataPanelCollapsed = !dataPanelCollapsed"
          [matTooltip]="dataPanelCollapsed ? 'Open data' : 'Collapse data'"
          [attr.aria-label]="dataPanelCollapsed ? 'Open data panel' : 'Collapse data panel'"
        ><mat-icon>{{ paneCollapseIcon('data') }}</mat-icon></button>
      </div>
    </header>

    <div class="data-panel-content panel-content">
    <div class="data-search">
      <mat-icon>search</mat-icon>
      <input type="search" [(ngModel)]="dataPaneFilter" placeholder="Search tables" aria-label="Search tables" />
    </div>

    <div class="data-browser">
      <div class="data-table-list" aria-label="Available tables">
        <button
          type="button"
          class="data-table-item"
          *ngFor="let entity of filteredExplorerEntities"
          [class.is-active]="entity === activeExplorerEntity"
          (click)="selectExplorerEntity(entity)"
          [title]="entity"
        >
          <mat-icon>table_chart</mat-icon><span>{{ entity }}</span><mat-icon>chevron_right</mat-icon>
        </button>
        <div class="data-pane-empty" *ngIf="!entitiesLoading && !filteredExplorerEntities.length">No tables found</div>
        <div class="data-pane-empty" *ngIf="entitiesLoading">Loading tables…</div>
      </div>

      <div class="data-field-list" aria-label="Table fields">
        <div class="data-field-heading" *ngIf="activeExplorerEntity" [title]="activeExplorerEntity">{{ activeExplorerEntity }}</div>
        <div class="field-search" *ngIf="activeExplorerEntity && !explorerColumnsLoading">
          <mat-icon>search</mat-icon>
          <input type="search" [(ngModel)]="fieldPaneFilter" placeholder="Search columns" aria-label="Search columns" />
          <button type="button" *ngIf="fieldPaneFilter" (click)="fieldPaneFilter = ''" aria-label="Clear column search">
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <div class="data-field-scroll">
          <div class="data-pane-empty" *ngIf="!activeExplorerEntity">Select a table to view its fields.</div>
          <div class="data-pane-empty" *ngIf="explorerColumnsLoading">Loading fields…</div>
          <div class="data-pane-empty" *ngIf="activeExplorerEntity && !explorerColumnsLoading && !filteredExplorerColumns.length">
            {{ fieldPaneFilter ? 'No matching columns' : 'No columns available' }}
          </div>
          <div
            class="data-field-item"
            *ngFor="let column of filteredExplorerColumns"
            draggable="true"
            (dragstart)="onExplorerFieldDragStart($event, activeExplorerEntity, column)"
            [title]="'Drag ' + column + ' to a bridge step'"
          >
            <mat-icon>drag_indicator</mat-icon><span>{{ column }}</span>
          </div>
        </div>
      </div>
    </div>
    </div>
  </aside>
</section>

<!-- Interactions modal — rendered at the template ROOT (not inside a tile) so
     it's a real centered overlay, never clipped/cramped by the small tile card.
     Wires which live tiles an INPUT control drives, using its own state, so the
     left builder form/sidebar is untouched. -->
<div class="dvm-interactions-overlay" *ngIf="interactionTile" (click)="cancelTileInteractions()">
  <div class="dvm-interactions-modal" (click)="$event.stopPropagation()">
    <div class="dvm-interactions-modal-head">
      <span>Interactions — {{ interactionTile.title || (interactionTile.layoutType | titlecase) }}</span>
      <button type="button" mat-icon-button (click)="cancelTileInteractions()" aria-label="Close"><mat-icon>close</mat-icon></button>
    </div>
    <div class="dvm-interactions-modal-body">
      <div class="control-group control-group-full">
        <label>{{ interactionTileIsInput ? 'Target chart(s) / input(s) this control drives' : 'Target chart(s) / input(s) this chart interacts with' }}</label>
        <ng-select
          class="ss-select ss-select--multi ss-select--full"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="interactionTargetOptionsList"
          bindLabel="label"
          bindValue="value"
          [multiple]="true"
          [closeOnSelect]="false"
          [hideSelected]="false"
          [clearable]="true"
          [searchable]="true"
          placeholder="Select the chart(s)/input(s) this control drives"
          [(ngModel)]="interactionTargets"
        ></ng-select>
        <small class="control-hint" *ngIf="!interactionTargetOptionsList.length">Add more charts/inputs to the dashboard first — they'll appear here.</small>
      </div>

      <!-- Per-target formulas/references only apply to INPUT controls (they
           push a computed value into each target). A chart tile just declares
           WHICH components it interacts with — the select above is enough. -->
      <div class="control-group control-group-full" *ngFor="let id of (interactionTileIsInput ? interactionTargets : [])">
        <ng-container *ngIf="isWaterfallTarget(id)">
          <span class="target-formula-name">{{ targetLabelForId(id) }} — per bar</span>
          <div class="target-formula-bar" *ngFor="let step of targetSteps(id); trackBy: trackByStepLabel">
            <div class="target-formula-row">
              <span class="target-formula-name">{{ step.label || step.stepType }}</span>
              <input type="text" [ngModel]="interactionStepFormula(id, step.label)" (ngModelChange)="setInteractionStepFormula(id, step.label, $event)" placeholder="e.g. [price] * value" />
            </div>
            <ng-container *ngIf="formulaReferencesEnabledFor(targetLayoutTypeForId(id))">
              <ng-container *ngTemplateOutlet="interactionRefList; context: { id: id, step: step.label }"></ng-container>
            </ng-container>
          </div>
        </ng-container>
        <div *ngIf="!isWaterfallTarget(id)">
          <div class="target-formula-row">
            <span class="target-formula-name">{{ targetLabelForId(id) }}</span>
            <input type="text" [ngModel]="interactionFormulas[id] || ''" (ngModelChange)="interactionFormulas[id] = $event" [placeholder]="isInputControlTargetId(id) ? 'value (default — passes this control’s value through)' : 'e.g. [price] * value'" />
          </div>
          <!-- A CHART target with no formula is silently skipped by the
               recompute (nothing to calculate) — surface that instead. -->
          <div class="target-formula-row" *ngIf="!isInputControlTargetId(id) && !(interactionFormulas[id] || '').trim()">
            <small class="control-warning">No formula yet — "{{ targetLabelForId(id) }}" will NOT update when this control changes. Add one, e.g. [{{ targetLabelForId(id) }}]*(1+(value/100))</small>
          </div>
          <ng-container *ngIf="formulaReferencesEnabledFor(targetLayoutTypeForId(id))">
            <ng-container *ngTemplateOutlet="interactionRefList; context: { id: id }"></ng-container>
          </ng-container>
        </div>
      </div>

    </div>
    <div class="dvm-interactions-modal-foot">
      <button type="button" class="add-step-button" mat-stroked-button (click)="cancelTileInteractions()">Cancel</button>
      <button type="button" class="add-step-button is-primary" mat-flat-button color="primary" (click)="saveTileInteractions()">Save</button>
    </div>
  </div>
</div>

<!-- "Custom request body" popup — scoped to ONE CRUD endpoint (whichever
     "Body" button opened it). Row edits (the table's own columns) are the
     DYNAMIC part and need no editor here — the chart component sends them
     automatically. This popup only edits the STATIC scaffolding: constants
     merged alongside row data, the endpoints array, and extra top-level
     fields. -->
<ng-template #ptKvEditor let-rows="rows" let-label="label" let-hint="hint">
  <div class="dvm-pt-subsection">
    <div class="dvm-pt-subsection-head">
      <span>{{ label }}</span>
      <button type="button" class="dvm-pt-add-btn" (click)="addPtKvRow(rows)">+ Add</button>
    </div>
    <p class="dvm-pt-hint" *ngIf="hint">{{ hint }}</p>
    <p class="dvm-pt-hint">Value is plain text by default; type valid JSON (e.g. <code>["engine_model"]</code>, <code>true</code>, <code>42</code>) to send a real array/object/number/boolean instead of a string.</p>
    <div class="dvm-pt-kv-row" *ngFor="let row of rows; let ri = index">
      <input type="text" placeholder="Key" [(ngModel)]="row.key" />
      <input type="text" placeholder='Value — text, or JSON like ["engine_model"]' [(ngModel)]="row.value" />
      <button type="button" class="dvm-pt-remove-btn" (click)="removePtKvRow(rows, ri)" aria-label="Remove row"><mat-icon>close</mat-icon></button>
    </div>
    <p class="dvm-pt-empty" *ngIf="!rows.length">None configured.</p>
  </div>
</ng-template>

<ng-template #ptEndpointEditor let-rows="rows">
  <div class="dvm-pt-subsection">
    <div class="dvm-pt-subsection-head">
      <span>Endpoints</span>
      <button type="button" class="dvm-pt-add-btn" (click)="addPtEndpointRow(rows)">+ Add endpoint</button>
    </div>
    <div class="dvm-pt-kv-row" *ngFor="let row of rows; let ri = index">
      <input type="text" placeholder="functionName" [(ngModel)]="row.functionName" />
      <input type="text" placeholder="version" [(ngModel)]="row.version" />
      <button type="button" class="dvm-pt-remove-btn" (click)="removePtEndpointRow(rows, ri)" aria-label="Remove row"><mat-icon>close</mat-icon></button>
    </div>
    <p class="dvm-pt-empty" *ngIf="!rows.length">None configured.</p>
  </div>
</ng-template>

<div class="dvm-interactions-overlay" *ngIf="showPayloadTemplateModal" (click)="closePayloadTemplateModal()">
  <div class="dvm-interactions-modal dvm-pt-modal" (click)="$event.stopPropagation()">
    <div class="dvm-interactions-modal-head">
      <span>Request body — {{ ptEndpointLabel }}</span>
      <button type="button" mat-icon-button (click)="closePayloadTemplateModal()" aria-label="Close"><mat-icon>close</mat-icon></button>
    </div>
    <div class="dvm-interactions-modal-body">
      <p class="dvm-pt-intro">Applies to this endpoint only — every other endpoint keeps its own body. Each row's field values (whatever columns this table has) are sent automatically under the key below; that's the DYNAMIC part, no setup needed. Everything else here is STATIC — identical on every call to this endpoint. Leave it all empty to post the default contract.</p>

      <div class="dvm-pt-field">
        <label>Row values key</label>
        <input type="text" [(ngModel)]="ptDraft.dataKey" placeholder="requestData" />
      </div>

      <!-- Which of the row's fields go into the payload. None checked = every
           column of the table (hidden ones included). -->
      <div class="dvm-pt-subsection">
        <div class="dvm-pt-subsection-head">
          <span>Columns to send</span>
          <button type="button" class="dvm-pt-add-btn" *ngIf="ptDraft.includeColumns.length" (click)="clearPtColumns()">Send all</button>
        </div>
        <p class="dvm-pt-hint">
          {{ ptDraft.includeColumns.length
              ? 'Only the checked columns are sent.'
              : 'Nothing checked — every column of the table is sent. Check specific columns to send only those.' }}
        </p>
        <div class="dvm-pt-col-grid" *ngIf="ptAvailableColumns.length">
          <label class="dvm-pt-col-item" *ngFor="let column of ptAvailableColumns">
            <input type="checkbox" [checked]="isPtColumnSelected(column)" (change)="togglePtColumn(column)" />
            <span>{{ column }}</span>
          </label>
        </div>
        <p class="dvm-pt-empty" *ngIf="!ptAvailableColumns.length">Pick a dataset first to list its columns.</p>
      </div>

      <ng-container *ngTemplateOutlet="ptKvEditor; context: { rows: ptDraft.staticValues, label: 'Static values (merged with row data)', hint: 'Enter text normally. JSON values are supported, for example an array containing uuid or the number 1.' }"></ng-container>
      <ng-container *ngTemplateOutlet="ptEndpointEditor; context: { rows: ptDraft.endpoints }"></ng-container>
      <ng-container *ngTemplateOutlet="ptKvEditor; context: { rows: ptDraft.extraFields, label: 'Extra top-level fields', hint: 'e.g. entity, workflowId, version' }"></ng-container>

      <!-- Dynamic tokens available in Static values / Extra top-level fields.
           Resolved by the table at save time (see resolvePayloadTokens). -->
      <div class="pt-token-help">
        <strong>Dynamic values</strong>
        <small>Use these in <em>Static values</em> or <em>Extra top-level fields</em> — they're resolved when the save runs.</small>
        <div class="pt-token-list">
          <span class="pt-token"><code>{{ '{' }}userId{{ '}' }}</code> signed-in user id</span>
          <span class="pt-token"><code>{{ '{' }}userEmail{{ '}' }}</code> their email</span>
          <span class="pt-token"><code>{{ '{' }}userName{{ '}' }}</code> their username</span>
          <span class="pt-token"><code>{{ '{' }}now{{ '}' }}</code> ISO timestamp</span>
          <span class="pt-token"><code>{{ '{' }}componentId{{ '}' }}</code> this table</span>
          <span class="pt-token"><code>{{ '{' }}dataset{{ '}' }}</code> its dataset</span>
          <span class="pt-token"><code>{{ '{' }}column{{ '}' }}</code> that row's value (single-row saves)</span>
        </div>
      </div>
    </div>
    <div class="dvm-interactions-modal-foot">
      <button type="button" class="add-step-button" mat-stroked-button (click)="closePayloadTemplateModal()">Cancel</button>
      <button type="button" class="add-step-button is-primary" mat-flat-button color="primary" (click)="savePayloadTemplateModal()">Save</button>
    </div>
  </div>
</div>

<!-- Reference rows for the Interactions modal — its own state, separate from the
     create form's #referenceList so the two never clobber each other. -->
<ng-template #interactionRefList let-id="id" let-step="step">
  <div class="target-reference-list">
    <div class="target-reference-card" *ngFor="let ref of interactionRefs(id, step)">
      <div class="target-reference-card-header">
        <span>Reference</span>
        <button type="button" class="target-reference-remove" (click)="removeInteractionRef(id, ref)" title="Remove reference">&times;</button>
      </div>
      <div class="target-reference-grid">
        <div class="control-group"><label>Token</label><input type="text" [(ngModel)]="ref.token" placeholder="e.g. DN Price" /></div>
        <div class="control-group"><label>Input title</label><input type="text" [(ngModel)]="ref.inputTitle" placeholder="e.g. Price Inflation %" /></div>
        <div class="control-group"><label>Target label</label><input type="text" [(ngModel)]="ref.targetLabel" placeholder="e.g. Product Margins" /></div>
        <div class="control-group"><label>Row label</label><input type="text" [(ngModel)]="ref.rowLabel" placeholder="e.g. DN Price" /></div>
      </div>
    </div>
    <button type="button" class="target-reference-add" (click)="addInteractionRef(id, step)">+ Add reference</button>
  </div>
</ng-template>

